import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import DOMPurify from "dompurify";
import AddCandidatesModal from "../components/AddCandidatesModal";
import OriginMark from "../components/OriginMark";
import {
  STATUSES,
  DEFAULT_STATUS,
  statusRank,
  statusFor,
  furthestStatus,
  AWAITING_STATUS,
  MEETING_STATUSES,
} from "../lib/pipeline";
import {
  CALL_OUTCOMES,
  callOutcomeLabel,
  createCallLog,
  deleteCallLog,
  getCallLogs,
} from "../lib/callLogService";
import CompanyLogo from "../components/CompanyLogo";

import {
  FiAlertCircle,
  FiAlertTriangle,
  FiCheckCircle,
  FiBarChart2,
  FiBold,
  FiBookmark,
  FiBriefcase,
  FiCheck,
  FiChevronDown,
  FiChevronLeft,
  FiCopy,
  FiEdit3,
  FiExternalLink,
  FiItalic,
  FiLink,
  FiMail,
  FiMapPin,
  FiMoreHorizontal,
  FiPaperclip,
  FiPhone,
  FiPlus,
  FiSearch,
  FiSend,
  FiTag,
  FiTrash2,
  FiUsers,
  FiX,
  FiMinus,
  FiDownload,
} from "react-icons/fi";
import { BrandLinkedIn, Building, Spark } from "../components/ui/icons";
import { useToast } from "../components/ToastProvider";
import { useAuth } from "../context/AuthContext";
import {
  AVATAR_TINTS,
  PROVIDER_BRAND,
  hashStr,
  useTheme,
} from "../theme/ThemeContext";
import { fetchWithAuth } from "../utils/authFetch";
import { newBatchId } from "../utils/batchId";
import { creditsChanged } from "../utils/useCredits";
import {
  getMailStatus,
  sendOutreachEmail,
  getOutreachHistory,
  generateEmailDraft,
  deleteOutreachCandidate,
} from "../lib/outreachService";
import {
  getOutreachState,
  patchOutreachState,
} from "../lib/outreachStateService";
import {
  getCandidateState,
  patchCandidateStatus,
} from "../lib/candidateStateService";
import { getUserSettings } from "../lib/userSettingsService";
import Skeleton, { FadeIn, useFillCount, useDelayedFlag } from "../components/Skeleton";
import { timeAgo } from "../utils/timeAgo";
import {
  toCsv,
  downloadCsv,
  csvFileName,
  profileCells,
  formatLanguageProficiency,
} from "../utils/csvExport";
import { matchLevelFromScore } from "../utils/matchLevel";

/**
 * The status to show on a candidate row.
 *
 * Status is per assignment, so a person on two jobs has two of them. With a job
 * filter active the answer is that job's; without one we show how far they've
 * got anywhere, and `varies` flags that it differs between their jobs.
 */
/** Apply one job's status to a candidate's row, for optimistic updates. */
function applyScopedStatus(state, jobId, status) {
  const base = state || { jobs: {} };
  return { ...base, jobs: { ...(base.jobs || {}), [jobId]: status } };
}

function rowStatusOf(statusMap, jobFilter, p) {
  if (jobFilter && jobFilter !== "all") {
    return { status: statusFor(statusMap, jobFilter, p.id), varies: false };
  }
  return furthestStatus(statusMap, p.id);
}
function sanitizeComposerHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(String(html), {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus"],
    // Allow data:image URLs so signature images survive the round-trip
    // through this sanitizer (they get swapped to cid:... at send time).
    // DOMPurify's default URI regex drops data: URIs entirely.
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?|mailto|tel|ftp):|data:image\/(?:png|jpe?g|gif|webp);base64,|#|\/|\?)/i,
    ADD_DATA_URI_TAGS: ["img"],
    // Plain values like width="120" fail the URI regexp unless marked URI-safe.
    ADD_URI_SAFE_ATTR: [
      "width", "height", "colspan", "rowspan", "cellpadding", "cellspacing",
      "border", "align", "valign", "bgcolor",
    ],
  });
}

/** First word of a person's full name; "" if missing. */
function firstNameOf(person) {
  const full = String(person?.name || "").trim();
  if (!full) return "";
  return full.split(/\s+/)[0];
}

/** Last word of a person's full name; "" if name is single-word or missing. */
function lastNameOf(person) {
  const full = String(person?.name || "").trim();
  if (!full) return "";
  const parts = full.split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1] : "";
}

/** Mail-merge token list. Users type these literally in the composer; they
 * stay as plain text in the editor + draft, and only get substituted at
 * send time using each recipient's values. Same pattern as Mailchimp /
 * HubSpot / Lemlist. */
const SUPPORTED_TOKENS = [
  { key: "firstName", label: "First name", example: "Hi {{firstName}}," },
  { key: "lastName", label: "Last name", example: "Dear Ms. {{lastName}}," },
  { key: "name", label: "Full name", example: "{{name}}" },
  { key: "company", label: "Company", example: "your work at {{company}}" },
  { key: "role", label: "Role / title", example: "as a {{role}}" },
];

/** Resolve {{firstName}} / {{lastName}} / {{name}} / {{company}} / {{role}}
 * (case-insensitive, optional whitespace inside the braces) against a
 * recipient. Unknown tokens are left intact so the recipient sees them and
 * the sender knows something was misspelled. Empty values are also left as
 * tokens to avoid awkward "Hi ," lines. */
// Job matching is one queued job for both the profile pane (one candidate) and the
// selection bar (many); its progress shows in the activity tray.
const MATCH_POLL_MS = 1500;
const MATCH_MAX_MS = 5 * 60 * 1000;

/** Queue a job match for tagged candidates [{ id, origin }]; resolves the jobId. */
async function startMatchJob(candidates, scope) {
  const res = await fetchWithAuth("/api/candidates/match-batch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ candidates, scope }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.jobId) {
    throw new Error((data && data.error) || `Match failed (${res.status})`);
  }
  return data.jobId;
}

/** Poll a queued job match to its end; resolves the final status doc, throws at the ceiling. */
async function waitForMatchJob(jobId, startedAt = Date.now()) {
  while (Date.now() - startedAt < MATCH_MAX_MS) {
    await new Promise((r) => setTimeout(r, MATCH_POLL_MS));
    let sb = null;
    try {
      const sr = await fetchWithAuth(
        `/api/candidatematch/status?jobId=${encodeURIComponent(jobId)}`,
      );
      sb = await sr.json().catch(() => null);
    } catch {
      continue; // transient network blip — keep polling until the ceiling
    }
    if (sb?.status === "succeeded" || sb?.status === "failed") return sb;
  }
  throw new Error("Job match timed out");
}

function resolveTokens(body, recipient) {
  if (!body) return "";
  if (!recipient) return String(body);
  const map = {
    firstname: firstNameOf(recipient),
    lastname: lastNameOf(recipient),
    name: String(recipient?.name || "").trim(),
    fullname: String(recipient?.name || "").trim(),
    company: String(recipient?.company || "").trim(),
    role: String(recipient?.role || "").trim(),
    position: String(recipient?.role || "").trim(),
    title: String(recipient?.role || "").trim(),
  };
  return String(body).replace(/\{\{\s*([a-zA-Z]+)\s*\}\}/g, (match, key) => {
    const v = map[key.toLowerCase()];
    return v ? v : match;
  });
}

/** Return the list of distinct token names that DIDN'T resolve for this
 * recipient — e.g. `["firstName", "company"]` when the body mentions both
 * but the recipient has neither. Called only on deliberate user actions
 * (Preview toggle, Send click) — never on every render — so token
 * validation never runs during typing. */
function unresolvedTokens(text, recipient) {
  if (!text) return [];
  const resolved = resolveTokens(text, recipient);
  const found = [...resolved.matchAll(/\{\{\s*([a-zA-Z]+)\s*\}\}/g)].map(
    (m) => m[1],
  );
  return [...new Set(found)];
}

/** [Placeholders] the AI leaves for the sender (e.g. [Job title]). Read from
 * the draft's visible text so tags and entities don't confuse the match. */
function placeholdersIn(html) {
  if (!html) return [];
  const text = String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ");
  const found = [...text.matchAll(/\[[^[\]\n]{1,80}\]/g)].map((m) => m[0]);
  return [...new Set(found)];
}

/** Wrap any remaining `{{token}}` instances in a red `<mark>` so the
 * preview view surfaces missing data inline. */
function highlightTokens(text) {
  if (!text) return "";
  return String(text).replace(
    /\{\{\s*[a-zA-Z]+\s*\}\}/g,
    (m) =>
      `<mark style="background: var(--danger-soft); color: var(--danger); padding: 0 4px; border-radius: 3px; font-weight: 600;">${m}</mark>`,
  );
}

/** AI / template bodies sometimes come back as plain text with \n line
 * breaks. The composer's contentEditable renders those via white-space:
 * pre-wrap, but sent emails are real HTML — \n is just whitespace in HTML
 * and collapses into a single paragraph at the recipient's mail client.
 *
 * Convert plain-text-with-newlines into proper paragraph HTML before the
 * body is stored or sent. Strings that already look like HTML (contain
 * any tag) pass through unchanged. */
function normalizeBodyToHtml(input) {
  if (!input) return "";
  const s = String(input);
  if (/<\s*[a-z][\s\S]*?>/i.test(s)) return s;
  return s
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

// Trailing/leading blank lines: <br>, empty <p>/<div>, whitespace, &nbsp;.
const TRAILING_BLANKS_RE =
  /(?:\s|&nbsp;|<br\s*\/?>|<p(?:\s[^>]*)?>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>|<div(?:\s[^>]*)?>(?:\s|&nbsp;|<br\s*\/?>)*<\/div>)+$/i;
const LEADING_BLANKS_RE =
  /^(?:\s|&nbsp;|<br\s*\/?>|<p(?:\s[^>]*)?>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>)+/i;

/** Saved signatures render tight like Outlook: paragraphs get margin:0 and
 * empty paragraphs become one visible blank line. Matches the editor. */
function normalizeSignatureHtml(html) {
  return String(html || "")
    .replace(/<p(\s[^>]*)?>/gi, (m, attrs = "") => {
      if (/margin/i.test(attrs)) return m;
      const styled = /style\s*=\s*"([^"]*)"/i.exec(attrs);
      if (styled)
        return `<p${attrs.replace(styled[0], `style="margin: 0; ${styled[1]}"`)}>`;
      return `<p${attrs} style="margin: 0">`;
    })
    .replace(/<p([^>]*)>(?:\s|&nbsp;)*<\/p>/gi, "<p$1><br></p>");
}

/** Body + signature with exactly one blank line between them, whatever the
 * body ends with. Paragraph-margin tags supply the gap; divs/tables need
 * a <br>; inline text needs two. */
function joinBodyAndSignature(body, signature) {
  const b = String(body || "").replace(TRAILING_BLANKS_RE, "");
  const sig = String(signature || "").replace(LEADING_BLANKS_RE, "");
  if (!sig) return b;
  if (!b) return sig;
  if (/<\/(p|h[1-6]|ul|ol|blockquote)>$/i.test(b)) return `${b}${sig}`;
  if (/<\/(div|table)>$/i.test(b)) return `${b}<br>${sig}`;
  return `${b}<br><br>${sig}`;
}

function escapeHtml(t) {
  return String(t || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Walk an HTML body, swap every <img src="data:image/...;base64,..."> for
 * an <img src="cid:..."> reference, and emit matching inline attachments
 * (Outlook-style). Each unique data URL becomes one attachment so the same
 * image referenced twice ships once.
 *
 * Returns: { body: rewrittenHtml, inlineAttachments: [{ name, contentType,
 * contentBase64, inline: true, contentId }] }
 */
function extractInlineImagesForSend(htmlBody) {
  const html = String(htmlBody || "");
  const seen = new Map(); // dataUrl -> { contentId, name, contentType, contentBase64 }
  const dataImgRe =
    /<img\b([^>]*?)\bsrc=(['"])(data:image\/([a-z0-9+]+);base64,([A-Za-z0-9+/=]+))\2([^>]*)>/gi;
  let extIdx = 0;
  const rewritten = html.replace(
    dataImgRe,
    (_full, pre, q, dataUrl, mime, b64, post) => {
      let entry = seen.get(dataUrl);
      if (!entry) {
        extIdx += 1;
        const subtype = String(mime || "png").toLowerCase();
        const contentId = `sig-img-${Date.now().toString(36)}-${extIdx}`;
        const ext =
          subtype === "jpeg" ? "jpg" : subtype.replace(/[^a-z0-9]/g, "");
        entry = {
          contentId,
          name: `signature-${extIdx}.${ext || "img"}`,
          contentType: `image/${subtype}`,
          contentBase64: b64,
        };
        seen.set(dataUrl, entry);
      }
      return `<img${pre} src=${q}cid:${entry.contentId}${q}${post}>`;
    },
  );
  const inlineAttachments = Array.from(seen.values()).map((e) => ({
    name: e.name,
    contentType: e.contentType,
    contentBase64: e.contentBase64,
    inline: true,
    contentId: e.contentId,
  }));
  return { body: rewritten, inlineAttachments };
}
/* ───────────────── Non-themeable tokens (shadows, fonts) ─────────────────
 * Color tokens live in CSS variables (see `outreachPaletteVars` below) so
 * the page can flip between light and dark with no styled-component churn.
 * The accent honours the app's site accent (theme.accent), not the original
 * scoutm8 copper. */
const C = {
  shadowLg: "var(--shadow-lg)",
  fontDisplay: "var(--font-display)",
  fontBody: "var(--font-body)",
  fontMono: "var(--font-mono)",
};
/** CSS-variable palette. The --orx-* names are kept because this page's ~8k
 * lines reference them, but they are now pure aliases of the app tokens on
 * :root (see theme/ThemeContext) rather than a second set of colour values.
 * Applied as inline `style` on <Shell>. */
function outreachPaletteVars() {
  return {
    "--orx-ink": "var(--ink)",
    "--orx-ink-2": "var(--ink-2)",
    "--orx-ink-3": "var(--ink-3)",
    "--orx-ink-soft": "var(--ink-soft)",
    "--orx-ink-muted": "var(--ink-muted)",
    "--orx-ink-faint": "var(--ink-faint)",
    "--orx-bg": "var(--bg)",
    "--orx-bg-2": "var(--bg-2)",
    "--orx-surface": "var(--surface)",
    "--orx-surface-tint": "var(--surface-tint)",
    "--orx-line": "var(--line)",
    "--orx-line-soft": "var(--line-soft)",
    "--orx-line-strong": "var(--line-strong)",
    "--orx-accent": "var(--accent)",
    "--orx-accent-2": "var(--accent-2)",
    "--orx-accent-soft": "var(--accent-soft)",
    "--orx-accent-ink": "var(--accent-ink)",
    "--orx-success": "var(--success)",
    "--orx-success-soft": "var(--success-soft)",
    "--orx-warn": "var(--warn)",
    "--orx-warn-soft": "var(--warn-soft)",
    "--orx-danger": "var(--danger)",
    "--orx-danger-soft": "var(--danger-soft)",
    "--orx-info": "var(--info)",
    "--orx-info-soft": "var(--info-soft)",
    // "Elevated" — the inverted surface used for checkboxes and tooltips. --ink/--bg swap places between
    // modes, so this inverts correctly without a per-mode branch.
    "--orx-elevated-bg": "var(--ink)",
    "--orx-elevated-fg": "var(--bg)",
    "--orx-elevated-divider": "color-mix(in srgb, var(--bg) 22%, transparent)",
  };
}

const OUTREACH_PALETTE_VARS = outreachPaletteVars();

// Job-match pool options surfaced by the "Job match" picker.
// Pools are the user's own jobs only — the public corpus is never matched against.
const MATCH_SCOPES = [
  { id: "all", label: "All jobs", sub: "imported + tracked-company jobs" },
  { id: "client", label: "Your imported jobs", sub: "jobs you uploaded" },
  { id: "custom", label: "Tracked-company jobs", sub: "companies you track and industry picks" },
];

// STATUSES / DEFAULT_STATUS now come from ../lib/pipeline so this page and the
// Dashboard summary can't drift apart. The old `cls` field was never read.


/* ───────────────── Helpers ───────────────── */
function initials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "·";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function tintFor(name) {
  return AVATAR_TINTS[hashStr(name || "") % AVATAR_TINTS.length];
}
function normalizeId(raw) {
  if (raw == null) return "";
  if (typeof raw === "string") return raw;
  if (raw.$oid) return raw.$oid;
  return String(raw);
}
/* True below the width where the profile becomes a full-screen overlay rather
   than a second pane. Keep in step with the 900px media queries on RightColumn
   and Body. Read at call time, so no resize listener is needed. */
function isSinglePaneViewport() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 900px)").matches
  );
}
// Coarse seniority ladder (mirrors ai_db/services/seniority.py), low -> high.
const SENIORITY_OPTIONS = [
  { id: "entry", label: "Entry" },
  { id: "mid", label: "Mid" },
  { id: "senior", label: "Senior" },
  { id: "manager", label: "Manager" },
  { id: "director", label: "Director" },
  { id: "executive", label: "Executive" },
  { id: "unknown", label: "Unknown" },
];
const SENIORITY_LABELS = Object.fromEntries(
  SENIORITY_OPTIONS.map((o) => [o.id, o.label]),
);

// Collect every usable address from a raw email field (string or array).
function emailList(v) {
  return (Array.isArray(v) ? v : [v])
    .filter((x) => typeof x === "string" && x.trim())
    .map((x) => x.trim());
}

// Sourced candidates arrive without an address; one can be looked up from their LinkedIn profile.
function canFindEmail(person) {
  return !!person && !person.email && !person.emailSearched && person.origin === "external" && !!person.linkedin;
}

function normalizePerson(p, kind, origin = "internal") {
  const id = normalizeId(p._id) || `${kind}-${hashStr(JSON.stringify(p))}`;
  const name = p.name || "—";
  // Scraped clients may carry several formula-guessed addresses; keep them
  // all so the UI can offer a picker, with the first (best guess) as default.
  const emails = [...new Set([...emailList(p.email), ...emailList(p.probable_email)])];
  const resolvedOrigin = p.origin || origin;
  return {
    id,
    raw: p,
    kind,
    // "internal" = added via the Add flow (user_candidates); "external" =
    // sourced from Find Candidates. Drives the badge + the All/Int/Ext filter.
    origin: resolvedOrigin,
    name,
    role: p.position || p.role || "",
    company: p.company || p.org || "",
    // Candidates carry location as { original_location, modified_location };
    // clients store a plain location string + top-level country. Support both
    // — else client country is blank and the country filter drops every row.
    location: String(
      (p.location && typeof p.location === "object"
        ? p.location.original_location
        : p.location) || "",
    ).trim(),
    country: String(
      (p.location && typeof p.location === "object"
        ? p.location.modified_location
        : "") ||
        p.country ||
        "",
    ).trim(),
    email: emails[0] || "",
    emails,
    // A sourced person this user already looked up and missed; retires "Find email".
    emailSearched: !!p.emailSearched,
    // When the person joined Outreach: shortlist row for sourced candidates,
    // record creation for added ones. Drives the "Date added" sort.
    addedAt: p.addedAt || p.createdAt || null,
    linkedin: p.link || p.linkedin || "",
    avatarSrc: p.thumbnail_src || p.photo || "",
    summary: p.summary || p.og_description || "",
    education: Array.isArray(p.education) ? p.education : [],
    experience: Array.isArray(p.experience) ? p.experience : [],
    certifications: Array.isArray(p.certifications) ? p.certifications : [],
    initials: initials(name),
    tint: tintFor(name),
    // Coarse seniority bucket derived from the candidate's current title at
    // ingest (people-search / scout). "unknown" for un-tagged / internal adds.
    seniority: p.seniorityLevel || "unknown",
    seniorityRank: typeof p.seniorityRank === "number" ? p.seniorityRank : 0,
    // Real top-match percentage from the candidate's match_id; null when not
    // yet AI-matched (or when this is a client). The sort handles nulls
    // by sinking them to the bottom.
    // Searches (from Find Candidates) that surfaced this candidate, as
    // [{ reqNo, title }]. Only sourced/external candidates carry these; drives
    // the candidates-tab "Search" filter. Empty for internal/CSV/manual adds.
    searches: Array.isArray(p.searches) ? p.searches : [],
  };
}

// An added (internal) candidate's sections as CSV cells, same shape as profileCells gives sourced ones.
function internalProfileCells(c) {
  const period = (it) => {
    const start = asDisplayText(it.startDate);
    const end = asDisplayText(it.endDate) || (it.current ? "Present" : "");
    return [start, end].filter(Boolean).join(" – ");
  };
  const experience = asObjects(c.experience)
    .map((it) => {
      const head = [asDisplayText(it.title), asDisplayText(it.company)].filter(Boolean).join(" — ");
      const when = period(it);
      return head ? (when ? `${head} (${when})` : head) : "";
    })
    .filter(Boolean)
    .join(" | ");
  const education = asObjects(c.education)
    .map((it) => {
      const what = [asDisplayText(it.degree), asDisplayText(it.details || it.field)].filter(Boolean).join(", ");
      const head = [what, asDisplayText(it.school)].filter(Boolean).join(" — ");
      const when = period(it);
      return head ? (when ? `${head} (${when})` : head) : "";
    })
    .filter(Boolean)
    .join(" | ");
  const certifications = asObjects(c.certifications)
    .map((it) => [asDisplayText(it.name), asDisplayText(it.issuer)].filter(Boolean).join(" — "))
    .filter(Boolean)
    .join(" | ");
  return {
    experience,
    education,
    skills: asDisplayText(c.skills),
    languages: asDisplayText(c.languages),
    certifications,
    summary: asDisplayText(c.summary).replace(/\s*\n\s*/g, " "),
  };
}

/** Snapshot the minimum person fields we need to re-render a draft / saved row
 * without going back to the source list. */
function snapshotPerson(p) {
  return {
    id: p.id,
    kind: p.kind,
    origin: p.origin,
    name: p.name,
    role: p.role,
    company: p.company,
    location: p.location,
    country: p.country,
    email: p.email,
    emails: Array.isArray(p.emails) ? p.emails : [],
    emailSearched: !!p.emailSearched,
    linkedin: p.linkedin,
    avatarSrc: p.avatarSrc,
    initials: p.initials,
    tint: p.tint,
    searches: Array.isArray(p.searches) ? p.searches : [],
  };
}

/* ───────────────── Animations ───────────────── */
const fadeIn = keyframes`
  from { transform: translateY(4px); opacity: .6; }
  to { transform: none; opacity: 1; }
`;

// Continuous rotation for the match-in-progress spinner.
const matchSpin = keyframes`
  to { transform: rotate(360deg); }
`;

// Slide the floating bulk-match banner up into the bottom-right corner.
// Soft green pulse around the row whose ranking just changed.
const rowPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--success) 45%, transparent); }
  60%  { box-shadow: 0 0 0 6px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

// Left-to-right gradient sweep for skeleton placeholders while the AI is
// drafting. Uses two color stops over a wide background so the loop reads
// as a moving highlight rather than a flash.
const aiShimmer = keyframes`
  0%   { background-position: -150% 0; }
  100% { background-position:  250% 0; }
`;

/* ───────────────── AI compose loading surfaces ─────────────────
 * Shown while a draft is being personalized:
 *   - AiHeader: a strip at the top of the compose card with sparkles,
 *     "Personalizing for {name}…", an elapsed timer, and Cancel.
 *   - ShimmerBar: thin animated bar for the subject field.
 *   - ShimmerLine: variable-width animated lines for the body editor. */
const AiHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  margin-bottom: 12px;
  background: var(--orx-accent-soft);
  border: 1px solid var(--orx-line);
  color: var(--orx-ink-2);
  font-size: 13px;
`;

// Shown above the body while the draft still holds [placeholders] to fill in.
const PlaceholderStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: var(--warn-soft);
  color: var(--orx-ink);
  font-size: 12.5px;
  line-height: 1.4;
  code {
    font: inherit;
    font-weight: 600;
  }
`;

const AiHeaderMeta = styled.span`
  color: var(--orx-ink-muted);
  font-size: 12px;
  margin-left: 4px;
`;

const AiCancelBtn = styled.button`
  margin-left: auto;
  border: 1px solid var(--orx-line);
  background: transparent;
  color: var(--orx-ink-2);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: var(--orx-line-soft);
  }
  /* Global 44px touch-target floor in App.js would blow this inline pill up. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

const Shimmer = styled.div`
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    var(--orx-line-soft) 0%,
    var(--orx-line-strong) 40%,
    var(--orx-accent-soft) 50%,
    var(--orx-line-strong) 60%,
    var(--orx-line-soft) 100%
  );
  background-size: 200% 100%;
  animation: ${aiShimmer} 1.6s linear infinite;
`;

const ShimmerBar = styled(Shimmer)`
  /* Match the subject <input>'s rendered height so swapping it in/out
   * doesn't shift the composer's vertical layout. */
  height: 24px;
  width: 60%;
`;

const ShimmerLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0;
  /* Match the Editor's min-height exactly (280px) — no extra padding, no
   * box-sizing tricks, so the compose body's vertical footprint is
   * identical whether the Editor or the shimmer is rendered. */
  min-height: 280px;
`;

const ShimmerLine = styled(Shimmer)`
  height: 12px;
  width: ${(p) => p.$w || "100%"};
  flex: 0 0 auto;
`;

/* Disable affordances on toolbar / send buttons while AI is running. We
 * fade them out and turn off pointer events; `aria-busy` on the parent
 * announces it to assistive tech. */
const AiDimmable = styled.div`
  /* Doesn't grow — wraps the footer toolbar/Send button, which stays
   * pinned at the bottom of ComposeCard. */
  flex: 0 0 auto;
  ${(p) =>
    p.$dimmed &&
    css`
      opacity: 0.45;
      pointer-events: none;
      filter: saturate(0.6);
    `}
`;

/* ───────────────── Match activity surface ─────────────────
 * MatchStrip: inline strip below the detail-pane action row, shown while a
 * single-person match is running or just resolved (success / error). Bulk-match
 * progress lives in the shared activity tray. */
const MatchStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  margin: 0 0 12px 0;
  border-radius: 10px;
  border: 1px solid var(--orx-line);
  background: var(--orx-accent-soft);
  color: var(--orx-ink-2);
  font-size: 13px;
  animation: ${fadeIn} 180ms ease-out;

  &[data-phase="success"] {
    border-color: color-mix(in srgb, var(--success) 45%, transparent);
    background: color-mix(in srgb, var(--success) 10%, transparent);
  }
  &[data-phase="error"] {
    border-color: color-mix(in srgb, var(--danger) 45%, transparent);
    background: color-mix(in srgb, var(--danger) 8%, transparent);
  }
`;

const MatchStripSpinner = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid var(--orx-line-strong);
  border-top-color: var(--orx-accent);
  animation: ${matchSpin} 800ms linear infinite;
  flex: 0 0 auto;
`;

// Inline "Find email" action for a missing address: a soft accent chip, not a boxed button.
const FindEmailChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px 3px 8px;
  border: 0;
  border-radius: 99px;
  background: var(--orx-accent-soft);
  color: var(--orx-accent-ink);
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  cursor: pointer;
  /* Beats the global 44px phone touch floor (App.js). */
  min-height: 0;
  transition: filter 0.12s, transform 0.12s;
  &:hover {
    filter: brightness(0.96);
  }
  &:active {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: 2px solid var(--orx-accent);
    outline-offset: 2px;
  }
  &:disabled {
    background: transparent;
    color: var(--orx-ink-muted);
    font-weight: 500;
    padding-left: 0;
    cursor: default;
  }
`;

const FindEmailSpinner = styled.span`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  border: 1.5px solid var(--orx-line-strong);
  border-top-color: var(--orx-accent);
  animation: ${matchSpin} 800ms linear infinite;
`;

function FindEmailAction({ pending, onClick }) {
  return (
    <FindEmailChip
      type="button"
      disabled={pending}
      aria-busy={pending || undefined}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      {pending ? <FindEmailSpinner aria-hidden /> : <FiSearch size={12} aria-hidden />}
      {pending ? "Searching…" : "Find email"}
    </FindEmailChip>
  );
}

const MatchStripBtn = styled.button`
  margin-left: auto;
  border: 1px solid var(--orx-line);
  background: transparent;
  color: var(--orx-ink-2);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: var(--orx-line-soft);
  }
  /* Global 44px touch-target floor in App.js would blow this inline pill up. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

/* Pins a pair of strip buttons (e.g. Retry + Dismiss) to the right edge and
 * keeps them together. Each MatchStripBtn carries its own `margin-left: auto`
 * for the single-button case; neutralise it here so two buttons don't split
 * the free space between them and drift apart. */
const MatchStripActions = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
  button {
    margin-left: 0;
  }
`;

/* ───────────────── Layout shells ───────────────── */
const Shell = styled.div`
  font-family: ${C.fontBody};
  color: var(--orx-ink);
  background: var(--orx-bg);
  /* Break out of MainContent's padding so the page goes edge-to-edge,
     from the sidebar all the way to the viewport edges. */
  margin: -2.5rem;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-feature-settings: "ss01", "cv11";
  -webkit-font-smoothing: antialiased;

  /* Below 1024px the sidebar collapses to a fixed menu button, and MainContent
     reserves a strip of top padding to clear it. Reclaim that strip so the Top
     bar's own surface colour runs all the way up behind the button instead of
     leaving a band of page background above the header. Top then carries the
     clearance as its own padding-top, which is what keeps the title out from
     under the button. */
  @media (max-width: 1024px) {
    margin: -4.75rem -1.5rem -1.5rem;
  }
  @media (max-width: 768px) {
    margin: -4.25rem -1rem -1rem;
  }
  @media (max-width: 450px) {
    margin: -4rem -0.75rem -0.75rem;
  }
  @media (max-width: 390px) {
    margin: -3.8rem -0.5rem -0.5rem;
  }
`;
const Top = styled.div`
  padding: 22px 28px 18px;
  border-bottom: 1px solid var(--orx-line);
  background: var(--orx-surface);
  flex-shrink: 0;
  /* Below 1024px Shell reaches the viewport top so this bar's surface colour
     sits behind the fixed menu button. The button's clearance becomes padding
     here — matching MainContent's reserved strip — so the title clears it.
     Shorthand first in each block, then padding-top, or the shorthand wins.
     The header is otherwise pure chrome: on a phone it should not out-weigh
     the list, so its own padding shrinks as the screen does. */
  @media (max-width: 1024px) {
    padding-top: calc(22px + 4.75rem);
  }
  @media (max-width: 768px) {
    padding: 14px 16px 12px;
    padding-top: calc(14px + 4.25rem);
  }
  @media (max-width: 450px) {
    padding: 12px 14px 10px;
    padding-top: calc(12px + 4rem);
  }
  @media (max-width: 390px) {
    padding-top: calc(12px + 3.8rem);
  }
`;
const TopRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  /* Phone: title left, Add top-right, tabs full width on their own row. */
  @media (max-width: 768px) {
    gap: 10px 12px;
    flex-wrap: wrap;
    align-items: center;
  }
`;

/* Title + tab strip. Shrinks so the header actions keep their width.
   On phones it dissolves so its two children join TopRow's flex row directly —
   that is what lets the tabs take a full-width row of their own beneath the
   title, rather than being boxed into the ~45% left beside the Add button. */
const TopLead = styled.div`
  min-width: 0;
  flex: 1;
  @media (max-width: 768px) {
    display: contents;
  }
`;
const TopTitle = styled.div`
  font-family: ${C.fontDisplay};
  font-size: 36px;
  line-height: 1;
  letter-spacing: -0.01em;
  /* A direct flex item of TopRow once TopLead dissolves: take the row so the
     Add button is pushed to the right edge. */
  @media (max-width: 768px) {
    font-size: 24px;
    flex: 1 1 auto;
    min-width: 0;
  }
  @media (max-width: 450px) {
    font-size: 21px;
  }
`;
const TopMeta = styled.span`
  font-family: ${C.fontBody};
  font-size: 13px;
  color: var(--orx-ink-muted);
  margin-left: 14px;
  font-weight: 400;
  /* Below the title rather than trailing it, so a long count can't shove the
     header actions onto their own line. */
  @media (max-width: 450px) {
    display: block;
    margin: 3px 0 0;
    font-size: 12px;
  }
`;
const Tabs = styled.div`
  display: inline-flex;
  background: var(--orx-bg-2);
  border-radius: 999px;
  padding: 3px;
  margin-top: 14px;
  /* order puts the strip after the Add button once TopLead dissolves (DOM
     order is title, tabs, actions), and the 100% basis drops it onto a row of
     its own. Full width means all four pills fit without scrolling. */
  @media (max-width: 768px) {
    margin-top: 2px;
    padding: 2px;
    display: flex;
    order: 1;
    flex: 1 1 100%;
    width: 100%;
  }
`;
const Tab = styled.button`
  height: 30px;
  padding: 0 16px;
  /* Keep full width inside the scrolling strip above rather than compressing. */
  flex-shrink: 0;
  white-space: nowrap;
  border: 0;
  /* Selected view: a neutral raised pill; colour is kept for actions (blue) and plan limits (orange). */
  background: ${(p) => (p.$active ? "var(--orx-surface)" : "transparent")};
  color: ${(p) => (p.$active ? "var(--orx-ink)" : "var(--orx-ink-soft)")};
  box-shadow: ${(p) =>
    p.$active ? "0 1px 3px color-mix(in srgb, #000 14%, transparent), 0 0 0 1px var(--orx-line)" : "none"};
  border-radius: 999px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition:
    background 0.12s,
    color 0.12s;
  &:hover {
    color: var(--orx-ink);
  }
  /* Last, so these win over the base font-size/gap above at equal specificity. */
  @media (max-width: 768px) {
    height: 28px;
    /* Defeats the global 44px touch-target floor on buttons in App.js, which
       clamps the height above and leaves these pills chunkier than designed. */
    min-height: 0;
    /* Share the strip evenly so all four fit rather than scrolling. Basis auto
       keeps each pill at least as wide as its own label. */
    flex: 1 1 auto;
    justify-content: center;
    padding: 0 8px;
    font-size: 12px;
    gap: 5px;
  }
  @media (max-width: 390px) {
    padding: 0 6px;
    font-size: 11.5px;
  }
`;
const TabCount = styled.span`
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  opacity: 0.7;
  @media (max-width: 768px) {
    font-size: 11px;
  }
  /* Last thing to give up: below this the labels alone need the room. */
  @media (max-width: 390px) {
    display: none;
  }
`;
const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  /* Holds its width against the title, and lets the rare "no mailbox" pill
     wrap beneath the button instead of shoving it off the row. */
  @media (max-width: 768px) {
    flex-shrink: 0;
    flex-wrap: wrap;
    justify-content: flex-end;
    max-width: 55%;
  }
`;

/* ───────────────── Buttons / chips ───────────────── */
const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: ${(p) => (p.$lg ? 38 : p.$sm ? 28 : 32)}px;
  padding: 0 ${(p) => (p.$lg ? 16 : p.$sm ? 10 : 12)}px;
  border-radius: ${(p) => (p.$lg ? 10 : p.$sm ? 7 : 8)}px;
  border: 1px solid
    ${(p) =>
      p.$variant === "primary" || p.$variant === "accent"
        ? "var(--orx-accent)"
        : p.$variant === "danger"
          ? "var(--orx-danger)"
          : p.$variant === "ghost"
            ? "transparent"
            : "var(--orx-line)"};
  background: ${(p) =>
    p.$variant === "primary" || p.$variant === "accent"
      ? "var(--orx-accent)"
      : p.$variant === "danger"
        ? "var(--orx-danger)"
        : p.$variant === "ghost"
          ? "transparent"
          : "var(--orx-surface)"};
  color: ${(p) =>
    p.$variant === "primary" || p.$variant === "accent" || p.$variant === "danger"
      ? "var(--on-fill)"
      : "var(--orx-ink)"};
  font: inherit;
  font-weight: 500;
  font-size: ${(p) => (p.$lg ? 14 : p.$sm ? 12.5 : 13)}px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.12s,
    border-color 0.12s,
    transform 0.06s;
  &:hover {
    filter: ${(p) => (p.$variant === "danger" ? "brightness(0.93)" : "none")};
    background: ${(p) =>
      p.$variant === "primary" || p.$variant === "accent"
        ? "var(--orx-accent-2)"
        : p.$variant === "ghost"
          ? "var(--orx-bg-2)"
          : "var(--orx-surface-tint)"};
    border-color: ${(p) =>
      p.$variant === "primary" || p.$variant === "accent"
        ? "var(--orx-accent-2)"
        : p.$variant === "ghost"
          ? "transparent"
          : "var(--orx-line-strong)"};
  }
  &:active {
    transform: translateY(0.5px);
  }
  &[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
  /* Keep the height set above. The global 44px touch-target floor on buttons
     in App.js clamps it otherwise, which is what leaves buttons looking chunky
     and out of proportion on phones. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;
const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => p.$size || 32}px;
  height: ${(p) => p.$size || 32}px;
  /* Keeps the square square: the global 44px touch-target floor on buttons
     (App.js) clamps the height above and leaves a 32x44 rectangle on phones. */
  min-height: 0;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--orx-ink-soft);
  cursor: pointer;
  transition:
    background 0.12s,
    color 0.12s;
  &:hover {
    background: var(--orx-bg-2);
    color: var(--orx-ink);
  }
`;
const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  /* Selected filter: light blue (blue = what you've picked or can act on). */
  background: ${(p) => (p.$active ? "var(--accent-soft)" : "var(--orx-surface)")};
  border: 1px solid ${(p) => (p.$active ? "color-mix(in srgb, var(--accent) 45%, transparent)" : "var(--orx-line)")};
  font-size: 11.5px;
  color: ${(p) => (p.$active ? "var(--accent)" : "var(--orx-ink-soft)")};
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  font-weight: 500;
  line-height: 1;
  transition:
    background 0.12s,
    border-color 0.12s,
    color 0.12s;
  &:hover {
    background: ${(p) => (p.$active ? "var(--accent-soft)" : "var(--orx-surface-tint)")};
    border-color: ${(p) => (p.$active ? "var(--accent)" : "var(--orx-line-strong)")};
    color: ${(p) => (p.$active ? "var(--accent)" : "var(--orx-ink)")};
  }
  /* Keep the 22px pill: the global 44px touch-target floor (App.js) turns the
     filter strip into a row of fat capsules otherwise. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

/* ───────────────── Body split ───────────────── */
const Body = styled.div`
  flex: 1;
  display: grid;
  /* List pane is a fixed width so the right column (detail or composer)
   * always takes the same share of the viewport. With minmax(360px,480px)
   * the right column would resize as the window/sidebar changed, making
   * the compose modal feel jittery. */
  grid-template-columns: ${(p) => (p.$solo ? "1fr" : "420px 1fr")};
  min-height: 0;
  background: var(--orx-bg);
  position: relative;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* ───────────────── List pane ───────────────── */
const ListPaneWrap = styled.div`
  border-right: 1px solid var(--orx-line);
  background: var(--orx-surface);
  display: flex;
  flex-direction: column;
  min-height: 0;
  @media (max-width: 900px) {
    border-right: 0;
  }
`;
const SearchWrap = styled.div`
  padding: 12px 14px 8px;
  border-bottom: 1px solid var(--orx-line-soft);
  position: relative;
`;
const SearchBox = styled.div`
  position: relative;
`;
const SearchInput = styled.input`
  width: 100%;
  height: 34px;
  padding: 0 12px 0 36px;
  border-radius: 8px;
  border: 1px solid var(--orx-line);
  background: var(--orx-surface-tint);
  font: inherit;
  font-size: 13px;
  color: var(--orx-ink);
  outline: 0;
  box-sizing: border-box;
  &::placeholder {
    color: var(--orx-ink-muted);
  }
  &:focus {
    background: var(--orx-surface);
    border-color: var(--orx-ink-3);
    box-shadow: 0 0 0 3px var(--ink-ring);
  }
`;
const SearchLead = styled.div`
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--orx-ink-muted);
  pointer-events: none;
  display: flex;
`;
const ChipStrip = styled.div`
  padding: 8px 14px;
  border-bottom: 1px solid var(--orx-line-soft);
  display: flex;
  gap: 6px;
  align-items: center;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const ChipDivider = styled.div`
  width: 1px;
  height: 18px;
  background: var(--orx-line);
  margin: 0 4px;
  flex-shrink: 0;
`;
const ListHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px 8px 16px;
  border-bottom: 1px solid var(--orx-line-soft);
`;
const ListCount = styled.div`
  font-size: 12px;
  color: var(--orx-ink-muted);
  font-variant-numeric: tabular-nums;
  b {
    color: var(--orx-ink);
    font-weight: 600;
  }
`;
const SortBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 0;
  color: var(--orx-ink-soft);
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  &:hover {
    background: var(--orx-bg-2);
    color: var(--orx-ink);
  }
  /* Global 44px touch-target floor would double the count/sort row's height. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;
const List = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  background: var(--orx-surface);
  /* Stop overscroll bounce from revealing the page bg behind the list. */
  overscroll-behavior: contain;
`;
/* Fixed height so the list scans as an even column and never reflows as
   optional fields arrive. Four line boxes at 18px each = 72, plus 9px padding
   top and bottom = 90. Every row renders all four lines (a faint em dash where
   there's no value), so the height is always exactly filled. Change any line
   metric below and this number has to move with it. */
const ROW_HEIGHT = 90;
const Row = styled.div`
  display: grid;
  grid-template-columns: 24px 36px 1fr auto;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  height: ${ROW_HEIGHT}px;
  padding: 9px 16px;
  border-bottom: 1px solid var(--orx-line-soft);
  cursor: pointer;
  transition: background 0.1s;
  position: relative;
  background: ${(p) =>
    p.$active
      ? "var(--orx-accent-soft)"
      : p.$selected
        ? "var(--orx-accent-soft)"
        : "transparent"};
  &:hover {
    background: ${(p) =>
      p.$active || p.$selected
        ? "var(--orx-accent-soft)"
        : "var(--orx-surface-tint)"};
  }
  ${(p) =>
    p.$active &&
    `
    &::before {
      content: "";
      position: absolute;
      left: 0; top: 0; bottom: 0;
      width: 3px;
      background: var(--orx-accent);
    }
  `}
  /* Phone: the status badge alone can eat a third of the row, so trim the
     gutters (checkbox column matches its 18px box) to give the name room. */
  @media (max-width: 450px) {
    grid-template-columns: 18px 36px 1fr auto;
    gap: 10px;
    padding: 9px 12px;
  }
`;
const Check = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid
    ${(p) => (p.$on ? "var(--orx-elevated-bg)" : "var(--orx-line-strong)")};
  background: ${(p) =>
    p.$on ? "var(--orx-elevated-bg)" : "var(--orx-surface)"};
  display: grid;
  place-items: center;
  color: var(--orx-elevated-fg);
  flex-shrink: 0;
  transition:
    background 0.1s,
    border-color 0.1s;
`;
const RowName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: var(--orx-ink);
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const RowSub = styled.div`
  font-size: 12.5px;
  color: var(--orx-ink-soft);
  line-height: 16px;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  span.sep {
    color: var(--orx-ink-faint);
    margin: 0 5px;
  }
`;
// Pill rendered next to a candidate row whose ranking was refreshed while
// the user wasn't looking at them. Clears when they open the profile.
const RerankedPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--orx-accent-ink);
  background: var(--orx-accent-soft);
  border: 1px solid var(--orx-line);
  white-space: nowrap;
  /* Phone: icon only (label stays in the title) — beside the status badge the
     full pill left the name column a few characters wide. */
  @media (max-width: 450px) {
    font-size: 0;
    gap: 0;
    padding: 0 5px;
  }
`;
// Origin is shown by <OriginMark> (icon + hover label), not a text tag.
const OriginMarkSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Matches ProfileLinkMark's padding so both marks occupy the same box and
     sit evenly spaced, rather than one glyph crowding the other. */
  padding: 4px;
  color: var(--orx-ink-muted);
`;
/* LinkedIn beside the name. Same treatment as the profile modal on Find
   Candidates — brand blue with a tinted hover pill — so one control doesn't
   look like two different things across pages. */
const ProfileLinkMark = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${PROVIDER_BRAND.linkedin};
  padding: 4px;
  border-radius: 6px;
  transition: background 0.13s ease;

  &:hover,
  &:focus-visible {
    background: color-mix(in srgb, ${PROVIDER_BRAND.linkedin} 12%, transparent);
  }
  /* The global 44px touch-target floor on links (App.js) stretches this 24px
     mark into a 44px box, dropping it below the name it sits beside — and out
     of step with OriginMarkSlot, a span the global rule never matched. */
  min-height: 0;
`;
/* Stands in for a missing value. The row is a fixed height with a line
   reserved per field, so an absent company or location has to occupy its line
   rather than collapse it — otherwise the lines below shift up and the column
   stops aligning across rows. */
const NoValue = () => (
  <span style={{ color: "var(--orx-ink-faint)" }}>&mdash;</span>
);
/* Company sits one step quieter than the job title, so the stacked pair reads
   as title-then-employer rather than two facts of equal weight. */
const RowCompany = styled(RowSub)`
  color: var(--orx-ink-muted);
`;
const RowMeta = styled.div`
  font-size: 11.5px;
  color: var(--orx-ink-muted);
  line-height: 16px;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  white-space: nowrap;

  /* The pin is a flex item, so it inherits flex-shrink: 1 and a long location
     squeezes it to zero width instead of truncating the text — the icon just
     disappears on exactly the rows that are hardest to read. Pin holds its
     size; the text beside it is what gives way. */
  svg {
    flex-shrink: 0;
  }
  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
// Fills the list and centres, level with the empty state in the panel beside it.
const RowEmpty = styled.div`
  min-height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  color: var(--orx-ink-muted);
  font-size: 13px;
`;
// Skeleton placeholder shaped exactly like <Row> (same grid, padding, avatar
// size and text line heights) so the list reserves its height while loading.
function SkeletonRow() {
  return (
    <Row as="div" style={{ cursor: "default" }}>
      <Skeleton width={18} height={18} radius="4px" />
      <Skeleton width={36} height={36} circle />
      <div style={{ minWidth: 0 }}>
        {/* Bars sum to the same 72px the real four lines occupy, so they sit
            where the text will land rather than drifting up the fixed row. */}
        <Skeleton width="55%" height={14} mb={7} />
        <Skeleton width="78%" height={12.5} mb={7} />
        <Skeleton width="62%" height={12.5} mb={7} />
        <Skeleton width="40%" height={11.5} />
      </div>
      <Skeleton width={56} height={20} radius="999px" />
    </Row>
  );
}

/* ───────────────── Avatar / badge ───────────────── */
const AvatarBox = styled.div`
  width: ${(p) =>
    p.$size === "xl"
      ? 88
      : p.$size === "lg"
        ? 64
        : p.$size === "sm"
          ? 28
          : p.$size === "xs"
            ? 22
            : 36}px;
  height: ${(p) =>
    p.$size === "xl"
      ? 88
      : p.$size === "lg"
        ? 64
        : p.$size === "sm"
          ? 28
          : p.$size === "xs"
            ? 22
            : 36}px;
  border-radius: 99px;
  background: ${(p) => p.$bg || "var(--orx-bg-2)"};
  color: ${(p) => p.$fg || "var(--orx-ink-soft)"};
  font-size: ${(p) =>
    p.$size === "xl"
      ? 28
      : p.$size === "lg"
        ? 22
        : p.$size === "sm"
          ? 11
          : p.$size === "xs"
            ? 9.5
            : 12.5}px;
  font-weight: 600;
  display: grid;
  place-items: center;
  overflow: hidden;
  flex-shrink: 0;
  letter-spacing: 0.02em;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const AVATAR_PX = { xl: 88, lg: 64, sm: 28, xs: 22 };

function Avatar({ person, size }) {
  const [err, setErr] = useState(false);
  if ((!person?.avatarSrc || err) && person?.kind === "client" && person?.company) {
    const px = AVATAR_PX[size] || 36;
    return (
      <CompanyLogo
        name={person.company}
        size={px}
        radius={99}
        bg={person?.tint?.bg}
        fg={person?.tint?.fg}
        fallback={person?.initials || "·"}
        style={{ fontWeight: 600, fontSize: Math.round(px * 0.34) }}
      />
    );
  }
  if (person?.avatarSrc && !err) {
    return (
      <AvatarBox $size={size} $bg={person.tint.bg} $fg={person.tint.fg}>
        <img src={person.avatarSrc} alt="" onError={() => setErr(true)} />
      </AvatarBox>
    );
  }
  return (
    <AvatarBox $size={size} $bg={person?.tint?.bg} $fg={person?.tint?.fg}>
      {person?.initials || "·"}
    </AvatarBox>
  );
}

const Dot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 99px;
  flex: 0 0 6px;
  background: ${(p) => p.$bg || "var(--orx-ink-faint)"};
`;
const BadgeBox = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 8px;
  border-radius: 99px;
  font-size: 11.5px;
  font-weight: 500;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
`;
// Semantic grouping: target = new (info), outreach = awaiting (warn),
// longlist→interviewing = active pipeline (accent), offer/placement = won
// (success), rejected = lost (danger).
const STATUS_COLORS = {
  target: {
    bg: "var(--orx-info-soft)",
    fg: "var(--orx-info)",
    dot: "var(--orx-info)",
  },
  outreach: {
    bg: "var(--orx-warn-soft)",
    fg: "var(--orx-warn)",
    dot: "var(--orx-warn)",
  },
  longlist: {
    bg: "var(--orx-accent-soft)",
    fg: "var(--orx-accent-ink)",
    dot: "var(--orx-accent)",
  },
  shortlist: {
    bg: "var(--orx-accent-soft)",
    fg: "var(--orx-accent-ink)",
    dot: "var(--orx-accent)",
  },
  first_interview: {
    bg: "var(--orx-accent-soft)",
    fg: "var(--orx-accent-ink)",
    dot: "var(--orx-accent)",
  },
  interviewing: {
    bg: "var(--orx-accent-soft)",
    fg: "var(--orx-accent-ink)",
    dot: "var(--orx-accent)",
  },
  offer: {
    bg: "var(--orx-success-soft)",
    fg: "var(--orx-success)",
    dot: "var(--orx-success)",
  },
  placement: {
    bg: "var(--orx-success-soft)",
    fg: "var(--orx-success)",
    dot: "var(--orx-success)",
  },
  rejected: {
    bg: "var(--orx-danger-soft)",
    fg: "var(--orx-danger)",
    dot: "var(--orx-danger)",
  },
};
/* `varies` marks a candidate whose status differs between their jobs — the
   badge is showing the furthest of them, not the whole picture. */
/* Superscript on a badge showing the furthest of several per-job statuses. */
const VariesMark = styled.sup`
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  margin-left: 1px;
  opacity: 0.75;
`;

function StatusBadge({ status, compact, varies, title }) {
  const s = STATUSES.find((x) => x.id === status) || STATUSES[0];
  const col = STATUS_COLORS[s.id] || STATUS_COLORS[DEFAULT_STATUS];
  return (
    <BadgeBox $bg={col.bg} $fg={col.fg} title={title}>
      <Dot $bg={col.dot} />
      {!compact && s.label}
      {varies && <VariesMark>+</VariesMark>}
    </BadgeBox>
  );
}

const StatusChipButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 8px 0 8px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1;
  cursor: ${(p) => (p.$static ? "default" : "pointer")};
  white-space: nowrap;
  transition: filter 0.12s;
  &:hover {
    filter: ${(p) => (p.$static ? "none" : "brightness(0.96)")};
  }
`;
/* Per-job status control on an Assigned jobs row — the only place a candidate's
   stage is edited. A plain <select>: these sit inside a scrolling card and a
   portalled menu would need its own positioning for every row. */
const JobStatusSelect = styled.select`
  height: 28px;
  max-width: 138px;
  padding: 0 6px;
  border: 1px solid var(--orx-line);
  border-radius: 8px;
  background: var(--orx-surface);
  /* Label carries the stage colour, echoing the row's rail and the badges
     used everywhere else — the control shows the stage, not just holds it. */
  color: ${(p) => p.$fg || "var(--orx-ink)"};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    border-color 0.12s,
    box-shadow 0.12s;
  &:hover {
    border-color: var(--orx-line-strong);
  }
  &:focus-visible {
    outline: none;
    border-color: var(--orx-accent);
    box-shadow: 0 0 0 3px var(--orx-accent-soft);
  }
  /* The menu itself is OS-drawn; keep its rows on the surface palette. */
  option {
    color: var(--orx-ink);
    background: var(--orx-surface);
  }
  /* Phone: keep the 28px control (global 44px floor would stretch the row) and
     cede some width to the job title beside it. */
  @media (max-width: 450px) {
    min-height: 0;
    max-width: 118px;
  }
`;

function JobStatusPicker({ status, onChange }) {
  const col = STATUS_COLORS[status] || STATUS_COLORS[DEFAULT_STATUS];
  return (
    <JobStatusSelect
      value={status}
      $fg={col.fg}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      title="Pipeline stage for this job"
      aria-label="Pipeline stage for this job"
    >
      {STATUSES.map((s) => (
        <option key={s.id} value={s.id}>
          {s.label}
        </option>
      ))}
    </JobStatusSelect>
  );
}

/* `readOnly` renders the same chip as a plain, non-interactive span: the header
   reports the resolved stage, and every edit happens on the per-job pickers
   under Assigned jobs, where the scope is explicit. */
function StatusChip({ status, onClick, varies, title, readOnly }) {
  const s = STATUSES.find((x) => x.id === status) || STATUSES[0];
  const col = STATUS_COLORS[s.id] || STATUS_COLORS[DEFAULT_STATUS];
  return (
    <StatusChipButton
      as={readOnly ? "span" : "button"}
      $bg={col.bg}
      $fg={col.fg}
      $border={col.bg}
      $static={readOnly}
      onClick={readOnly ? undefined : onClick}
      title={title}
    >
      <Dot $bg={col.dot} />
      {s.label}
      {varies && <VariesMark>+</VariesMark>}
      {!readOnly && <FiChevronDown size={12} />}
    </StatusChipButton>
  );
}

/* ───────────────── Dropdown menu ───────────────── */
/** Stationary frame: holds the bg / border / shadow. Doesn't scroll, so
 * macOS rubber-band can never displace it and expose what's behind. */
const Menu = styled.div`
  position: absolute;
  background: var(--orx-surface);
  border: 1px solid var(--orx-line);
  border-radius: 10px;
  box-shadow: ${C.shadowLg};
  min-width: 200px;
  z-index: 50;
  animation: ${fadeIn} 0.12s ease both;
  /* Clip the inner scroll so the bounce stays inside the frame visually. */
  overflow: hidden;
  @media (max-width: 450px) {
    max-width: calc(100vw - 16px);
  }
`;
/** Inner scroll container. Bounce happens here; the frame above stays put. */
const MenuScroll = styled.div`
  padding: 6px;
  overflow-y: auto;
  overscroll-behavior: contain;
`;
const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: ${(p) => (p.$danger ? "var(--orx-danger)" : "var(--orx-ink)")};
  &:hover {
    background: var(--orx-bg-2);
  }
`;
const MenuDivider = styled.div`
  height: 1px;
  background: var(--orx-line-soft);
  margin: 4px 0;
`;
/* Job rows inside a dropdown. Mirrors JobItemTitle/JobItemMeta (the assign-job
   list) one step down in size, so a job reads the same wherever it appears. */
const JobOptionTitle = styled.div`
  font-weight: 600;
  color: var(--orx-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const JobOptionMeta = styled.div`
  font-size: 11.5px;
  color: var(--orx-ink-muted);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* Generic menu wrapper closes on outside click */
function DropMenu({
  open,
  onClose,
  children,
  position,
  anchorRef,
  align = "left",
  maxHeight,
  minWidth,
}) {
  const ref = useRef(null);

  // Outside-click close. When portalled, anchor clicks shouldn't close us
  // (the anchor toggles us via its own onClick).
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose();
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, onClose, anchorRef]);

  // Portal path: position by the anchor's bounding rect so we can escape any
  // ancestor with `overflow: hidden / auto` (the chip strip clips us otherwise).
  const [portalStyle, setPortalStyle] = useState(null);
  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) return;
    function reposition() {
      const r = anchorRef.current.getBoundingClientRect();
      setPortalStyle({
        position: "fixed",
        top: r.bottom + 4,
        left: align === "right" ? undefined : r.left,
        right:
          align === "right"
            ? Math.max(0, window.innerWidth - r.right)
            : undefined,
        minWidth,
        zIndex: 1000,
      });
    }
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, anchorRef, align, minWidth]);

  // Anchored at the chip's edge, a menu opened near the far side of a phone
  // screen runs off it; once its width is known, nudge it back inside.
  useLayoutEffect(() => {
    if (!portalStyle || !ref.current) return;
    const w = ref.current.offsetWidth;
    if (align === "right") {
      const maxRight = Math.max(0, window.innerWidth - w - 8);
      if (portalStyle.right > maxRight)
        setPortalStyle((s) => ({ ...s, right: maxRight }));
    } else {
      const maxLeft = Math.max(8, window.innerWidth - w - 8);
      if (portalStyle.left > maxLeft)
        setPortalStyle((s) => ({ ...s, left: maxLeft }));
    }
  }, [portalStyle, align]);

  if (!open) return null;

  const scrollStyle = maxHeight ? { maxHeight } : undefined;

  if (anchorRef) {
    if (!portalStyle) return null;
    return createPortal(
      <Menu ref={ref} style={portalStyle}>
        <MenuScroll style={scrollStyle}>{children}</MenuScroll>
      </Menu>,
      document.body,
    );
  }

  return (
    <Menu ref={ref} style={position}>
      <MenuScroll style={scrollStyle}>{children}</MenuScroll>
    </Menu>
  );
}

/* ───────────────── List pane component ───────────────── */
function ListPane({
  tab,
  people,
  query,
  onQuery,
  statusFilter,
  setStatusFilter,
  savedKind,
  setSavedKind,
  companyFilter,
  setCompanyFilter,
  countryFilter,
  setCountryFilter,
  seniorityFilter,
  setSeniorityFilter,
  jobFilter,
  setJobFilter,
  jobOptions,
  jobIdsByCandidate,
  sortBy,
  setSortBy,
  selectedIds,
  onToggleId,
  onOpenPerson,
  activeId,
  statusMap,
  loading,
  pending,
  tabEmpty,
  showFilters,
  onScrollBottom,
  loadingMore,
  totalCount,
  disableClientSort,
  companyOptionsOverride,
  countryOptionsOverride,
  hideCompanyFilter,
  unseenMatches,
  selectionBar,
}) {
  // Loading placeholders fill the list's own scroll box, however tall the screen is.
  const listSkelRef = useRef(null);
  const listSkelCount = useFillCount(listSkelRef, { min: 7, enabled: !!loading });
  const [showSort, setShowSort] = useState(false);
  const [showCompany, setShowCompany] = useState(false);
  const [showCountry, setShowCountry] = useState(false);
  const [showSeniority, setShowSeniority] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const companyChipRef = useRef(null);
  const countryChipRef = useRef(null);
  const statusChipRef = useRef(null);
  const seniorityChipRef = useRef(null);
  const searchChipRef = useRef(null);

  // On Saved a main filter picks candidate vs client; every other tab IS its
  // own view kind. Sub-filters key off this, not `tab`, so the same chips serve
  // both the Candidates/Clients tabs and the two Saved sub-views.
  const viewKind = tab === "saved" ? savedKind : tab;
  const isCandidateView = viewKind === "candidates";

  // Only show seniority buckets that actually appear in the current list.
  const seniorityOptions = useMemo(() => {
    const present = new Set(people.map((p) => p.seniority || "unknown"));
    return SENIORITY_OPTIONS.filter((o) => present.has(o.id));
  }, [people]);

  const activeJobTitle =
    jobOptions.find((j) => j.jobId === jobFilter)?.title || "";

  const companies = useMemo(() => {
    // Prefer the full options list (fetched once for paginated tabs) so the
    // dropdown isn't limited to companies already paged in.
    if (companyOptionsOverride && companyOptionsOverride.length > 0) {
      return [...companyOptionsOverride].sort();
    }
    return Array.from(
      new Set(people.map((p) => p.company).filter(Boolean)),
    ).sort();
  }, [people, companyOptionsOverride]);
  const countries = useMemo(() => {
    if (countryOptionsOverride && countryOptionsOverride.length > 0) {
      return [...countryOptionsOverride].sort();
    }
    return Array.from(
      new Set(people.map((p) => p.country).filter(Boolean)),
    ).sort();
  }, [people, countryOptionsOverride]);

  const filtered = useMemo(() => {
    let arr = people;
    // Saved mixes both kinds; the main filter narrows to one before anything else.
    if (tab === "saved")
      arr = arr.filter(
        (p) => p.kind === (viewKind === "clients" ? "client" : "candidate"),
      );
    if (query) {
      const q = query.toLowerCase();
      arr = arr.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.role.toLowerCase().includes(q) ||
          p.company.toLowerCase().includes(q) ||
          p.location.toLowerCase().includes(q) ||
          (p.country || "").toLowerCase().includes(q) ||
          p.email.toLowerCase().includes(q),
      );
    }
    // Status is a candidate-pipeline concept; clients have none.
    if (isCandidateView && statusFilter !== "all") {
      arr = arr.filter(
        (p) =>
          rowStatusOf(statusMap, jobFilter, p).status ===
          statusFilter,
      );
    }
    if (companyFilter !== "all")
      arr = arr.filter((p) => p.company === companyFilter);
    if (countryFilter !== "all")
      arr = arr.filter((p) => p.country === countryFilter);
    if (isCandidateView && seniorityFilter !== "all")
      arr = arr.filter((p) => (p.seniority || "unknown") === seniorityFilter);
    if (isCandidateView && jobFilter && jobFilter !== "all")
      arr = arr.filter((p) => (jobIdsByCandidate[p.id] || []).includes(jobFilter));
    if (disableClientSort) return arr;
    const sorted = [...arr];
    if (sortBy === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === "added") {
      // Newest first; rows without a date sink to the bottom, ties on name.
      const t = (p) => (p.addedAt ? new Date(p.addedAt).getTime() || 0 : 0);
      sorted.sort((a, b) => t(b) - t(a) || a.name.localeCompare(b.name));
    } else if (sortBy === "company")
      sorted.sort((a, b) => a.company.localeCompare(b.company));
    else if (sortBy === "status") {
      // Pipeline order: target first through to rejected last. Unknown ids
      // sink to the bottom; ties break on name so the order is stable.
      const rank = (p) => {
        const i = statusRank(
          rowStatusOf(statusMap, jobFilter, p).status,
        );
        return i === -1 ? STATUSES.length : i;
      };
      sorted.sort((a, b) => {
        const d = rank(a) - rank(b);
        return d !== 0 ? d : a.name.localeCompare(b.name);
      });
    }
    return sorted;
  }, [
    people,
    tab,
    viewKind,
    isCandidateView,
    query,
    statusFilter,
    companyFilter,
    countryFilter,
    seniorityFilter,
    jobFilter,
    jobIdsByCandidate,
    sortBy,
    statusMap,
    disableClientSort,
  ]);

  return (
    <ListPaneWrap>
      <SearchWrap>
        <SearchBox>
          <SearchLead>
            <FiSearch size={14} />
          </SearchLead>
          <SearchInput
            placeholder={`Search ${tab}…`}
            value={query}
            onChange={(e) => onQuery(e.target.value)}
          />
        </SearchBox>
      </SearchWrap>

      {showFilters && (
        <ChipStrip>
          {tab === "saved" && (
            <>
              {[
                ["candidates", "Candidates"],
                ["clients", "Clients"],
              ].map(([id, label]) => (
                <Chip
                  key={id}
                  $active={savedKind === id}
                  onClick={() => setSavedKind(id)}
                >
                  {label}
                </Chip>
              ))}
              <ChipDivider />
            </>
          )}
          {isCandidateView && (
            <>
          <div
            ref={statusChipRef}
            style={{ position: "relative", flexShrink: 0 }}
          >
            <Chip
              $active={statusFilter !== "all"}
              onClick={() => {
                setShowStatus((v) => !v);
                setShowCompany(false);
                setShowCountry(false);
              }}
            >
              {statusFilter !== "all" && (
                <Dot $bg={(STATUS_COLORS[statusFilter] || {}).dot} />
              )}
              {statusFilter === "all"
                ? "Status"
                : (STATUSES.find((s) => s.id === statusFilter) || {}).label ||
                  "Status"}
              <FiChevronDown size={12} />
            </Chip>
            <DropMenu
              open={showStatus}
              onClose={() => setShowStatus(false)}
              anchorRef={statusChipRef}
              maxHeight={320}
              minWidth={200}
            >
              <MenuItem
                onClick={() => {
                  setStatusFilter("all");
                  setShowStatus(false);
                }}
              >
                All statuses
                {statusFilter === "all" && (
                  <FiCheck size={12} style={{ marginLeft: "auto" }} />
                )}
              </MenuItem>
              <MenuDivider />
              {STATUSES.map((s) => (
                <MenuItem
                  key={s.id}
                  onClick={() => {
                    setStatusFilter(s.id);
                    setShowStatus(false);
                  }}
                >
                  <Dot $bg={STATUS_COLORS[s.id].dot} />
                  {s.label}
                  {statusFilter === s.id && (
                    <FiCheck size={12} style={{ marginLeft: "auto" }} />
                  )}
                </MenuItem>
              ))}
            </DropMenu>
          </div>
          <ChipDivider />
            </>
          )}
          {!hideCompanyFilter && (
            <div
              ref={companyChipRef}
              style={{ position: "relative", flexShrink: 0 }}
            >
              <Chip
                $active={companyFilter !== "all"}
                onClick={() => {
                  setShowCompany((v) => !v);
                  setShowCountry(false);
                }}
              >
                <Building size={12} />
                {companyFilter === "all" ? "Company" : companyFilter}
                <FiChevronDown size={12} />
              </Chip>
              <DropMenu
                open={showCompany}
                onClose={() => setShowCompany(false)}
                anchorRef={companyChipRef}
                maxHeight={280}
                minWidth={220}
              >
                <MenuItem
                  onClick={() => {
                    setCompanyFilter("all");
                    setShowCompany(false);
                  }}
                >
                  All companies
                </MenuItem>
                <MenuDivider />
                {companies.length === 0 ? (
                  <div
                    style={{
                      padding: "10px 12px",
                      fontSize: 12.5,
                      color: "var(--orx-ink-muted)",
                    }}
                  >
                    No companies found
                  </div>
                ) : (
                  companies.map((c) => (
                    <MenuItem
                      key={c}
                      onClick={() => {
                        setCompanyFilter(c);
                        setShowCompany(false);
                      }}
                    >
                      {c}
                      {companyFilter === c && (
                        <FiCheck size={12} style={{ marginLeft: "auto" }} />
                      )}
                    </MenuItem>
                  ))
                )}
              </DropMenu>
            </div>
          )}
          <div
            ref={countryChipRef}
            style={{ position: "relative", flexShrink: 0 }}
          >
            <Chip
              $active={countryFilter !== "all"}
              onClick={() => {
                setShowCountry((v) => !v);
                setShowCompany(false);
              }}
            >
              <FiMapPin size={12} />
              {countryFilter === "all" ? "Country" : countryFilter}
              <FiChevronDown size={12} />
            </Chip>
            <DropMenu
              open={showCountry}
              onClose={() => setShowCountry(false)}
              anchorRef={countryChipRef}
              maxHeight={280}
              minWidth={220}
            >
              <MenuItem
                onClick={() => {
                  setCountryFilter("all");
                  setShowCountry(false);
                }}
              >
                All countries
              </MenuItem>
              <MenuDivider />
              {countries.length === 0 ? (
                <div
                  style={{
                    padding: "10px 12px",
                    fontSize: 12.5,
                    color: "var(--orx-ink-muted)",
                  }}
                >
                  No countries found
                </div>
              ) : (
                countries.map((c) => (
                  <MenuItem
                    key={c}
                    onClick={() => {
                      setCountryFilter(c);
                      setShowCountry(false);
                    }}
                  >
                    {c}
                    {countryFilter === c && (
                      <FiCheck size={12} style={{ marginLeft: "auto" }} />
                    )}
                  </MenuItem>
                ))
              )}
            </DropMenu>
          </div>
          {isCandidateView && seniorityOptions.length > 0 && (
            <div
              ref={seniorityChipRef}
              style={{ position: "relative", flexShrink: 0 }}
            >
              <Chip
                $active={seniorityFilter !== "all"}
                onClick={() => {
                  setShowSeniority((v) => !v);
                  setShowCompany(false);
                  setShowCountry(false);
                }}
              >
                <FiBarChart2 size={12} />
                {seniorityFilter === "all"
                  ? "Seniority"
                  : SENIORITY_LABELS[seniorityFilter] || "Seniority"}
                <FiChevronDown size={12} />
              </Chip>
              <DropMenu
                open={showSeniority}
                onClose={() => setShowSeniority(false)}
                anchorRef={seniorityChipRef}
                maxHeight={280}
                minWidth={200}
              >
                <MenuItem
                  onClick={() => {
                    setSeniorityFilter("all");
                    setShowSeniority(false);
                  }}
                >
                  All levels
                </MenuItem>
                <MenuDivider />
                {seniorityOptions.map((o) => (
                  <MenuItem
                    key={o.id}
                    onClick={() => {
                      setSeniorityFilter(o.id);
                      setShowSeniority(false);
                    }}
                  >
                    {o.label}
                    {seniorityFilter === o.id && (
                      <FiCheck size={12} style={{ marginLeft: "auto" }} />
                    )}
                  </MenuItem>
                ))}
              </DropMenu>
            </div>
          )}
          {isCandidateView && jobOptions.length > 0 && (
              <div
                ref={searchChipRef}
                style={{ position: "relative", flexShrink: 0 }}
              >
                <Chip
                  $active={jobFilter !== "all"}
                  onClick={() => {
                    setShowSearch((v) => !v);
                    setShowCompany(false);
                    setShowCountry(false);
                  }}
                >
                  <FiBriefcase size={12} />
                  {jobFilter === "all"
                    ? "Search Title"
                    : activeJobTitle || "Search Title"}
                  <FiChevronDown size={12} />
                </Chip>
                <DropMenu
                  open={showSearch}
                  onClose={() => setShowSearch(false)}
                  anchorRef={searchChipRef}
                  maxHeight={280}
                  minWidth={260}
                >
                  <MenuItem
                    onClick={() => {
                      setJobFilter("all");
                      setShowSearch(false);
                    }}
                  >
                    All Search Titles
                  </MenuItem>
                  <MenuDivider />
                  {jobOptions.map((j) => (
                    <MenuItem
                      key={j.jobId}
                      onClick={() => {
                        setJobFilter(j.jobId);
                        setShowSearch(false);
                      }}
                    >
                      {/* Same shape jobs take everywhere else on this page:
                          title, then the company muted on its own line. */}
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <JobOptionTitle>{j.title}</JobOptionTitle>
                        {j.company && (
                          <JobOptionMeta>{j.company}</JobOptionMeta>
                        )}
                      </span>
                      {jobFilter === j.jobId && (
                        <FiCheck size={12} style={{ marginLeft: "auto" }} />
                      )}
                    </MenuItem>
                  ))}
                </DropMenu>
              </div>
            )}
        </ChipStrip>
      )}

      <ListHead>
        <ListCount>
          {/* Clients are server-paginated → show the true total. Other tabs
              are filtered client-side, so the count tracks the active filters.
              While the sources are still loading, hold a skeleton — a partial
              count (3 → 19) reads as data popping in. */}
          {loading ? (
            <Skeleton
              width={18}
              height={11}
              radius="4px"
              inline
              style={{ verticalAlign: "middle" }}
            />
          ) : (
            <b>{totalCount != null ? totalCount : filtered.length}</b>
          )}
          <span> {tab}</span>
          {selectedIds.size > 0 && (
            <span>
              {" · "}
              <b>{selectedIds.size}</b> selected
            </span>
          )}
        </ListCount>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <SortBtn onClick={() => setShowSort((v) => !v)}>
              Sort:{" "}
              {sortBy === "company"
                ? "Company"
                : sortBy === "status"
                  ? "Status"
                  : sortBy === "added"
                    ? "Date added"
                    : "Name"}
              <FiChevronDown size={12} />
            </SortBtn>
            <DropMenu
              open={showSort}
              onClose={() => setShowSort(false)}
              position={{ top: "calc(100% + 4px)", right: 0, minWidth: 160 }}
            >
              {[
                ["name", "Name (A–Z)"],
                ...(tab === "clients" ? [] : [["added", "Date added (newest)"]]),
                // Candidate company is typically empty (filled by CSV import
                // only) so the option isn't useful there.
                ...(tab === "candidates" ? [] : [["company", "Company"]]),
                // Clients short-circuit client sorting (disableClientSort),
                // so a status sort there would be a no-op menu item.
                ...(tab === "clients"
                  ? []
                  : [["status", "Status (target → rejected)"]]),
              ].map(([id, lbl]) => (
                <MenuItem
                  key={id}
                  onClick={() => {
                    setSortBy(id);
                    setShowSort(false);
                  }}
                >
                  {lbl}
                  {sortBy === id && (
                    <FiCheck size={12} style={{ marginLeft: "auto" }} />
                  )}
                </MenuItem>
              ))}
            </DropMenu>
          </div>
        </div>
      </ListHead>
      {/* Selection toolbar slides in under the count/sort row while anything
          is ticked (count + bulk actions on one line). */}
      {selectionBar}

      <List
        ref={listSkelRef}
        onScroll={
          onScrollBottom
            ? (e) => {
                if (loadingMore) return;
                const el = e.currentTarget;
                if (el.scrollHeight - el.scrollTop - el.clientHeight <= 200) {
                  onScrollBottom();
                }
              }
            : undefined
        }
      >
        {loading ? (
          Array.from({ length: listSkelCount }).map((_, i) => <SkeletonRow key={i} />)
        ) : pending ? null : tabEmpty ? (
          <FadeIn style={{ height: "100%" }}>
            <RowEmpty>
              <FiUsers size={24} style={{ color: "var(--orx-ink-faint)" }} />
              <div style={{ marginTop: 12, fontSize: 14, fontWeight: 500, color: "var(--orx-ink-soft)" }}>
                No {tab} yet
              </div>
              <div style={{ marginTop: 4, fontSize: 12.5 }}>
                {tab === "candidates"
                  ? "People you find or upload show up here."
                  : tab === "saved"
                    ? "People you save show up here."
                    : "Clients show up here once they're imported."}
              </div>
            </RowEmpty>
          </FadeIn>
        ) : filtered.length === 0 ? (
          <RowEmpty>
            <FiSearch size={24} style={{ color: "var(--orx-ink-faint)" }} />
            <div
              style={{
                marginTop: 12,
                fontSize: 14,
                fontWeight: 500,
                color: "var(--orx-ink-soft)",
              }}
            >
              No matches
            </div>
            <div style={{ marginTop: 4, fontSize: 12.5 }}>
              Try clearing filters or a different search.
            </div>
          </RowEmpty>
        ) : (
          <FadeIn>
            {filtered.map((p) => {
              const { status, varies: statusVaries } = rowStatusOf(
                statusMap,
                jobFilter,
                p,
              );
              const selected = selectedIds.has(p.id);
              const active = activeId === p.id;
              return (
                <Row
                  key={p.id}
                  $selected={selected}
                  $active={active}
                  onClick={() => onOpenPerson(p)}
                >
                  <Check
                    $on={selected}
                    onClick={(e) => {
                      // Stop the row's onClick from firing twice — we drive
                      // both behaviors explicitly here so the navigation is
                      // tied to a successful toggle. Ticking a checkbox
                      // also opens that candidate's profile so the user can
                      // confirm what they just added to their selection.
                      e.stopPropagation();
                      onToggleId(p.id);
                      // Only for the first tick, though: from the second
                      // person on, the right column becomes the bulk-action
                      // pane and opening a profile would hide it again.
                      // Except on phones, where the profile is a full-screen
                      // overlay: opening it would throw the user out of the
                      // list they are selecting from. There the box only
                      // selects, and tapping the row opens the profile.
                      if (!selected && selectedIds.size === 0 && !isSinglePaneViewport())
                        onOpenPerson(p);
                    }}
                  >
                    {selected && <FiCheck size={12} strokeWidth={3} />}
                  </Check>
                  <Avatar person={p} />
                  <div style={{ minWidth: 0 }}>
                    {/* One line per fact — name, job title, company, location.
                        Candidates used to fold title and company into a single
                        "title · company" line, which truncated to nonsense on
                        long titles and read differently from the client rows. */}
                    <RowName>{p.name}</RowName>
                    <RowSub>{p.role || <NoValue />}</RowSub>
                    <RowCompany>{p.company || <NoValue />}</RowCompany>
                    <RowMeta>
                      <FiMapPin size={12} />
                      <span>
                        {[p.location, p.country].filter(Boolean).join(", ") || (
                          <NoValue />
                        )}
                      </span>
                    </RowMeta>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    {unseenMatches && unseenMatches.has(p.id) && (
                      <RerankedPill title="Re-ranked since you last viewed">
                        <Spark size={12} /> Re-ranked
                      </RerankedPill>
                    )}
                    {/* Status is a candidate-pipeline concept; clients have none. */}
                    {p.kind !== "client" && (
                      <StatusBadge
                        status={status}
                        varies={statusVaries}
                        title={
                          statusVaries
                            ? "Furthest stage — differs between this person's jobs"
                            : undefined
                        }
                      />
                    )}
                  </div>
                </Row>
              );
            })}
          </FadeIn>
        )}
        {loadingMore && (
          <div
            style={{
              padding: "10px 16px",
              textAlign: "center",
              fontSize: 11.5,
              color: "var(--orx-ink-muted)",
            }}
          >
            Loading…
          </div>
        )}
        {!loadingMore &&
          totalCount != null &&
          people.length >= totalCount &&
          totalCount > 0 && (
            <div
              style={{
                padding: "14px 16px",
                textAlign: "center",
                fontSize: 11.5,
                color: "var(--orx-ink-faint)",
              }}
            >
              End of list
            </div>
          )}
      </List>
    </ListPaneWrap>
  );
}

/* ───────────────── Detail pane ───────────────── */
/** Wrapper that occupies the right grid column. Hosts the scrollable
 * Detail and any overlays (matched-jobs modal) without letting the modal
 * scroll with the content or escape into the list pane. */
const DetailColumn = styled.div`
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

/* The outer right column. Separate from DetailColumn because DetailPane reuses
   DetailColumn as its own root — a mobile rule on the shared component hides
   the profile content nested inside this one.
   Phone: the profile takes the whole screen rather than sharing it with the
   list. Layered over the list (which stays in flow underneath) instead of
   hiding it, so a missing profile falls back to the list, never a blank
   screen. "Back to list" dismisses it. Body is the positioning context. */
const RightColumn = styled(DetailColumn)`
  @media (max-width: 900px) {
    position: absolute;
    inset: 0;
    z-index: 5;
    background: var(--orx-bg);
    display: ${(p) => (p.$mobileActive ? "flex" : "none")};
  }
`;
/* Tab strip rendered above the right column when both a compose modal and
 * a profile are open simultaneously. */
const RightTabStrip = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
  background: var(--orx-bg);
  border-bottom: 1px solid var(--orx-line);
  padding: 6px 12px 0;
  flex: 0 0 auto;
  @media (max-width: 450px) {
    padding: 6px 8px 0;
  }
`;
const RightTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  margin-right: 4px;
  border: 1px solid var(--orx-line);
  border-bottom: 0;
  border-radius: 8px 8px 0 0;
  background: ${(p) =>
    p.$active ? "var(--orx-bg-card, var(--orx-surface))" : "transparent"};
  color: ${(p) => (p.$active ? "var(--orx-ink)" : "var(--orx-ink-muted)")};
  font: inherit;
  font-size: 12.5px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: pointer;
  position: relative;
  top: 1px; /* pull the active tab down to overlap the bottom border */
  &:hover {
    color: var(--orx-ink);
  }
  .name {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .count {
    font-size: 11px;
    color: var(--orx-ink-muted);
    font-weight: 500;
  }
  /* Phone: a 180px name plus the Compose tab overran the screen. Let the
     profile tab shrink (its name ellipsizes) and keep the 32px tab height
     against the global 44px touch-target floor. */
  @media (max-width: 450px) {
    min-height: 0;
    min-width: 0;
    padding: 0 10px;
    gap: 6px;
    .name {
      max-width: 140px;
    }
  }
`;
const RightTabClose = styled.span`
  display: inline-grid;
  place-items: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin-left: 4px;
  color: var(--orx-ink-muted);
  &:hover {
    background: var(--orx-line);
    color: var(--orx-ink);
  }
`;
const RightPanel = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: ${(p) => (p.$show ? "flex" : "none")};
  flex-direction: column;
`;
const Detail = styled.div`
  flex: 1;
  overflow-y: auto;
  background: var(--orx-bg);
  min-width: 0;
  animation: ${fadeIn} 0.18s ease both;
`;
const DetailInner = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: 22px 28px 40px;
  @media (max-width: 768px) {
    padding: 16px 16px 32px;
  }
  @media (max-width: 450px) {
    padding: 14px 14px 28px;
  }
`;
const ProfileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 4px 0 18px;
  @media (max-width: 450px) {
    gap: 14px;
  }
`;

/* Send email / Job match / Save. They read as one set of actions on the person,
   so they stay on a single row at every width — the buttons trim down instead
   of wrapping. Selectors target the three buttons only: Job match sits in a
   relative wrapper for its dropdown, whose menu items are a level deeper. */
const ProfileActions = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  margin: 4px 0 8px;
  & > button,
  & > div > button {
    white-space: nowrap;
  }
  @media (max-width: 560px) {
    gap: 6px;
    & > button,
    & > div > button {
      height: 34px;
      /* Global 44px touch-target floor in App.js would clamp the height. */
      min-height: 0;
      padding: 0 10px;
      font-size: 12.5px;
    }
  }
  @media (max-width: 390px) {
    & > button,
    & > div > button {
      padding: 0 8px;
      font-size: 12px;
    }
  }
`;
const ProfileName = styled.div`
  font-family: ${C.fontDisplay};
  font-size: 34px;
  line-height: 1.05;
  letter-spacing: -0.01em;
  /* A long name has little room next to the avatar on a phone. */
  overflow-wrap: anywhere;
  @media (max-width: 768px) {
    font-size: 26px;
  }
  @media (max-width: 450px) {
    font-size: 22px;
  }
  color: var(--orx-ink);
  min-width: 0;
  overflow-wrap: anywhere;
`;
/* The marks cluster together as one unit beside the name — tight to each other,
   with a clear gap from the name itself. They sit on the optical centre of the
   first line rather than the block, so a wrapped two-line name doesn't drag them
   to the middle. Name is 34px/1.05, so a 24px mark box centres at 6px. */
const NameMarks = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  /* Optical nudge centring the 24px marks on the name beside them, so it
     tracks ProfileName's size down through the breakpoints. */
  margin-top: 6px;
  @media (max-width: 768px) {
    margin-top: 2px;
  }
  @media (max-width: 450px) {
    margin-top: 0;
  }
`;
const ProfileNameRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
`;
const ProfileRole = styled.div`
  font-size: 14.5px;
  color: var(--orx-ink-soft);
  margin-top: 6px;
  b {
    font-weight: 600;
    color: var(--orx-ink);
  }
`;
const ProfileMeta = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  font-size: 12.5px;
  color: var(--orx-ink-muted);
  align-items: center;
  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: var(--orx-ink);
    }
  }
  span.item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    max-width: 280px;
    overflow: hidden;
    white-space: nowrap;
    /* Long emails/URLs must ellipsize in the text, not crush the icon to 0px. */
    svg {
      flex-shrink: 0;
    }
    a {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;
const SectionH = styled.div`
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--orx-ink-muted);
  font-weight: 600;
  margin: 22px 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const SectionLine = styled.span`
  flex: 1;
  height: 1px;
  background: var(--orx-line);
`;
const Card = styled.div`
  background: var(--orx-surface);
  border: 1px solid var(--orx-line);
  border-radius: 10px;
`;
const Bento = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 10px;
`;

/* ── Assigned jobs ───────────────────────────────────────────────────────
   One tile per job the candidate is attached to, each carrying its own stage.
   Deliberately not wrapped in a Card: every row is already a bordered tile, so
   nesting them inside one doubled the border on all four edges and welded the
   rows into a single block. */
const AssignedList = styled.div`
  display: grid;
  gap: 8px;
`;
const AssignedRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 10px;
  position: relative;
  overflow: hidden;
  padding: 11px 10px 11px 15px;
  border: 1px solid var(--orx-line);
  border-radius: 10px;
  background: var(--orx-surface);
  transition:
    border-color 0.12s,
    background 0.12s;
  /* Stage rail, in the same palette as the status badges: the row's stage is
     legible at a glance down the left edge, before reading any control. */
  &::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 3px;
    background: ${(p) => p.$rail};
  }
  &:hover {
    border-color: var(--orx-line-strong);
    background: var(--orx-surface-tint);
  }
  &:hover .assigned-x {
    opacity: 1;
  }
`;
/* Removal is destructive and rarely wanted — present but recessed until the
   row is hovered (always visible on touch, which never hovers). */
const AssignedRemove = styled(IconBtn)`
  opacity: 0.4;
  transition:
    opacity 0.12s,
    background 0.12s,
    color 0.12s;
  &:focus-visible {
    opacity: 1;
  }
  &:hover {
    color: var(--orx-danger);
    background: var(--orx-danger-soft);
  }
  @media (hover: none) {
    opacity: 1;
  }
`;
/* Count beside the section heading. */
const SectionCount = styled.span`
  color: var(--orx-ink-faint);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;
/* Same grid, padding and control sizes as a real row, so the section occupies
   its final height while loading and the rows land in place instead of
   shunting everything below them down the pane. */
function AssignedRowSkeleton() {
  return (
    <AssignedRow $rail="var(--skeleton)" aria-hidden="true">
      <div style={{ minWidth: 0 }}>
        <Skeleton width="52%" height={16} mb={5} />
        <Skeleton width="34%" height={13} />
      </div>
      <Skeleton width={138} height={28} radius="8px" />
      <Skeleton width={28} height={28} radius="8px" />
    </AssignedRow>
  );
}
const Panel = styled.div`
  background: var(--orx-surface);
  border: 1px solid var(--orx-line);
  border-radius: 10px;
  padding: 14px 16px;
  grid-column: span ${(p) => p.$span || 3};
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  transition:
    border-color 0.12s,
    background 0.12s,
    transform 0.06s;
  &:hover {
    ${(p) =>
      p.$clickable &&
      `border-color: var(--orx-line-strong); background: var(--orx-surface-tint);`}
  }
  &:active {
    ${(p) => p.$clickable && `transform: translateY(0.5px);`}
  }
  &[disabled] {
    cursor: default;
  }
  @media (max-width: 700px) {
    grid-column: span 6;
  }
  /* Half-width leaves ~180px on a phone, which truncates emails and titles. */
  @media (max-width: 560px) {
    grid-column: span 12;
    padding: 12px 14px;
  }
`;
const PanelH = styled.div`
  font-size: 11.5px;
  color: var(--orx-ink-muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
`;
const PanelV = styled.div`
  font-size: 15px;
  color: var(--orx-ink);
  font-weight: 600;
`;
// Top-match-score Panel variant with two extras:
//   - $pulse + a key swap on each fresh single-person match → fires the
//     one-shot rowPulse box-shadow animation so the user sees what changed.
//   - min-height locked so the Panel doesn't reflow between its three
//     content states (loading "…" / "N%" + ScoreBar + link / "—" + "No
//     matches yet"). The matched state is tallest; pinning min-height to
//     that prevents the surrounding Bento grid from jumping while the
//     match-score fetch is in flight.
const PulsingPanel = styled(Panel)`
  min-height: 132px;
  /* Rendered with as="button", so the user-agent stylesheet vertically
   * centers content. Force top alignment so loading "…" / matched "N%" /
   * empty "—" all sit at the top of the locked-height box. */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  ${(p) =>
    p.$pulse &&
    css`
      animation: ${rowPulse} 1500ms ease-out;
    `}
`;
const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px;
  border-radius: 6px;
  background: ${(p) =>
    p.$variant === "accent" ? "var(--orx-accent-soft)" : "var(--orx-bg-2)"};
  color: ${(p) =>
    p.$variant === "accent" ? "var(--orx-accent-ink)" : "var(--orx-ink-soft)"};
  font-size: 11.5px;
  font-weight: 500;
`;
const Timeline = styled.div`
  padding: 6px 18px;
  @media (max-width: 450px) {
    padding: 4px 14px;
  }
`;
const TlItem = styled.div`
  display: grid;
  grid-template-columns: 24px 1fr auto;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid var(--orx-line-soft);
  align-items: flex-start;
  &:last-child {
    border-bottom: 0;
  }
  /* Phone: a full locale timestamp as the third column squeezed the subject
     to a narrow strip; it drops under the text instead (see TlWhen). */
  @media (max-width: 450px) {
    grid-template-columns: 24px 1fr;
    row-gap: 4px;
    & > :nth-child(3) {
      grid-column: 2;
    }
  }
`;
const TlIcon = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 99px;
  display: grid;
  place-items: center;
  background: ${(p) => p.$bg || "var(--orx-bg-2)"};
  color: ${(p) => p.$fg || "var(--orx-ink-soft)"};
  margin-top: 1px;
`;
const TlWhen = styled.div`
  font-size: 12px;
  color: var(--orx-ink-muted);
  @media (max-width: 450px) {
    &:empty {
      display: none;
    }
  }
`;
/* Inline text action on a timeline row (delete a logged call). */
const TlAction = styled.button`
  border: 0;
  background: none;
  padding: 0;
  margin-top: 6px;
  font: inherit;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--orx-ink-muted);
  cursor: pointer;
  &:hover {
    color: var(--orx-danger, var(--danger));
    text-decoration: underline;
  }
  /* A text link, not a 44px block (global touch-target floor). */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

function ScoreBar({ score }) {
  return (
    <div
      style={{
        marginTop: 6,
        height: 6,
        background: "var(--orx-bg-2)",
        borderRadius: 99,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${score}%`,
          height: "100%",
          background: `linear-gradient(90deg, var(--orx-accent), var(--orx-accent-2))`,
        }}
      />
    </div>
  );
}

function DetailPane({
  person,
  statusMap,
  jobFilter,
  jobIdsByCandidate,
  jobLinksByCandidate,
  onChangeStatus,
  onSendEmail,
  onBack,
  history,
  historyLoading,
  userId,
  isSaved,
  onToggleSaved,
  isUnseenMatch,
  onMarkMatchUnseen,
  onClearMatchUnseen,
  onJobLinksChanged,
  onRequestDelete,
  onFindEmail,
  emailPending,
  emailNote,
}) {
  // Job matching is parked (FEATURE_JOB_MATCH): no Job match button, no Top match score panel while off.
  const jobMatchOn = !!useAuth().features?.jobMatch;
  const [showMore, setShowMore] = useState(false);
  const [showEmailsMenu, setShowEmailsMenu] = useState(false);
  // "Copied" tick on the Contact panel's copy button, cleared after a moment or on a new person.
  const [emailCopied, setEmailCopied] = useState(false);
  useEffect(() => {
    if (!emailCopied) return undefined;
    const id = setTimeout(() => setEmailCopied(false), 1500);
    return () => clearTimeout(id);
  }, [emailCopied]);
  useEffect(() => setEmailCopied(false), [person.id]);
  const emailsBtnRef = useRef(null);
  const [matchedJobs, setMatchedJobs] = useState([]);
  const [matchedLoading, setMatchedLoading] = useState(false);
  const [jobsModalOpen, setJobsModalOpen] = useState(false);
  // Jobs this candidate is deliberately attached to ("Chosen" on the job side),
  // as opposed to the AI matches above. Assigning is only possible from here.
  const [assignedJobs, setAssignedJobs] = useState([]);
  // True from the moment a person is opened until their jobs land, so the
  // section can hold its own height instead of appearing out of nowhere.
  const [assignedLoading, setAssignedLoading] = useState(true);
  const assignedReqRef = useRef(0);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  // Manually logged calls for this person, merged into the timeline below.
  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(true);
  const callsReqRef = useRef(0);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [matchRunning, setMatchRunning] = useState(false);
  const [hasMatchOverride, setHasMatchOverride] = useState(false);
  // Open state + anchor for the "Job match" pool picker (all/yours/scoutm8).
  const [showMatchMenu, setShowMatchMenu] = useState(false);
  const matchBtnRef = useRef(null);
  // Last job pool the user matched against, so the error-strip Retry reuses it.
  const matchScopeRef = useRef("all");
  // Inline strip state below the action buttons. Phases:
  //   "running" → spinner + elapsed timer
  //   "success" → green confirmation with new top-match summary, auto-dismiss
  //   "error"   → red, with a retry button that doesn't auto-dismiss
  const [matchStrip, setMatchStrip] = useState(null); // { phase, startedAt, message?, error? }
  // Highlight key for the row that should briefly pulse after a fresh match.
  const [pulseTopAt, setPulseTopAt] = useState(0);
  // Live mirror of the currently-displayed person.id. The match flow uses
  // this to discard its UI update if the user navigated to a different
  // candidate while the request was in flight (otherwise the success
  // strip would land on the wrong profile, since this DetailPane
  // instance is reused as `person` changes).
  const personIdRef = useRef(person.id);
  useEffect(() => {
    personIdRef.current = person.id;
  }, [person.id]);
  // What the header chip reports: the filtered job's stage when a job is in
  // scope, else how far they've got anywhere. Read-only — editing is per job,
  // under Assigned jobs.
  const scopedJobId = jobFilter && jobFilter !== "all" ? jobFilter : null;
  const { status, varies: statusVaries } = scopedJobId
    ? { status: statusFor(statusMap, scopedJobId, person.id), varies: false }
    : furthestStatus(statusMap, person.id);
  const { showToast } = useToast();

  // Reset transient match UI when the user navigates to a different person.
  // Also clears matchRunning so the "Matching…" button label doesn't stick
  // on a profile whose match was never actually started. If this profile
  // has a pending "Re-ranked while you were away" marker, surface that
  // instead and clear the marker (one-shot notification).
  useEffect(() => {
    setPulseTopAt(0);
    setMatchRunning(false);
    if (isUnseenMatch) {
      setMatchStrip({ phase: "missed", startedAt: Date.now() });
      if (onClearMatchUnseen) onClearMatchUnseen(person.id);
    } else {
      setMatchStrip(null);
    }
    // Only fire when navigating to a new person — don't re-run when the
    // unseen Set ticks for another candidate.
    // eslint-disable-next-line
  }, [person.id]);

  // Auto-dismiss the missed-update strip after a generous read window.
  useEffect(() => {
    if (matchStrip?.phase !== "missed") return;
    const id = setTimeout(() => {
      setMatchStrip((s) => (s?.phase === "missed" ? null : s));
    }, 6000);
    return () => clearTimeout(id);
  }, [matchStrip?.phase]);

  // Tick to redraw the elapsed timer in the strip while a match runs. Cheap
  // — just a setState every second on the strip object.
  const [, setStripTick] = useState(0);
  useEffect(() => {
    if (matchStrip?.phase !== "running") return;
    const id = setInterval(() => setStripTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [matchStrip?.phase]);

  const canLookUpEmail = canFindEmail(person);

  useEffect(() => {
    setMatchedJobs([]);
    setJobsModalOpen(false);
    setHasMatchOverride(false);
    if (person.kind !== "candidate" || !userId) return;
    const matchId = person.raw?.match_id;
    const hasMatch =
      matchId && typeof matchId === "object" && Object.keys(matchId).length > 0;
    if (!hasMatch && !hasMatchOverride) return;
    let cancelled = false;
    setMatchedLoading(true);
    fetchWithAuth(
      `/api/candidates/${encodeURIComponent(
        person.id,
      )}/jobs?userId=${encodeURIComponent(userId)}&origin=${encodeURIComponent(
        person.origin || "internal",
      )}`,
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const jobs = Array.isArray(data) ? data : data.jobs || [];
        setMatchedJobs(jobs);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setMatchedLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // hasMatchOverride is a refetch trigger after a fresh per-person match —
    // including it in deps re-runs this effect with the new match_id.
    // eslint-disable-next-line
  }, [person.id, person.kind, person.raw, userId, hasMatchOverride]);

  // normalizePerson falls back to a synthetic "<kind>-<hash>" id when a record
  // has no _id. Those can't be assigned — there's nothing to reference.
  const canAssign = /^[a-f0-9]{24}$/i.test(String(person?.id || ""));

  // The page already holds every job↔candidate link (it backs the Job filter),
  // and each one carries the job's title and company — everything a row needs.
  // So the section can be drawn at its final size the moment you open someone,
  // and the per-candidate request only refines it. Same `_id`s, so React keeps
  // the rows in place when the authoritative list lands: no reflow, no flicker.
  const seededJobs = useMemo(() => {
    const links = jobLinksByCandidate?.[person.id] || [];
    return links.map((l) => ({
      _id: l.jobId,
      title: l.title,
      source: l.company,
      source_kind: l.source,
    }));
  }, [jobLinksByCandidate, person.id]);

  // Seeds stand in only until the request answers — after that the fetched list
  // is authoritative, including when it comes back empty (a job the link points
  // at may have been deleted, and /jobs/assigned drops those).
  const rows =
    assignedLoading && assignedJobs.length === 0 ? seededJobs : assignedJobs;

  // Placeholders only when we have nothing at all to show — no seeds and no
  // rows. A refetch after assigning or unassigning keeps the current rows on
  // screen rather than flashing the section back to skeletons.
  const assignedPending = assignedLoading && rows.length === 0;

  // Both loaders bump a request token: this pane is reused as `person` changes,
  // so a slow response for the person you just left must not land on the one
  // you're looking at now.
  const loadAssignedJobs = useCallback(() => {
    if (!person?.id) return;
    const req = ++assignedReqRef.current;
    setAssignedLoading(true);
    fetchWithAuth(
      `/api/candidates/${encodeURIComponent(person.id)}/jobs/assigned`,
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (req !== assignedReqRef.current) return;
        setAssignedJobs(Array.isArray(data) ? data : []);
        setAssignedLoading(false);
      })
      .catch(() => {
        if (req !== assignedReqRef.current) return;
        setAssignedJobs([]);
        setAssignedLoading(false);
      });
  }, [person?.id]);

  useEffect(() => {
    setAssignedJobs([]);
    setAssignedLoading(!!person?.id);
    loadAssignedJobs();
  }, [loadAssignedJobs, person?.id]);

  const loadCalls = useCallback(() => {
    if (!person?.id) return;
    const req = ++callsReqRef.current;
    setCallsLoading(true);
    getCallLogs(person.id)
      .then((rows) => {
        if (req !== callsReqRef.current) return;
        setCalls(rows);
        setCallsLoading(false);
      })
      .catch(() => {
        if (req !== callsReqRef.current) return;
        setCalls([]);
        setCallsLoading(false);
      });
  }, [person?.id]);

  useEffect(() => {
    setCalls([]);
    setCallsLoading(!!person?.id);
    loadCalls();
  }, [loadCalls, person?.id]);

  // Emails (filtered by address upstream) plus logged calls, newest first, as
  // one chronological list. Calls carry kind:"call" so the row can branch.
  const timeline = useMemo(() => {
    const emails = (history || []).map((e) => ({
      ...e,
      kind: "email",
      at: e.sentAt,
    }));
    const callRows = (calls || []).map((c) => ({ ...c, at: c.occurredAt }));
    return [...emails, ...callRows].sort(
      (a, b) => new Date(b.at || 0) - new Date(a.at || 0),
    );
  }, [history, calls]);

  // Emails and calls arrive from two requests and interleave by date, so
  // rendering as soon as the emails land makes calls stitch themselves into
  // the middle of a list the user is already reading. Wait for both.
  const timelinePending =
    historyLoading || (callsLoading && calls.length === 0);

  const assignToJob = useCallback(
    async (jobId) => {
      if (!person?.id || !jobId) return;
      try {
        const r = await fetchWithAuth(
          `/api/candidates/${encodeURIComponent(person.id)}/jobs`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              jobId,
              origin: person.origin === "external" ? "external" : "internal",
            }),
          },
        );
        if (!r.ok) throw new Error("assign failed");
        loadAssignedJobs();
        // Refresh the parent's link map so the "Job" filter reflects this now.
        onJobLinksChanged?.();
      } catch {
        // best-effort; the list just won't gain the row
      }
    },
    [person?.id, person?.origin, loadAssignedJobs, onJobLinksChanged],
  );

  const unassignFromJob = useCallback(
    async (jobId) => {
      if (!person?.id || !jobId) return;
      // Optimistic — the row disappears immediately, restored on failure.
      // Filter what's on screen: during the initial load that's still the
      // seeded list, and filtering state (empty) would leave the row visible.
      // Retiring the in-flight token stops that older response, which was
      // issued before this delete, from putting the row back.
      assignedReqRef.current += 1;
      setAssignedLoading(false);
      setAssignedJobs((prev) =>
        (prev.length ? prev : rows).filter((j) => j._id !== jobId),
      );
      try {
        const r = await fetchWithAuth(
          `/api/candidates/${encodeURIComponent(person.id)}/jobs/${encodeURIComponent(jobId)}`,
          { method: "DELETE" },
        );
        if (!r.ok) throw new Error("unassign failed");
        onJobLinksChanged?.();
      } catch {
        loadAssignedJobs();
      }
    },
    [person?.id, rows, loadAssignedJobs, onJobLinksChanged],
  );

  async function runSinglePersonMatch(scope = "all") {
    if (matchRunning) return;
    // Remember the chosen pool so the error-strip Retry reuses it.
    matchScopeRef.current = scope;
    // Pin the person this run is for. If the user navigates to a different
    // profile while the request is in flight, all post-await UI updates
    // are dropped — the success/error strip belongs to that original
    // candidate, not whoever the user is looking at now. The server-side
    // match still completes; navigating back will show the new ranking
    // via the matched-jobs effect.
    const forPersonId = person.id;
    const forPersonName = person.name;
    setMatchRunning(true);
    const startedAt = Date.now();
    setMatchStrip({ phase: "running", startedAt });
    showToast(`Matching jobs for ${forPersonName || "this candidate"}…`, {
      duration: 2500,
    });
    try {
      // Origin tells the matcher which store to read: internal → user_candidates,
      // external (sourced) → outreach_external_shortlist. Scope selects the job pool.
      const jobId = await startMatchJob(
        [
          {
            id: forPersonId,
            origin: person.origin === "external" ? "external" : "internal",
          },
        ],
        scope,
      );
      const final = await waitForMatchJob(jobId, startedAt);
      if (final.status === "failed") {
        throw new Error(final.error || "Couldn't run job match");
      }
      const matched =
        Number(final.result?.matched || 0) +
        Number(final.result?.external_matched || 0);
      if (matched === 0) {
        throw new Error("Could not match (missing text or jobs)");
      }
      if (personIdRef.current !== forPersonId) {
        // User navigated away while the match was in flight. Server side
        // is done — surface this with a "Re-ranked" pill on their row +
        // a "missed update" strip when the user opens their profile.
        if (onMarkMatchUnseen) onMarkMatchUnseen(forPersonId);
        return;
      }
      // Flip the refetch trigger so the matched-jobs fetch re-runs and the
      // top-score panel + Match-score tag reflect the new ranking.
      setHasMatchOverride((v) => !v);
      setMatchStrip({
        phase: "success",
        startedAt,
        message: null,
      });
      setPulseTopAt(Date.now());
    } catch (err) {
      if (personIdRef.current !== forPersonId) return;
      setMatchStrip({
        phase: "error",
        startedAt,
        error: err?.message || "Couldn't run job match",
      });
    } finally {
      if (personIdRef.current === forPersonId) setMatchRunning(false);
    }
  }

  // When the strip lands in success state, fold the freshly-loaded top match
  // into its message once matchedJobs refetches. Then auto-dismiss after a
  // few seconds so the strip doesn't linger forever.
  useEffect(() => {
    if (matchStrip?.phase !== "success") return;
    if (matchedLoading) return;
    const id = setTimeout(() => {
      setMatchStrip((s) => (s?.phase === "success" ? null : s));
    }, 4500);
    return () => clearTimeout(id);
  }, [matchStrip?.phase, matchedLoading]);

  const topScorePct = useMemo(() => {
    if (!matchedJobs.length) return null;
    const scores = matchedJobs
      .map((j) => Number(j.similarity_score))
      .filter((n) => !Number.isNaN(n));
    if (!scores.length) return null;
    const max = Math.max(...scores);
    return Math.round((max <= 1 ? max * 100 : max) * 10) / 10;
  }, [matchedJobs]);

  const sortedJobs = useMemo(() => {
    return [...matchedJobs].sort(
      (a, b) =>
        (Number(b.similarity_score) || 0) - (Number(a.similarity_score) || 0),
    );
  }, [matchedJobs]);

  return (
    <DetailColumn key={person.id}>
      <Detail>
        <DetailInner>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <Btn $variant="ghost" $sm onClick={onBack}>
              <FiChevronLeft size={14} /> Back to list
            </Btn>
            <div style={{ display: "flex", gap: 6 }}>
              {person.linkedin && (
                <IconBtn
                  as="a"
                  href={
                    person.linkedin.startsWith("http")
                      ? person.linkedin
                      : `https://${person.linkedin}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  title="Open LinkedIn"
                >
                  <FiExternalLink size={16} />
                </IconBtn>
              )}
              <div style={{ position: "relative" }}>
                <IconBtn title="More" onClick={() => setShowMore((v) => !v)}>
                  <FiMoreHorizontal size={16} />
                </IconBtn>
                <DropMenu
                  open={showMore}
                  onClose={() => setShowMore(false)}
                  position={{ top: "calc(100% + 4px)", right: 0 }}
                >
                  <MenuItem
                    onClick={() => {
                      navigator.clipboard?.writeText(person.email || "");
                      setShowMore(false);
                    }}
                  >
                    <FiCopy size={12} /> Copy email
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      if (person.linkedin) {
                        navigator.clipboard?.writeText(person.linkedin);
                      }
                      setShowMore(false);
                    }}
                  >
                    <FiLink size={12} /> Copy LinkedIn
                  </MenuItem>
                  {/* Passing on someone is a stage, set per job under Assigned
                      jobs — a menu item can't say which role it meant. This
                      one removes the person. */}
                  {person.kind === "candidate" && (
                    <MenuItem
                      onClick={() => {
                        setShowMore(false);
                        onRequestDelete(person);
                      }}
                      $danger
                    >
                      <FiTrash2 size={12} /> Delete candidate
                    </MenuItem>
                  )}
                </DropMenu>
              </div>
            </div>
          </div>

          <ProfileHeader>
            <Avatar person={person} size="xl" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <ProfileNameRow>
                <ProfileName>{person.name}</ProfileName>
                <NameMarks>
                  {person.linkedin && (
                    <ProfileLinkMark
                      href={
                        person.linkedin.startsWith("http")
                          ? person.linkedin
                          : `https://${person.linkedin}`
                      }
                      target="_blank"
                      rel="noreferrer"
                      title="Open LinkedIn profile"
                      aria-label="Open LinkedIn profile"
                    >
                      <BrandLinkedIn size={16} />
                    </ProfileLinkMark>
                  )}
                  {person.kind !== "client" && (
                    <OriginMarkSlot>
                      <OriginMark
                        origin={person.origin || "internal"}
                        size={16}
                      />
                    </OriginMarkSlot>
                  )}
                </NameMarks>
              </ProfileNameRow>
              <ProfileRole>
                {person.role && <b>{person.role}</b>}
                {person.role && person.company && (
                  <span
                    style={{ color: "var(--orx-ink-faint)", margin: "0 6px" }}
                  >
                    at
                  </span>
                )}
                {person.company && <span>{person.company}</span>}
              </ProfileRole>
              <ProfileMeta>
                {(person.location || person.country) && (
                  <span className="item">
                    <FiMapPin size={12} />{" "}
                    {[person.location, person.country]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                )}
              </ProfileMeta>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: 14,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {/* Clients have no pipeline status — no chip at all.
                    For candidates this is a read-out, not a control: status is
                    per job, so it's set on the Assigned jobs pickers below,
                    where you can see which role you're moving them on. */}
                {person.kind !== "client" && (
                  <StatusChip
                    readOnly
                    status={status}
                    varies={statusVaries}
                    title={
                      rows.length === 0
                        ? "Assign this candidate to a job to track their stage"
                        : scopedJobId
                          ? "Stage on the filtered job — change it under Assigned jobs"
                          : statusVaries
                            ? "Furthest stage across their jobs — set per job under Assigned jobs"
                            : "Set per job under Assigned jobs"
                    }
                  />
                )}
                {topScorePct != null && (
                  <Tag $variant="accent">
                    <Spark size={12} /> Top match {topScorePct}%
                  </Tag>
                )}
              </div>
            </div>
          </ProfileHeader>

          <ProfileActions>
            {/* Not gated on a known email — the composer lets you draft now
                and fill the address in there (it blocks the actual send with a
                "No email" reason until one is set). */}
            <Btn
              $variant="primary"
              $lg
              onClick={() => onSendEmail([person.id])}
              title={
                person.email ? undefined : "No email on file — you can still draft"
              }
            >
              <FiSend size={14} /> Send email
            </Btn>
            {/* Assigning lives on the Assigned jobs section itself, next to
                the rows it changes — not up here among the actions you take
                on the person. */}
            {person.kind === "candidate" && jobMatchOn && (
              <div style={{ position: "relative" }} ref={matchBtnRef}>
                <Btn
                  $lg
                  onClick={() => setShowMatchMenu((v) => !v)}
                  disabled={matchRunning}
                  title="Re-rank jobs for this candidate"
                >
                  <Spark size={14} />{" "}
                  {matchRunning ? "Matching…" : "Job match"}
                  {!matchRunning && <FiChevronDown size={12} />}
                </Btn>
                <DropMenu
                  open={showMatchMenu}
                  onClose={() => setShowMatchMenu(false)}
                  anchorRef={matchBtnRef}
                  minWidth={240}
                >
                  {MATCH_SCOPES.map((s) => (
                    <MenuItem
                      key={s.id}
                      onClick={() => {
                        setShowMatchMenu(false);
                        runSinglePersonMatch(s.id);
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 500 }}>{s.label}</div>
                        <div
                          style={{
                            fontSize: 11.5,
                            color: "var(--orx-ink-muted)",
                            marginTop: 1,
                          }}
                        >
                          {s.sub}
                        </div>
                      </div>
                    </MenuItem>
                  ))}
                </DropMenu>
              </div>
            )}
            <Btn
              $lg
              $variant={isSaved ? "primary" : undefined}
              onClick={() => onToggleSaved && onToggleSaved(person)}
            >
              {isSaved ? <FiBookmark size={12} /> : <FiBookmark size={14} />}
              {isSaved ? "Saved" : "Save"}
            </Btn>
          </ProfileActions>

          {matchStrip && (
            <MatchStrip data-phase={matchStrip.phase}>
              {matchStrip.phase === "missed" && (
                <>
                  <Spark size={14} />
                  <span>
                    Re-ranked while you were away
                    {topScorePct != null
                      ? `. New top match: ${
                          sortedJobs[0]?.title || "your top job"
                        }${
                          sortedJobs[0]?.company
                            ? ` at ${sortedJobs[0].company}`
                            : ""
                        } · ${topScorePct}%`
                      : "."}
                  </span>
                  <MatchStripBtn onClick={() => setMatchStrip(null)}>
                    Dismiss
                  </MatchStripBtn>
                </>
              )}
              {matchStrip.phase === "running" && (
                <>
                  <MatchStripSpinner aria-hidden />
                  <span>
                    Re-ranking jobs for {person.name || "this candidate"}…
                  </span>
                  <span
                    style={{ color: "var(--orx-ink-muted)", marginLeft: 6 }}
                  >
                    {formatElapsed(matchStrip.startedAt)}
                  </span>
                </>
              )}
              {matchStrip.phase === "success" && (
                <>
                  <FiCheckCircle size={14} aria-hidden />
                  <span>
                    Re-ranked
                    {topScorePct != null
                      ? `. New top match: ${
                          sortedJobs[0]?.title || "your top job"
                        }${
                          sortedJobs[0]?.company
                            ? ` at ${sortedJobs[0].company}`
                            : ""
                        } · ${topScorePct}%`
                      : "."}
                  </span>
                  <MatchStripBtn onClick={() => setMatchStrip(null)}>
                    Dismiss
                  </MatchStripBtn>
                </>
              )}
              {matchStrip.phase === "error" && (
                <>
                  <FiAlertTriangle size={14} aria-hidden />
                  <span>{matchStrip.error}</span>
                  <MatchStripActions>
                    <MatchStripBtn
                      onClick={() => {
                        setMatchStrip(null);
                        runSinglePersonMatch(matchScopeRef.current || "all");
                      }}
                    >
                      Retry
                    </MatchStripBtn>
                    <MatchStripBtn onClick={() => setMatchStrip(null)}>
                      Dismiss
                    </MatchStripBtn>
                  </MatchStripActions>
                </>
              )}
            </MatchStrip>
          )}

          <SectionH>
            Snapshot <SectionLine />
          </SectionH>
          <Bento>
            {person.kind !== "client" && jobMatchOn && (
              <PulsingPanel
                key={pulseTopAt || "no-pulse"}
                $pulse={!!pulseTopAt && !matchedLoading}
                $span={4}
                as="button"
                type="button"
                $clickable={sortedJobs.length > 0}
                disabled={sortedJobs.length === 0}
                onClick={() => sortedJobs.length > 0 && setJobsModalOpen(true)}
              >
                <PanelH>Top match score</PanelH>
                {matchedLoading ? (
                  <>
                    <Skeleton width={64} height={28} mb={8} />
                    <Skeleton width="100%" height={6} radius="99px" mb={8} />
                    <Skeleton width="55%" height={11.5} />
                  </>
                ) : topScorePct != null ? (
                  <>
                    <PanelV>
                      {topScorePct}
                      <span
                        style={{
                          fontSize: 13,
                          color: "var(--orx-ink-muted)",
                          fontWeight: 400,
                        }}
                      >
                        %
                      </span>
                    </PanelV>
                    <ScoreBar score={topScorePct} />
                    <div
                      style={{
                        fontSize: 11.5,
                        color: "var(--orx-accent-ink)",
                        marginTop: 6,
                        fontWeight: 500,
                      }}
                    >
                      View {sortedJobs.length} matched job
                      {sortedJobs.length === 1 ? "" : "s"} →
                    </div>
                  </>
                ) : (
                  <>
                    <PanelV
                      style={{ fontSize: 14, color: "var(--orx-ink-muted)" }}
                    >
                      —
                    </PanelV>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--orx-ink-muted)",
                        marginTop: 4,
                      }}
                    >
                      No matches yet
                    </div>
                  </>
                )}
              </PulsingPanel>
            )}
            <Panel $span={4}>
              <PanelH>Engagement</PanelH>
              <PanelV style={{ fontSize: 14 }}>
                {["longlist", "shortlist", "first_interview", "interviewing", "offer", "placement"].includes(status)
                  ? "Engaged"
                  : status === "outreach"
                    ? "Awaiting reply"
                    : "Not contacted"}
              </PanelV>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--orx-ink-muted)",
                  marginTop: 4,
                }}
              >
                {history.length} interaction{history.length === 1 ? "" : "s"}
              </div>
            </Panel>
            <Panel $span={4}>
              <PanelH>Contact</PanelH>
              {/* The address itself is the value — it used to be repeated up in
                  the profile header. Emails run longer than this third-width
                  panel, so it truncates and keeps the full string in the
                  tooltip rather than wrapping and growing the row. */}
              <PanelV
                style={{
                  fontSize: 13,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
                title={person.email || undefined}
              >
                {person.email ? (
                  <>
                    <a
                      href={`mailto:${person.email}`}
                      style={{ color: "inherit", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}
                    >
                      {person.email}
                    </a>
                    <IconBtn
                      type="button"
                      $size={24}
                      style={{ flexShrink: 0 }}
                      title={emailCopied ? "Copied" : "Copy email"}
                      aria-label="Copy email"
                      onClick={() => {
                        navigator.clipboard?.writeText(person.email || "");
                        setEmailCopied(true);
                      }}
                    >
                      {emailCopied ? <FiCheck size={12} /> : <FiCopy size={12} />}
                    </IconBtn>
                  </>
                ) : canLookUpEmail ? (
                  <FindEmailAction
                    pending={emailPending}
                    onClick={() => onFindEmail?.(person.id)}
                  />
                ) : person.emailSearched ? (
                  "No email found"
                ) : (
                  "No email"
                )}
              </PanelV>
              {emailNote && (
                <div
                  style={{
                    fontSize: 12,
                    color: emailNote.isError
                      ? "var(--orx-danger, var(--danger))"
                      : "var(--orx-ink-muted)",
                    marginTop: 4,
                  }}
                >
                  {emailNote.text}
                </div>
              )}
              {/* Alternates picker, moved down here with the address it belongs
                  to. Only scraped rows carry formula-guessed extras. */}
              {!emailNote && (person.emails?.length || 0) > 1 && (
                <>
                  <button
                    ref={emailsBtnRef}
                    type="button"
                    onClick={() => setShowEmailsMenu((v) => !v)}
                    title="Show other possible emails"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                      marginTop: 5,
                      padding: "1px 6px",
                      borderRadius: 99,
                      border: "1px solid var(--orx-line-soft)",
                      background: "var(--orx-bg-2)",
                      color: "var(--orx-ink-soft)",
                      fontSize: 11,
                      cursor: "pointer",
                      // Beats the global 44px phone touch floor (App.js).
                      minHeight: 0,
                    }}
                  >
                    +{person.emails.length - 1}
                    <FiChevronDown size={12} />
                  </button>
                  <DropMenu
                    open={showEmailsMenu}
                    onClose={() => setShowEmailsMenu(false)}
                    anchorRef={emailsBtnRef}
                  >
                    <div
                      style={{
                        padding: "4px 10px",
                        fontSize: 11,
                        color: "var(--orx-ink-muted)",
                      }}
                    >
                      Possible emails — click to copy
                    </div>
                    {person.emails.map((e) => (
                      <MenuItem
                        key={e}
                        onClick={() => {
                          navigator.clipboard?.writeText(e);
                          setShowEmailsMenu(false);
                        }}
                      >
                        <FiCopy size={12} /> {e}
                      </MenuItem>
                    ))}
                  </DropMenu>
                </>
              )}
            </Panel>
          </Bento>

          {/* Rendered for every assignable candidate, loaded or not — gating it
              on assignedJobs.length meant the whole section popped into
              existence mid-read and shoved About/Timeline down the pane. */}
          {person.kind === "candidate" && (canAssign || rows.length > 0) && (
            <>
              <SectionH>
                Assigned jobs{" "}
                {rows.length > 0 && <SectionCount>{rows.length}</SectionCount>}{" "}
                <SectionLine />
                {/* Btn inherits SectionH's uppercase + tracking otherwise. */}
                {canAssign && rows.length > 0 && (
                  <Btn
                    $sm
                    onClick={() => setAssignModalOpen(true)}
                    title="Attach this candidate to a job"
                    style={{ textTransform: "none", letterSpacing: "normal" }}
                  >
                    <FiPlus size={12} /> Assign
                  </Btn>
                )}
              </SectionH>
              {assignedPending ? (
                // One placeholder, not a guessed stack: a single row is about
                // the height of the empty card, so the common 0-or-1 job case
                // settles in place. More rows only ever grow downward, which
                // reads far better than content jumping up.
                <AssignedList>
                  <AssignedRowSkeleton />
                </AssignedList>
              ) : rows.length === 0 ? (
                <Card
                  as={FadeIn}
                  style={{
                    padding: "14px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 14,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                      Not on any job yet
                    </div>
                    <div
                      style={{
                        fontSize: 12.5,
                        color: "var(--orx-ink-muted)",
                        marginTop: 2,
                      }}
                    >
                      Assign a role to start tracking their stage — pipeline
                      status is set per job.
                    </div>
                  </div>
                  <Btn
                    $variant="accent"
                    $sm
                    onClick={() => setAssignModalOpen(true)}
                  >
                    <FiBriefcase size={12} /> Assign
                  </Btn>
                </Card>
              ) : (
              <AssignedList as={FadeIn}>
                {/* One pipeline per job. The same person can be shortlisted
                    here and still a target there, so each row owns its own
                    status rather than sharing one across every role — these
                    pickers are where a candidate's stage is set. */}
                {rows.map((j) => {
                  const jobStatus = statusFor(statusMap, j._id, person.id);
                  const col =
                    STATUS_COLORS[jobStatus] || STATUS_COLORS[DEFAULT_STATUS];
                  return (
                    <AssignedRow key={j._id} $rail={col.dot}>
                      <div style={{ minWidth: 0 }}>
                        <JobItemTitle>
                          {j.title || "Untitled role"}
                        </JobItemTitle>
                        <JobItemMeta>
                          {[
                            asDisplayText(j.source),
                            j.source_kind === "search"
                              ? "added from a search on this role"
                              : "assigned manually",
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </JobItemMeta>
                      </div>
                      <JobStatusPicker
                        status={jobStatus}
                        onChange={(next) =>
                          onChangeStatus(person.id, next, j._id)
                        }
                      />
                      <AssignedRemove
                        className="assigned-x"
                        $size={28}
                        onClick={() => unassignFromJob(j._id)}
                        title="Remove from this job"
                        aria-label={`Remove from ${j.title || "this job"}`}
                      >
                        <FiX size={14} />
                      </AssignedRemove>
                    </AssignedRow>
                  );
                })}
              </AssignedList>
              )}
            </>
          )}

          {person.summary && (
            <>
              <SectionH>
                About <SectionLine />
              </SectionH>
              <Card
                style={{
                  padding: "16px 18px",
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: "var(--orx-ink-2)",
                }}
              >
                <p style={{ margin: 0 }}>{person.summary}</p>
              </Card>
            </>
          )}

          {hasItems(person.experience) && (
            <>
              <SectionH>
                Experience <SectionLine />
              </SectionH>
              <Card>
                <ExperienceList items={asObjects(person.experience)} />
              </Card>
            </>
          )}

          {hasItems(person.education) && (
            <>
              <SectionH>
                Education <SectionLine />
              </SectionH>
              <Card>
                <EducationList items={asObjects(person.education)} />
              </Card>
            </>
          )}

          {hasItems(person.certifications) && (
            <>
              <SectionH>
                Certifications <SectionLine />
              </SectionH>
              <Card>
                <CertList items={asObjects(person.certifications)} />
              </Card>
            </>
          )}

          <SectionH>
            Outreach history <SectionLine />
            {/* Btn uses `font: inherit` and doesn't reset case/tracking, so it
                would pick up SectionH's uppercase + letter-spacing. */}
            <Btn
              $sm
              onClick={() => setCallModalOpen(true)}
              title="Add a call log"
              style={{ textTransform: "none", letterSpacing: "normal" }}
            >
              <FiPlus size={12} /> Add log
            </Btn>
          </SectionH>
          <Card
            style={{
              padding:
                timelinePending || timeline.length > 0 ? "6px 0" : "14px 18px",
            }}
          >
            {timelinePending ? (
              <Timeline>
                {Array.from({ length: 3 }).map((_, i) => (
                  <TlItem key={i}>
                    <Skeleton width={22} height={22} circle />
                    <div>
                      <Skeleton width="65%" height={13.5} mb={6} />
                      <Skeleton width="40%" height={12} />
                    </div>
                    <Skeleton width={90} height={12} />
                  </TlItem>
                ))}
              </Timeline>
            ) : timeline.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 14,
                }}
              >
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                    No outreach yet
                  </div>
                  <div
                    style={{
                      fontSize: 12.5,
                      color: "var(--orx-ink-muted)",
                      marginTop: 2,
                    }}
                  >
                    Send your first email to start the conversation.
                  </div>
                </div>
                <Btn
                  $variant="accent"
                  $sm
                  onClick={() => onSendEmail([person.id])}
                  title={
                    person.email
                      ? undefined
                      : "No email on file — you can still draft"
                  }
                >
                  <FiSend size={12} /> Send now
                </Btn>
              </div>
            ) : (
              <Timeline as={FadeIn}>
                {timeline.map((ev, i) => {
                  if (ev.kind === "call") {
                    const mins = ev.durationMinutes;
                    const meta = [
                      callOutcomeLabel(ev.outcome),
                      mins != null ? `${mins} min` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ");
                    return (
                      <TlItem key={ev._id || i}>
                        <TlIcon
                          $bg="var(--orx-accent-soft)"
                          $fg="var(--orx-accent)"
                        >
                          <FiPhone size={12} />
                        </TlIcon>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 500 }}>
                            Call · {meta}
                          </div>
                          {ev.summary && (
                            <div
                              style={{
                                fontSize: 12.5,
                                color: "var(--orx-ink-muted)",
                                marginTop: 4,
                                lineHeight: 1.5,
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {ev.summary}
                            </div>
                          )}
                          <TlAction
                            onClick={async () => {
                              try {
                                await deleteCallLog(ev._id);
                                loadCalls();
                              } catch {
                                showToast("Couldn't delete that call log.");
                              }
                            }}
                          >
                            Delete
                          </TlAction>
                        </div>
                        <TlWhen>
                          {ev.at ? new Date(ev.at).toLocaleString() : ""}
                        </TlWhen>
                      </TlItem>
                    );
                  }
                  const failed = ev.status === "failed";
                  return (
                    <TlItem key={ev._id || i}>
                      <TlIcon
                        $bg={
                          failed
                            ? "var(--orx-danger-soft)"
                            : "var(--orx-success-soft)"
                        }
                        $fg={
                          failed ? "var(--orx-danger)" : "var(--orx-success)"
                        }
                      >
                        <FiSend size={12} />
                      </TlIcon>
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 500 }}>
                          {ev.subject || "(no subject)"}
                        </div>
                        {ev.error && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--orx-danger)",
                              marginTop: 4,
                            }}
                          >
                            {ev.error}
                          </div>
                        )}
                      </div>
                      <TlWhen>
                        {ev.at ? new Date(ev.at).toLocaleString() : ""}
                      </TlWhen>
                    </TlItem>
                  );
                })}
              </Timeline>
            )}
          </Card>

          <div style={{ height: 40 }} />
        </DetailInner>
      </Detail>

      {jobsModalOpen && (
        <MatchedJobsModal
          person={person}
          jobs={sortedJobs}
          onClose={() => setJobsModalOpen(false)}
        />
      )}

      {callModalOpen && (
        <LogCallModal
          person={person}
          onClose={() => setCallModalOpen(false)}
          onSaved={() => {
            setCallModalOpen(false);
            loadCalls();
          }}
        />
      )}

      {assignModalOpen && (
        <AssignJobModal
          person={person}
          userId={userId}
          // `rows`, not the fetched list: opening the modal before the request
          // lands must still show which jobs are already assigned.
          assignedIds={new Set(rows.map((j) => j._id))}
          onAssign={assignToJob}
          onUnassign={unassignFromJob}
          onClose={() => setAssignModalOpen(false)}
        />
      )}
    </DetailColumn>
  );
}

/** Backdrop is absolute so it scopes to the DetailColumn (right pane only),
 * not the whole viewport. The DetailColumn is the nearest positioned
 * ancestor and provides the bounds. */
const ModalBackdrop = styled.div`
  position: absolute;
  inset: 0;
  background: var(--scrim);
  z-index: 5;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  display: grid;
  place-items: center;
  padding: 24px;
  animation: ${fadeIn} 0.15s ease both;
  @media (max-width: 450px) {
    padding: 12px;
  }
`;
const ModalCard = styled.div`
  background: var(--orx-surface);
  border-radius: 16px;
  box-shadow: ${C.shadowLg};
  width: 100%;
  max-width: 620px;
  max-height: calc(100vh - 80px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;
const ModalHead = styled.div`
  padding: 20px 24px 14px;
  border-bottom: 1px solid var(--orx-line-soft);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  @media (max-width: 450px) {
    padding: 16px 16px 12px;
  }
`;
const ModalH = styled.div`
  font-family: ${C.fontDisplay};
  font-size: 24px;
  line-height: 1.1;
`;
const ModalSub = styled.div`
  font-size: 13px;
  color: var(--orx-ink-muted);
  margin-top: 4px;
`;
const ModalBody = styled.div`
  padding: 14px 18px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/* Footer action row for form modals (Log a call). */
const ModalFoot = styled.div`
  padding: 14px 24px;
  border-top: 1px solid var(--orx-line-soft);
  background: var(--orx-surface-tint);
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  @media (max-width: 450px) {
    padding: 12px 16px;
  }
`;

/* Log-a-call form controls, matching SearchInput's treatment. */
const CallField = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  label {
    font-size: 12px;
    font-weight: 500;
    color: var(--orx-ink-soft);
    margin-bottom: 6px;
  }
`;
const callControl = css`
  width: 100%;
  border-radius: 8px;
  border: 1px solid var(--orx-line);
  background: var(--orx-surface-tint);
  font: inherit;
  font-size: 13px;
  color: var(--orx-ink);
  outline: 0;
  box-sizing: border-box;
  &::placeholder {
    color: var(--orx-ink-muted);
  }
  &:focus {
    background: var(--orx-surface);
    border-color: var(--orx-ink-3);
    box-shadow: 0 0 0 3px var(--ink-ring);
  }
`;
const CallInput = styled.input`
  ${callControl}
  height: 36px;
  padding: 0 10px;
`;
const CallSelect = styled.select`
  ${callControl}
  height: 36px;
  padding: 0 8px;
  cursor: pointer;
`;
const CallTextarea = styled.textarea`
  ${callControl}
  padding: 9px 10px;
  resize: vertical;
  line-height: 1.5;
`;

/* Assign-to-job panel. Unlike MatchedJobsModal this is not a floating card —
 * it fills the detail column edge to edge (stopping at the candidate list),
 * with no dimmed or blurred backdrop behind it. Closing is via the header X
 * or Escape, since there's no backdrop left to click. */
const AssignPanel = styled.div`
  position: absolute;
  inset: 0;
  z-index: 5;
  background: var(--orx-surface);
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.15s ease both;
  /* Side gutter shared by every row of the panel, inline search row included. */
  --assign-gx: 24px;
  @media (max-width: 450px) {
    --assign-gx: 16px;
  }

  /* The body scrolls inside the full-height panel rather than inside a card,
   * so it needs to flex; min-height:0 lets it shrink below its content. */
  .assign-body {
    flex: 1;
    min-height: 0;
    padding: 14px var(--assign-gx) 20px;
  }
`;

/* Filter row for the assign panel — the same source / country / function axes
 * the Jobs page filters on, as inline selects rather than its draft+apply
 * panel (this is a picker, not a browse surface). Sits on its own row under
 * the pool tabs; the three selects share the width so they line up with them. */
const AssignFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px var(--assign-gx, 24px) 0;
`;
/* Segmented control for the job pool — Imported / Tracked / Saved. Full width,
 * one row of its own, each tab an equal third. */
const AssignScopes = styled.div`
  display: flex;
  margin: 10px var(--assign-gx, 24px) 0;
  padding: 2px;
  border-radius: 9px;
  background: var(--orx-surface-tint);
  border: 1px solid var(--orx-line);
`;
const AssignScope = styled.button`
  flex: 1 1 0;
  min-width: 0;
  border: 0;
  border-radius: 7px;
  padding: 0 10px;
  height: 30px;
  font: inherit;
  font-size: 12.5px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  white-space: nowrap;
  background: ${({ $active }) => ($active ? "var(--orx-surface)" : "transparent")};
  color: ${({ $active }) =>
    $active ? "var(--orx-ink)" : "var(--orx-ink-muted)"};
  box-shadow: ${({ $active }) =>
    $active ? "var(--shadow-sm)" : "none"};
  &:hover {
    color: var(--orx-ink);
  }
  /* Keep the 30px segment (global 44px floor) and fit three labels at 360px. */
  @media (max-width: 450px) {
    min-height: 0;
    padding: 0 6px;
  }
`;
const AssignSelect = styled.select`
  flex: 1 1 0;
  min-width: 0;
  height: 34px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid var(--orx-line);
  background: var(--orx-surface-tint);
  font: inherit;
  font-size: 13px;
  color: var(--orx-ink);
  outline: 0;
  cursor: pointer;
  &:focus {
    background: var(--orx-surface);
    border-color: var(--orx-ink-3);
    box-shadow: 0 0 0 3px var(--ink-ring);
  }
  /* Three-up on a phone leaves ~95px each; smaller type keeps "All companies"
     legible instead of clipped mid-word. */
  @media (max-width: 450px) {
    min-height: 0;
    padding: 0 4px;
    font-size: 12px;
  }
`;
const AssignClear = styled.button`
  border: 0;
  background: none;
  padding: 0 4px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--orx-accent);
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  @media (max-width: 450px) {
    min-height: 0;
    align-self: stretch;
  }
`;
const JobItem = styled.a`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--orx-line);
  border-radius: 10px;
  background: var(--orx-surface);
  text-decoration: none;
  color: inherit;
  transition:
    border-color 0.12s,
    background 0.12s;
  &:hover {
    border-color: var(--orx-line-strong);
    background: var(--orx-surface-tint);
  }
`;
const JobItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--orx-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const JobItemMeta = styled.div`
  font-size: 12px;
  color: var(--orx-ink-muted);
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const JobItemScore = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 10px;
  border-radius: 99px;
  background: var(--orx-accent-soft);
  color: var(--orx-accent-ink);
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

/**
 * Job picker for attaching a candidate to a job. Searches everything the user
 * can see via GET /api/jobs (public corpus + their tracked/imported jobs) —
 * you may well be recruiting against a public posting. Already-assigned jobs
 * stay in the list, toggled, so the modal doubles as an unassign surface.
 */
/** Local datetime string for <input type="datetime-local">, which has no TZ. */
function toLocalInputValue(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** Log a call against a person. Summary is stored KMS-encrypted server-side. */
/* Confirmation for deleting a candidate. Destructive and unrecoverable, so it
   states exactly what goes and what doesn't rather than asking "are you sure" —
   the answer depends on facts the user can't see from the list. */
function DeleteCandidateModal({ person, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const isExternal = person?.origin === "external";

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape" && !busy) onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);

  const go = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    // Fixed, not absolute: the other modals sit inside the positioned detail
    // pane, but this one is rendered at page level, where Shell isn't a
    // containing block. Above the sidebar (1100) so it can't be reached round.
    <ModalBackdrop
      onClick={() => !busy && onCancel()}
      style={{ position: "fixed", zIndex: 1200 }}
    >
      <ModalCard
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}
        role="dialog"
        aria-modal="true"
      >
        <ModalHead>
          <div>
            <ModalH>Delete {person?.name || "this candidate"}?</ModalH>
            <ModalSub>This can't be undone.</ModalSub>
          </div>
        </ModalHead>
        <ModalBody>
          <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "var(--orx-ink-2)" }}>
            Their pipeline status and every job they're assigned to will be
            removed.{" "}
            {isExternal
              ? "They were sourced, so they stay in the searchable pool — this takes them off your list."
              : "They were added by you, so their record is deleted from your database."}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--orx-ink-muted)" }}>
            Emails already sent stay in your outreach history.
          </div>
        </ModalBody>
        <ModalFoot>
          <Btn onClick={onCancel} disabled={busy}>
            Cancel
          </Btn>
          <Btn $variant="danger" onClick={go} disabled={busy}>
            <FiTrash2 size={13} /> {busy ? "Deleting…" : "Delete candidate"}
          </Btn>
        </ModalFoot>
      </ModalCard>
    </ModalBackdrop>
  );
}

function LogCallModal({ person, onClose, onSaved }) {
  const { showToast } = useToast();
  const [when, setWhen] = useState(() => toLocalInputValue(new Date()));
  const [duration, setDuration] = useState("");
  const [outcome, setOutcome] = useState(CALL_OUTCOMES[0].id);
  const [summary, setSummary] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // datetime-local has no timezone — parse as local, send as ISO.
      const parsed = when ? new Date(when) : new Date();
      await createCallLog({
        personId: person.id,
        personName: person.name || "",
        summary: summary.trim(),
        occurredAt: (Number.isNaN(parsed.getTime())
          ? new Date()
          : parsed
        ).toISOString(),
        durationMinutes: duration === "" ? null : Number(duration),
        outcome,
      });
      onSaved();
    } catch {
      showToast("Couldn't save that call log. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBackdrop>
      <ModalCard
        style={{ maxWidth: 480 }}
      >
        <ModalHead>
          <div style={{ minWidth: 0 }}>
            <ModalH>Log a call</ModalH>
            <ModalSub>{person.name}</ModalSub>
          </div>
          <IconBtn onClick={onClose} title="Close">
            <FiX size={16} />
          </IconBtn>
        </ModalHead>
        <ModalBody>
          <CallField>
            <label htmlFor="call-when">When</label>
            <CallInput
              id="call-when"
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
          </CallField>
          <div style={{ display: "flex", gap: 10 }}>
            <CallField style={{ flex: 1 }}>
              <label htmlFor="call-duration">Duration (min)</label>
              <CallInput
                id="call-duration"
                type="number"
                min="0"
                max="1440"
                placeholder="—"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </CallField>
            <CallField style={{ flex: 1 }}>
              <label htmlFor="call-outcome">Outcome</label>
              <CallSelect
                id="call-outcome"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
              >
                {CALL_OUTCOMES.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </CallSelect>
            </CallField>
          </div>
          <CallField>
            <label htmlFor="call-summary">Summary</label>
            <CallTextarea
              id="call-summary"
              rows={5}
              maxLength={5000}
              placeholder="What was discussed, next steps, anything worth remembering…"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />
          </CallField>
        </ModalBody>
        <ModalFoot>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn $variant="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Log call"}
          </Btn>
        </ModalFoot>
      </ModalCard>
    </ModalBackdrop>
  );
}

// The job pools the picker can draw from, mirroring the Jobs page tabs
// (imported clientJobs / tracked-company + industry-pick jobs) plus the Saved
// (watchlist) list. `mineType` is what /api/jobs and /api/jobs/filter-options take.
const ASSIGN_SCOPES = [
  { id: "imported", label: "Imported jobs", mineType: "imported" },
  { id: "tracked", label: "Tracked jobs", mineType: "tracked" },
  { id: "saved", label: "Saved jobs" },
];
const assignMineType = (scopeId) =>
  ASSIGN_SCOPES.find((s) => s.id === scopeId)?.mineType || "";

function AssignJobModal({
  person,
  userId,
  assignedIds,
  onAssign,
  onUnassign,
  onClose,
}) {
  const [scope, setScope] = useState("tracked");
  const [q, setQ] = useState("");
  // Only the typed query is debounced. Scope and filter changes are discrete
  // clicks and fire immediately — making them wait 250ms read as a stall.
  const [debouncedQ, setDebouncedQ] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [source, setSource] = useState("");
  const [country, setCountry] = useState("");
  const [jobFunction, setJobFunction] = useState("");
  const [opts, setOpts] = useState({
    sources: [],
    countries: [],
    functions: [],
  });
  const hasFilters = !!(source || country || jobFunction || q.trim());

  // In-memory equivalent of the server-side filters, for the Saved scope only.
  const matchesFilters = (job) => {
    const needle = debouncedQ.toLowerCase();
    if (needle) {
      const hay = `${asDisplayText(job.title)} ${asDisplayText(job.source)}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    if (source && asDisplayText(job.source) !== source) return false;
    if (jobFunction && asDisplayText(job.function) !== jobFunction) {
      return false;
    }
    if (country) {
      // Jobs carry country on the doc or nested under location, and it may be
      // an array — match if any of them equals the selection.
      const vals = [
        job.country,
        job.modified_country,
        job.location?.modified_country,
        job.location?.original_country,
      ]
        .flat()
        .filter(Boolean)
        .map((v) => String(v));
      if (!vals.includes(country)) return false;
    }
    return true;
  };

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Same options the Jobs page filter bar is built from, per tab: imported or
  // tracked. Saved jobs span both, so that tab gets the union (no mineType).
  useEffect(() => {
    let cancelled = false;
    const mt = assignMineType(scope);
    fetchWithAuth(`/api/jobs/filter-options?scope=mine${mt ? `&mineType=${mt}` : ""}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setOpts({
          sources: data.sources || [],
          countries: data.countries || [],
          functions: data.functions || [],
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [scope]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        let list = [];
        if (scope === "saved") {
          // Saved jobs live as ids on user_settings.watchlist and resolve via
          // POST /api/jobs/by-ids, which takes no filters — so All/My filter
          // server-side, Saved filters in memory. Watchlists are small.
          const settings = await getUserSettings(userId);
          const ids = (settings?.watchlist || [])
            .map((w) => {
              const raw = w?.job_id ?? w;
              return raw && typeof raw === "object" && raw.$oid
                ? String(raw.$oid)
                : String(raw || "");
            })
            .filter((s) => /^[a-f0-9]{24}$/i.test(s))
            .slice(0, 100);
          if (ids.length) {
            const r = await fetchWithAuth("/api/jobs/by-ids", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ ids }),
            });
            const data = r.ok ? await r.json() : [];
            list = (Array.isArray(data) ? data : []).filter(matchesFilters);
          }
        } else {
          const qs = new URLSearchParams({ limit: "15", scope: "mine" });
          const mt = assignMineType(scope);
          if (mt) qs.set("mineType", mt);
          if (debouncedQ) qs.set("search", debouncedQ);
          if (source) qs.set("source", source);
          if (country) qs.set("country", country);
          if (jobFunction) qs.set("function", jobFunction);
          const r = await fetchWithAuth(`/api/jobs?${qs.toString()}`);
          const data = r.ok ? await r.json() : [];
          list = Array.isArray(data) ? data : data.jobs || [];
        }
        if (!cancelled) setJobs(list);
      } catch {
        if (!cancelled) setJobs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // matchesFilters is re-created each render; deps are the real inputs.
    // eslint-disable-next-line
  }, [scope, debouncedQ, source, country, jobFunction, userId]);

  const clearFilters = () => {
    setQ("");
    setDebouncedQ("");
    setSource("");
    setCountry("");
    setJobFunction("");
  };

  const toggle = async (jobId) => {
    setBusyId(jobId);
    if (assignedIds.has(jobId)) await onUnassign(jobId);
    else await onAssign(jobId);
    setBusyId(null);
  };

  return (
    <AssignPanel>
      <ModalHead>
        <div style={{ minWidth: 0 }}>
          <ModalH>Assign to job</ModalH>
          <ModalSub>{person.name}</ModalSub>
        </div>
        <IconBtn onClick={onClose} title="Close">
          <FiX size={16} />
        </IconBtn>
      </ModalHead>
      <div style={{ padding: "12px var(--assign-gx, 24px) 0" }}>
        <SearchBox>
          <SearchLead>
            <FiSearch size={14} />
          </SearchLead>
          <SearchInput
            autoFocus
            placeholder="Search jobs by title or company…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </SearchBox>
      </div>
      <AssignScopes role="tablist" aria-label="Job pool">
        {ASSIGN_SCOPES.map((s) => (
          <AssignScope
            key={s.id}
            type="button"
            role="tab"
            aria-selected={scope === s.id}
            $active={scope === s.id}
            onClick={() => setScope(s.id)}
          >
            {s.label}
          </AssignScope>
        ))}
      </AssignScopes>
      <AssignFilters>
        <AssignSelect
          value={source}
          onChange={(e) => setSource(e.target.value)}
          aria-label="Filter by company"
        >
          <option value="">All companies</option>
          {opts.sources.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </AssignSelect>
        <AssignSelect
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          aria-label="Filter by country"
        >
          <option value="">All countries</option>
          {opts.countries.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </AssignSelect>
        <AssignSelect
          value={jobFunction}
          onChange={(e) => setJobFunction(e.target.value)}
          aria-label="Filter by function"
        >
          <option value="">All functions</option>
          {opts.functions.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </AssignSelect>
        {hasFilters && (
          <AssignClear onClick={clearFilters}>Clear</AssignClear>
        )}
      </AssignFilters>
      <ModalBody className="assign-body">
          {loading && jobs.length === 0 ? (
            <div style={{ padding: 20 }}>
              <Skeleton width="100%" height={14} mb={10} />
              <Skeleton width="80%" height={14} mb={10} />
              <Skeleton width="60%" height={14} />
            </div>
          ) : jobs.length === 0 ? (
            <div
              style={{
                padding: 20,
                textAlign: "center",
                color: "var(--orx-ink-muted)",
                fontSize: 13.5,
              }}
            >
              {hasFilters
                ? "No jobs match these filters."
                : "No jobs to show."}
            </div>
          ) : (
            jobs.map((job, i) => {
              const jobId = job._id?.$oid || job._id || job.id || "";
              const isOn = assignedIds.has(jobId);
              const title = asDisplayText(job.title) || "Untitled role";
              const meta = [
                asDisplayText(job.source),
                asDisplayText(
                  job.location?.modified_country ||
                    job.modified_country ||
                    job.location?.original_country ||
                    job.country,
                ),
              ]
                .filter(Boolean)
                .join(" · ");
              return (
                <JobItem
                  as="button"
                  type="button"
                  key={jobId || i}
                  onClick={() => jobId && toggle(jobId)}
                  disabled={busyId === jobId}
                  style={{ width: "100%", textAlign: "left" }}
                >
                  <div style={{ minWidth: 0 }}>
                    <JobItemTitle>{title}</JobItemTitle>
                    {meta && <JobItemMeta>{meta}</JobItemMeta>}
                  </div>
                  <JobItemScore
                    style={{ color: isOn ? "var(--orx-success)" : "var(--orx-ink-muted)" }}
                  >
                    {busyId === jobId ? "…" : isOn ? "Assigned" : "Assign"}
                  </JobItemScore>
                </JobItem>
              );
            })
          )}
      </ModalBody>
    </AssignPanel>
  );
}

function MatchedJobsModal({ person, jobs, onClose }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // The modal is rendered inside the DetailColumn wrapper and scoped to it
  // via position:absolute — no portal — so it only covers the right pane.
  return (
    <ModalBackdrop>
      <ModalCard>
        <ModalHead>
          <div style={{ minWidth: 0 }}>
            <ModalH>Matched jobs</ModalH>
            <ModalSub>
              {person.name} · {jobs.length} match
              {jobs.length === 1 ? "" : "es"}
            </ModalSub>
          </div>
          <IconBtn onClick={onClose} title="Close">
            <FiX size={16} />
          </IconBtn>
        </ModalHead>
        <ModalBody>
          {jobs.length === 0 ? (
            <div
              style={{
                padding: 20,
                textAlign: "center",
                color: "var(--orx-ink-muted)",
                fontSize: 13.5,
              }}
            >
              No matched jobs to show.
            </div>
          ) : (
            jobs.map((job, i) => {
              const url = job.url || job.link || job.original_url || "";
              const title = asDisplayText(job.title) || "Matched job";
              const src = asDisplayText(job.source);
              // Jobs store location as a sub-object: prefer the modified
              // (cleaned) country, fall back to the original or any
              // top-level country field.
              const country = asDisplayText(
                job.location?.modified_country ||
                  job.modified_country ||
                  job.location?.original_country ||
                  job.country,
              );
              const meta = [src, country].filter(Boolean).join(" · ");
              const s = Number(job.similarity_score);
              const pct = Number.isNaN(s)
                ? null
                : Math.round((s <= 1 ? s * 100 : s) * 10) / 10;
              return (
                <JobItem
                  key={job._id?.$oid || job._id || job.id || i}
                  href={url || undefined}
                  target={url ? "_blank" : undefined}
                  rel="noreferrer"
                  onClick={(e) => {
                    if (!url) e.preventDefault();
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <JobItemTitle>{title}</JobItemTitle>
                    {meta && <JobItemMeta>{meta}</JobItemMeta>}
                  </div>
                  {pct != null && <JobItemScore>{pct}%</JobItemScore>}
                </JobItem>
              );
            })
          )}
        </ModalBody>
      </ModalCard>
    </ModalBackdrop>
  );
}

/* ── Background normalisers ── */
function hasItems(val) {
  return Array.isArray(val) && val.length > 0;
}
function asObjects(val) {
  return Array.isArray(val)
    ? val.filter((x) => x && typeof x === "object")
    : [];
}
/** Defensive: experience/education items occasionally arrive with object
 * values (e.g. location: {city, country, region}) when the LLM ignored the
 * "return string" rule. Render them as readable text instead of "[object
 * Object]". */
function asDisplayText(val) {
  if (val == null || val === "") return "";
  if (typeof val === "string") return val.trim();
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (Array.isArray(val)) {
    return val.map(asDisplayText).filter(Boolean).join(", ");
  }
  if (typeof val === "object") {
    // Prefer common single-value keys; fall back to all values joined.
    const preferred = [
      val.name,
      val.label,
      val.value,
      val.text,
      val.title,
      val.city && val.country
        ? `${val.city}, ${val.country}`
        : val.city || val.country || val.location || val.region,
    ].find((v) => typeof v === "string" && v.trim());
    if (preferred) return preferred.trim();
    return Object.values(val)
      .filter((v) => typeof v === "string" || typeof v === "number")
      .map(String)
      .filter(Boolean)
      .join(", ");
  }
  return "";
}
/** Build a compact resume-style snippet for the AI prompt. Includes summary,
 * the most recent few roles, top education entry, and skills if present.
 * Each section is dropped silently if missing so the prompt stays tight. */
function buildCandidateContext(person) {
  if (!person) return "";
  const lines = [];
  const summary = asDisplayText(person.summary);
  if (summary) lines.push(`Summary: ${summary}`);

  const exp = Array.isArray(person.experience) ? person.experience : [];
  if (exp.length) {
    const recent = exp.slice(0, 3).map((it) => {
      const t = asDisplayText(it.title);
      const c = asDisplayText(it.company);
      const period = formatExpPeriod(it);
      const head = [t, c].filter(Boolean).join(" at ");
      const tail = period ? ` (${period})` : "";
      const desc = asDisplayText(it.description);
      return `- ${head}${tail}${desc ? ` — ${desc}` : ""}`;
    });
    lines.push(`Experience:\n${recent.join("\n")}`);
  }

  const edu = Array.isArray(person.education) ? person.education : [];
  if (edu.length) {
    const top = edu.slice(0, 2).map((it) => {
      const d = asDisplayText(it.degree);
      const s = asDisplayText(it.school);
      const period = [asDisplayText(it.startDate), asDisplayText(it.endDate)]
        .filter(Boolean)
        .join("–");
      const head = [d, s].filter(Boolean).join(" at ");
      return `- ${head}${period ? ` (${period})` : ""}`;
    });
    lines.push(`Education:\n${top.join("\n")}`);
  }

  // Skills can live on the raw payload (string) — pass through if present.
  const skills = asDisplayText(person.raw?.skills);
  if (skills) lines.push(`Skills: ${skills}`);

  return lines.join("\n\n");
}

function formatExpPeriod(item) {
  const start = asDisplayText(item.startDate);
  const end = asDisplayText(item.endDate) || (item.current ? "Present" : "");
  if (start && end) return `${start} – ${end}`;
  return start || end || "";
}

const TimelineRow = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr auto;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--orx-line-soft);
  align-items: flex-start;
  &:last-child {
    border-bottom: 0;
  }
  /* Phone: the nowrap date range as a third column left titles ~140px wide;
     it drops under the text instead. */
  @media (max-width: 450px) {
    grid-template-columns: 32px 1fr;
    gap: 6px 12px;
    padding: 12px 14px;
    & > :nth-child(3) {
      grid-column: 2;
    }
  }
`;
const TimelineIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--orx-bg-2);
  display: grid;
  place-items: center;
  color: var(--orx-ink-soft);
  margin-top: 2px;
`;
const ItemTitle = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: var(--orx-ink);
  line-height: 1.3;
`;
const ItemSub = styled.div`
  font-size: 12.5px;
  color: var(--orx-ink-soft);
  margin-top: 2px;
`;
const ItemDesc = styled.div`
  font-size: 13px;
  color: var(--orx-ink-2);
  margin-top: 6px;
  line-height: 1.55;
  white-space: pre-wrap;
`;
const ItemPeriod = styled.div`
  font-size: 12px;
  color: var(--orx-ink-muted);
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  padding-top: 2px;
  @media (max-width: 450px) {
    padding-top: 0;
    &:empty {
      display: none;
    }
  }
`;

function ExperienceList({ items }) {
  return (
    <>
      {items.map((it, i) => {
        const period = formatExpPeriod(it);
        const title = asDisplayText(it.title);
        const company = asDisplayText(it.company);
        const location = asDisplayText(it.location);
        const description = asDisplayText(it.description);
        const durationText = asDisplayText(it.durationText);
        const subParts = [company, location].filter(Boolean).join(" · ");
        return (
          <TimelineRow key={i}>
            <TimelineIcon>
              <Building size={12} />
            </TimelineIcon>
            <div style={{ minWidth: 0 }}>
              {title && <ItemTitle>{title}</ItemTitle>}
              {subParts && <ItemSub>{subParts}</ItemSub>}
              {description && <ItemDesc>{description}</ItemDesc>}
              {durationText && !period && (
                <ItemSub style={{ color: "var(--orx-ink-muted)" }}>
                  {durationText}
                </ItemSub>
              )}
            </div>
            <ItemPeriod>
              {period}
              {it.current && <Dot $bg={"var(--orx-success)"} />}
            </ItemPeriod>
          </TimelineRow>
        );
      })}
    </>
  );
}

function EducationList({ items }) {
  return (
    <>
      {items.map((it, i) => {
        const degree = asDisplayText(it.degree);
        const school = asDisplayText(it.school);
        const details = asDisplayText(it.details);
        const startDate = asDisplayText(it.startDate);
        const endDate = asDisplayText(it.endDate);
        const period = [startDate, endDate].filter(Boolean).join(" – ");
        return (
          <TimelineRow key={i}>
            <TimelineIcon>
              <Building size={12} />
            </TimelineIcon>
            <div style={{ minWidth: 0 }}>
              {degree && <ItemTitle>{degree}</ItemTitle>}
              {school && <ItemSub>{school}</ItemSub>}
              {details && <ItemDesc>{details}</ItemDesc>}
            </div>
            <ItemPeriod>{period}</ItemPeriod>
          </TimelineRow>
        );
      })}
    </>
  );
}

function CertList({ items }) {
  return (
    <>
      {items.map((it, i) => {
        const name = asDisplayText(it.name);
        const issuer = asDisplayText(it.issuer);
        const id = asDisplayText(it.id);
        const issued = asDisplayText(it.issued);
        const expires = asDisplayText(it.expires);
        const period =
          issued || expires
            ? `${issued || "—"}${expires ? ` → ${expires}` : ""}`
            : "";
        return (
          <TimelineRow key={i}>
            <TimelineIcon>
              <FiTag size={12} />
            </TimelineIcon>
            <div style={{ minWidth: 0 }}>
              {name && <ItemTitle>{name}</ItemTitle>}
              {(issuer || id) && (
                <ItemSub>
                  {[issuer, id && `ID ${id}`].filter(Boolean).join(" · ")}
                </ItemSub>
              )}
            </div>
            <ItemPeriod>{period}</ItemPeriod>
          </TimelineRow>
        );
      })}
    </>
  );
}

/* ───────────────── Composer ───────────────── */
const ComposerWrap = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  background: var(--orx-bg);
  /* Fill the parent right panel vertically so the recipients column
   * stretches to the bottom of the page (grid rows default to
   * align-items: stretch). */
  flex: 1 1 auto;
  min-height: 0;
  animation: ${fadeIn} 0.18s ease both;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    /* Recipients take what they need; the editor gets the rest. */
    grid-template-rows: auto minmax(0, 1fr);
  }
`;
const RecipientsCol = styled.div`
  border-right: 1px solid var(--orx-line);
  background: var(--orx-surface);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  /* Stacked above the editor on phones — divider moves to the bottom edge and
     the list is capped so it can't push the editor off-screen. */
  @media (max-width: 900px) {
    border-right: 0;
    border-bottom: 1px solid var(--orx-line);
    max-height: 34vh;
  }
`;
const RecipientItem = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--orx-line-soft);
  cursor: pointer;
  position: relative;
  background: ${(p) => (p.$active ? "var(--orx-bg-2)" : "transparent")};
  &:hover {
    background: ${(p) =>
      p.$active ? "var(--orx-bg-2)" : "var(--orx-surface-tint)"};
  }
  ${(p) =>
    p.$active &&
    `
    &::before { content:""; position:absolute; left:0; top:0; bottom:0; width:3px; background:var(--orx-ink); }
  `}
`;
const RName = styled.div`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const RRole = styled.div`
  font-size: 11.5px;
  color: var(--orx-ink-muted);
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const EditCol = styled.div`
  background: var(--orx-bg);
  min-height: 0;
  /* Flex column so ComposeCard can fill the column and run its own
   * internal scroll on the body — the footer (icons + Send) stays
   * pinned because it sits below the scrollable region. */
  display: flex;
  flex-direction: column;
  /* Side gutter of the Cancel / Apply to all row above the card. */
  --compose-gx: 24px;
  @media (max-width: 450px) {
    --compose-gx: 12px;
  }
`;
const ComposeCard = styled.div`
  background: var(--orx-surface);
  border: 1px solid var(--orx-line);
  border-radius: 14px;
  margin: 16px 20px;
  overflow: hidden;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  @media (max-width: 450px) {
    margin: 10px 12px 12px;
  }
`;
const ComposeField = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid var(--orx-line-soft);
  gap: 12px;
  label {
    font-size: 12px;
    color: var(--orx-ink-muted);
    font-weight: 500;
  }
  input {
    border: 0;
    outline: 0;
    font: inherit;
    font-size: 14px;
    background: transparent;
    color: var(--orx-ink);
    width: 100%;
  }
  /* Phone: an 80px label column plus wide gutters left a long From address
     overflowing; narrower label, and the value may break mid-address. */
  @media (max-width: 450px) {
    grid-template-columns: 52px 1fr;
    gap: 8px;
    padding: 8px 12px;
    /* Borderless subject line; the global 44px floor only adds dead height. */
    input {
      min-height: 0;
    }
    & > div {
      min-width: 0;
      overflow-wrap: anywhere;
    }
  }
`;
const ComposeBody = styled.div`
  padding: 16px;
  /* The only scrollable region in the compose card. Header (recipient,
   * From/To/Subject) and footer (toolbar + send) stay pinned outside it. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  @media (max-width: 450px) {
    padding: 12px;
  }
`;
/** Rich-text editor — a contentEditable div so bold/italic/links render
 * inline instead of showing raw HTML tags. Kept uncontrolled (state syncs
 * one-way via onInput) so the caret doesn't jump on every keystroke. */
const Editor = styled.div`
  width: 100%;
  min-height: 280px;
  outline: 0;
  font: inherit;
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--orx-ink);
  background: transparent;
  box-sizing: border-box;
  white-space: pre-wrap;
  word-wrap: break-word;
  &:empty:before {
    content: attr(data-placeholder);
    color: var(--orx-ink-muted);
    pointer-events: none;
  }
  a {
    color: var(--orx-accent);
    text-decoration: underline;
  }
  b,
  strong {
    font-weight: 700;
  }
  i,
  em {
    font-style: italic;
  }
`;
const ComposeFoot = styled.div`
  padding: 10px 16px;
  border-top: 1px solid var(--orx-line-soft);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: var(--orx-surface-tint);
  /* Stay pinned at the bottom of ComposeCard — never grow, never shrink. */
  flex: 0 0 auto;
  /* Phone: seven tool icons plus Send / Send all are ~490px, and the card
     clipped the send buttons off its right edge. Sends wrap to a row of
     their own, right-aligned. The toolbar's menus anchor to the footer rather
     than their icon (static wrappers), so a menu opened from the right-hand
     icons isn't clipped by the card either. */
  @media (max-width: 450px) {
    flex-wrap: wrap;
    padding: 8px 12px;
    row-gap: 8px;
    position: relative;
    & > div:last-child {
      flex: 1 1 100%;
      justify-content: flex-end;
    }
    & > div:first-child > div {
      position: static !important;
    }
  }
`;
const AttachmentStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 0 4px;
  border-top: 1px dashed var(--orx-line-soft);
  margin-top: 12px;
`;
const AttachmentChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 4px 0 8px;
  border-radius: 6px;
  background: var(--orx-bg-2);
  border: 1px solid var(--orx-line);
  font-size: 11.5px;
  color: var(--orx-ink);
  max-width: 360px;
  flex-shrink: 0;
  .att-name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .att-size {
    flex-shrink: 0;
    color: var(--orx-ink-muted);
    font-variant-numeric: tabular-nums;
  }
  button {
    flex-shrink: 0;
    background: transparent;
    border: 0;
    cursor: pointer;
    color: var(--orx-ink-muted);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    /* Global 44px touch-target floor would overflow the 26px chip. */
    min-height: 0;
    border-radius: 4px;
  }
  button:hover {
    background: var(--orx-line);
    color: var(--orx-ink);
  }
`;
function formatBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Render elapsed time since `startedAt` (ms epoch) as M:SS — used by the
// single-match strip and the bulk-match floating banner.
function formatElapsed(startedAt) {
  if (!startedAt) return "0:00";
  return formatDurationMs(Date.now() - startedAt);
}

function formatDurationMs(ms) {
  const secs = Math.max(0, Math.floor((ms || 0) / 1000));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const AiPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: linear-gradient(
    135deg,
    var(--orx-accent-soft),
    var(--orx-surface)
  );
  color: var(--orx-accent-ink);
  border: 1px solid var(--orx-accent-soft);
  height: 22px;
  padding: 0 8px;
  border-radius: 99px;
  font-size: 11.5px;
  font-weight: 500;
`;

function Composer({
  recipients,
  onClose,
  onSendOne,
  onRemoveRecipient,
  onChangeRecipientEmail,
  fromAddress,
  fromName,
  initialAiForId,
  initialDrafts,
  initialDraftIds,
  onAutosave,
  templates,
  onSaveTemplate,
  onDeleteTemplate,
  signatureHtml,
  aiPrompts = [],
  jobLinksByCandidate = {},
  onFindEmail,
  emailPendingIds,
  emailNotes,
}) {
  const { showToast } = useToast();
  // Role pitched per candidate: jobId, or "" for no specific role. Unset =
  // default to the most recent assignment (links arrive newest first).
  const [pitchJobIds, setPitchJobIds] = useState({});
  const [nameClient, setNameClient] = useState(false);
  const [showJobMenu, setShowJobMenu] = useState(false);
  const jobBtnRef = useRef(null);
  const jobLinksFor = useCallback(
    (person) => (person ? jobLinksByCandidate[person.id] || [] : []),
    [jobLinksByCandidate],
  );
  const pitchJobFor = useCallback(
    (person) => {
      const links = jobLinksFor(person);
      if (!links.length) return null;
      const chosen = pitchJobIds[person.id];
      if (chosen === "") return null;
      return links.find((l) => l.jobId === chosen) || links[0];
    },
    [jobLinksFor, pitchJobIds],
  );
  // The signature is only ever inserted by the AI personalize flow at the
  // bottom of the generated body. Nothing else touches it — no auto-insert
  // on open, no backfill effect, no race-condition handling. If the user
  // hasn't run AI on a recipient, the body stays as the user typed it.
  const signatureBlock = signatureHtml || "";
  const [drafts, setDrafts] = useState(() => {
    const m = {};
    recipients.forEach((r) => {
      const seed = (initialDrafts && initialDrafts[r.id]) || {};
      m[r.id] = {
        subject: seed.subject || "",
        body: seed.body || "",
        isHtml: !!seed.isHtml,
        personalized: !!seed.personalized,
        sent: false,
      };
    });
    return m;
  });
  /** Stable draft id per recipient. Resumed drafts carry the existing id via
   * `initialDraftIds`; new sessions generate one so the same person can
   * accumulate multiple drafts across composer sessions. */
  const [draftIds, setDraftIds] = useState(() => {
    const m = {};
    recipients.forEach((r) => {
      m[r.id] =
        (initialDraftIds && initialDraftIds[r.id]) ||
        `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${r.id.slice(0, 6)}`;
    });
    return m;
  });
  const [activeId, setActiveId] = useState(recipients[0]?.id || null);
  const [aiBusy, setAiBusy] = useState(false);
  // While `aiBusy` is true, this is the recipient whose draft is being
  // generated — used by the header strip + shimmer overlay so they can
  // say "Personalizing for {name}…" instead of being anonymous. Also
  // tracks bulk-personalize progress as { id, name, index, total }.
  const [aiActive, setAiActive] = useState(null);
  // Pinned start time for the elapsed timer in the AI header strip.
  const [aiStartedAt, setAiStartedAt] = useState(0);
  // AbortController so the Cancel button can stop the client from waiting
  // on the in-flight personalize request. Server may still bill.
  const aiAbortRef = useRef(null);
  /* Saved AI prompt applied to every personalize run in this session.
   * `null` = none. Seeded from the user's default prompt once settings land
   * (they arrive after mount), but an explicit pick in the composer always
   * wins from then on. */
  const [promptId, setPromptId] = useState(null);
  const promptTouched = useRef(false);
  const [showPromptMenu, setShowPromptMenu] = useState(false);
  const promptBtnRef = useRef(null);
  useEffect(() => {
    if (promptTouched.current) return;
    const def = aiPrompts.find((p) => p.isDefault);
    setPromptId(def ? def.id : null);
  }, [aiPrompts]);
  const selectedPrompt = aiPrompts.find((p) => p.id === promptId) || null;
  const choosePrompt = useCallback((id) => {
    promptTouched.current = true;
    setPromptId(id);
    setShowPromptMenu(false);
  }, []);
  const [bulkBusy, setBulkBusy] = useState(false);
  /* Live progress through a bulk send. Null when idle; while running it
   * holds {currentId, currentIndex, total} so the recipient list can
   * highlight whoever's mid-flight and the Send button can show "Sending
   * 3 of 12…" instead of a static "Sending…" label. */
  const [sendProgress, setSendProgress] = useState(null);

  // Tick once per second to redraw the elapsed timer in the AI header.
  const [, setAiHeaderTick] = useState(0);
  useEffect(() => {
    if (!aiBusy) return;
    const id = setInterval(() => setAiHeaderTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [aiBusy]);

  /* ─── Continuous draft autosave (Gmail/Outlook pattern) ───
   * The composer's `drafts` state is the source of truth while composing.
   * Every change debounces a call to onAutosave (which is the parent's
   * persistDrafts), so the Drafts tab + encrypted outreach-state stay
   * live in sync. No "did my draft save?" question on Cancel anymore. */
  // Skip the very first effect fire: initial drafts already match what's
  // on the server (either seeded from a resume, or all empty for a fresh
  // session — empty drafts are dropped by persistDrafts anyway).
  const autosaveSkipFirst = useRef(true);
  // Latest values for the unmount-flush so it doesn't capture stale
  // closures when the page navigates away mid-compose.
  const latestRecipientsRef = useRef(recipients);
  const latestDraftIdsRef = useRef(draftIds);
  const latestDraftsRef = useRef(drafts);
  // `onAutosave` is a plain (non-memoized) prop from the parent, so its
  // reference changes on EVERY parent re-render. Reading it through a ref
  // keeps the debounce effect from re-running every render — which would
  // otherwise create a poll loop: timer fires → persistDrafts runs setState
  // in the parent → parent re-renders → new onAutosave → effect re-runs →
  // schedules another 2s timer → ad infinitum, PATCHing the same data.
  const onAutosaveRef = useRef(onAutosave);
  useEffect(() => {
    latestRecipientsRef.current = recipients;
  }, [recipients]);
  useEffect(() => {
    latestDraftIdsRef.current = draftIds;
  }, [draftIds]);
  useEffect(() => {
    latestDraftsRef.current = drafts;
  }, [drafts]);
  useEffect(() => {
    onAutosaveRef.current = onAutosave;
  }, [onAutosave]);

  useEffect(() => {
    if (autosaveSkipFirst.current) {
      autosaveSkipFirst.current = false;
      return;
    }
    if (!recipients.length) return;
    const id = setTimeout(() => {
      onAutosaveRef.current?.(recipients, draftIds, drafts);
    }, 2000);
    return () => clearTimeout(id);
    // onAutosave intentionally omitted — see ref pattern above.
    // eslint-disable-next-line
  }, [drafts, recipients, draftIds]);

  // Flush any pending edits on unmount so closing the composer / navigating
  // away within the debounce window doesn't lose the last few keystrokes.
  useEffect(() => {
    return () => {
      if (autosaveSkipFirst.current) return; // never edited → nothing to flush
      try {
        onAutosaveRef.current?.(
          latestRecipientsRef.current,
          latestDraftIdsRef.current,
          latestDraftsRef.current,
        );
      } catch {
        /* fire-and-forget */
      }
    };
    // eslint-disable-next-line
  }, []);

  // While a bulk send is in flight, prompt before unload. Without this the
  // user can navigate or close the tab partway through the loop, leaving
  // some recipients emailed + marked sent and others stranded.
  useEffect(() => {
    if (!bulkBusy) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [bulkBusy]);
  // Per-draft attachments (kept outside `drafts` so we never accidentally
  // persist base64 blobs to the encrypted outreach-state doc).
  const [attachments, setAttachments] = useState({}); // { [personId]: [{id,name,size,type,base64}] }
  // templates is owned by the parent page. It survives close/reopen of the
  // composer and is hydrated once on page load, so saving a template here
  // can never race with a fresh fetch the way the previous per-composer
  // useState did (which used to wipe templates on first open).
  const [showTemplates, setShowTemplates] = useState(false);
  const [showInsertMenu, setShowInsertMenu] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showToMenu, setShowToMenu] = useState(false);
  const toBtnRef = useRef(null);

  /** Insert a mail-merge token at the cursor inside the contentEditable
   * body. The token stays literal in the draft and the editor; it only
   * gets substituted at send time by resolveTokens(). */
  function insertToken(key) {
    if (!bodyRef.current || !active) return;
    bodyRef.current.focus();
    const text = `{{${key}}}`;
    // execCommand is deprecated but it's how the existing bold/italic/link
    // toolbar works in this composer, so we match the established pattern.
    try {
      document.execCommand("insertText", false, text);
    } catch {
      // Final fallback: append at end. Rare on modern browsers.
      bodyRef.current.innerHTML += text;
    }
    captureEditorBody();
    setShowInsertMenu(false);
  }

  /** Append the user's signature at the bottom of the active draft. Same
   * append-only semantics as the AI flow — no detection, no de-dup. If the
   * user clicks twice they get two signatures (and can undo manually). */
  function insertSignature() {
    if (!active) return;
    if (!signatureBlock) {
      showToast("No signature configured yet");
      return;
    }
    const current = activeDraft?.body || "";
    const safe = sanitizeComposerHtml(
      joinBodyAndSignature(normalizeBodyToHtml(current), signatureBlock),
    );
    updateDraft(active.id, { body: safe, isHtml: true });
    if (bodyRef.current) bodyRef.current.innerHTML = safe;
  }
  const bodyRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setDrafts((d) => {
      const next = { ...d };
      recipients.forEach((r) => {
        if (!next[r.id])
          next[r.id] = {
            subject: "",
            body: "",
            isHtml: false,
            personalized: false,
            sent: false,
          };
      });
      Object.keys(next).forEach((k) => {
        if (!recipients.find((r) => r.id === k)) delete next[k];
      });
      return next;
    });
    setDraftIds((m) => {
      const next = { ...m };
      recipients.forEach((r) => {
        if (!next[r.id]) {
          next[r.id] =
            `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${r.id.slice(0, 6)}`;
        }
      });
      Object.keys(next).forEach((k) => {
        if (!recipients.find((r) => r.id === k)) delete next[k];
      });
      return next;
    });
    if (!recipients.find((r) => r.id === activeId)) {
      setActiveId(recipients[0]?.id || null);
    }
  }, [recipients, activeId]);

  const active = recipients.find((r) => r.id === activeId);
  const activeDraft = active ? drafts[active.id] : null;

  // Hydrate the contentEditable only when switching to a different
  // recipient. After that the editor is the source of truth — typing,
  // bold, italic, link all mutate the DOM directly via execCommand and
  // we just *read* the result into state. Re-syncing state → DOM during
  // editing would normalise the markup mid-keystroke and clobber things
  // like the just-applied bold.
  const initializedFor = useRef(null);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || !active) return;
    if (initializedFor.current !== active.id) {
      el.innerHTML = sanitizeComposerHtml(
        normalizeBodyToHtml(activeDraft?.body || ""),
      );
      initializedFor.current = active.id;
    }
    // eslint-disable-next-line
  }, [active?.id]);

  /** Programmatic body write — used by template apply / AI personalize,
   * which need to update both the state AND the editor DOM since the
   * editor is uncontrolled. */
  function setEditorBody(html, extra = {}) {
    if (!active) return;
    // Templates/AI may arrive as plain text with newlines — promote to
    // real HTML paragraphs so the saved draft renders identically in the
    // composer and at the recipient's mail client.
    const safe = sanitizeComposerHtml(normalizeBodyToHtml(html));
    updateDraft(active.id, { body: safe, isHtml: true, ...extra });
    if (bodyRef.current) bodyRef.current.innerHTML = safe;
  }

  const personalizedCount = Object.values(drafts).filter(
    (d) => d.personalized,
  ).length;
  /** Lightweight "ready" check used for the button count and disable
   * state — only looks at email + content. Token resolution is checked
   * once at actual send time (and surfaced via the Preview action), so
   * we don't repeat that work on every keystroke. */
  function isReadyToSend(r) {
    if (!r?.email) return false;
    const d = drafts[r.id];
    if (!d?.subject?.trim() || !d?.body?.trim()) return false;
    if (placeholdersIn(d.subject).length || placeholdersIn(d.body).length)
      return false;
    return true;
  }
  const readyCount = recipients.filter(isReadyToSend).length;

  function updateDraft(id, patch) {
    setDrafts((d) => {
      // If the user edits subject/body after a Send all run, clear the
      // skipped flag so the row goes back to "Draft" status and the next
      // Send all re-evaluates this recipient.
      const clearsSkipped =
        patch.subject !== undefined || patch.body !== undefined;
      return {
        ...d,
        [id]: {
          ...d[id],
          ...patch,
          ...(clearsSkipped ? { skipped: false, skipReason: "" } : {}),
        },
      };
    });
  }

  /** Sync state from the editor without rewriting innerHTML (keeps caret).
   * Doesn't touch `personalized` — only outright body REPLACEMENTS (template
   * apply, AI fresh draft) should toggle that flag. Otherwise typing /
   * formatting would flip personalized back to false and re-trigger the
   * auto-AI useEffect, which would overwrite your edits. */
  function captureEditorBody() {
    if (!active || !bodyRef.current) return;
    updateDraft(active.id, {
      body: bodyRef.current.innerHTML,
      isHtml: true,
    });
  }

  /** Run an execCommand on the active editor + sync state. execCommand is
   * deprecated but still the simplest cross-browser way to do basic
   * bold/italic/link in a contentEditable. */
  function execFormat(cmd, value) {
    if (!bodyRef.current) return;
    bodyRef.current.focus();
    document.execCommand(cmd, false, value);
    captureEditorBody();
  }

  /* ── Attachments ── */
  const activeAttachments = (active && attachments[active.id]) || [];
  const ATTACH_TOTAL_BYTES = 20 * 1024 * 1024;
  const ATTACH_MAX_COUNT = 10;

  function bytesOf(list) {
    return list.reduce((sum, a) => sum + (a.size || 0), 0);
  }

  function readAsBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error("read error"));
      reader.onload = () => {
        // strip "data:<type>;base64,"
        const result = String(reader.result || "");
        const comma = result.indexOf(",");
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.readAsDataURL(file);
    });
  }

  async function handleAttachFiles(fileList) {
    if (!active || !fileList || fileList.length === 0) return;
    const current = activeAttachments.slice();
    let totalSoFar = bytesOf(current);
    const accepted = [];
    for (const f of Array.from(fileList)) {
      if (current.length + accepted.length >= ATTACH_MAX_COUNT) {
        showToast(`Max ${ATTACH_MAX_COUNT} attachments per email`);
        break;
      }
      if (totalSoFar + f.size > ATTACH_TOTAL_BYTES) {
        showToast(`Attachments would exceed 20MB total — skipping ${f.name}`);
        continue;
      }
      try {
        const base64 = await readAsBase64(f);
        accepted.push({
          id: `${f.name}-${f.size}-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)}`,
          name: f.name,
          type: f.type || "application/octet-stream",
          size: f.size,
          base64,
        });
        totalSoFar += f.size;
      } catch (err) {
        showToast(`Couldn't read ${f.name}`);
      }
    }
    if (accepted.length) {
      setAttachments((m) => ({
        ...m,
        [active.id]: [...current, ...accepted],
      }));
    }
    // Reset the file input so re-picking the same file fires onChange again.
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function removeAttachment(attId) {
    if (!active) return;
    setAttachments((m) => ({
      ...m,
      [active.id]: (m[active.id] || []).filter((a) => a.id !== attId),
    }));
  }

  function applyTemplate(t) {
    if (!active) return;
    updateDraft(active.id, {
      subject: t.subject || drafts[active.id]?.subject || "",
      personalized: false,
    });
    setEditorBody(t.body || "", { personalized: false });
    showToast(`Applied "${t.name}"`);
  }

  async function saveCurrentAsTemplate() {
    if (!active) return;
    const d = drafts[active.id] || {};
    if (!(d.subject || "").trim() && !(d.body || "").trim()) {
      showToast("Fill in a subject or body first");
      return;
    }
    const name = window.prompt("Template name");
    if (!name || !name.trim()) return;
    const newTemplate = {
      id: `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: name.trim(),
      subject: d.subject || "",
      body: d.body || "",
      isHtml: !!d.isHtml,
    };
    if (!onSaveTemplate) {
      showToast("Templates aren't ready yet");
      return;
    }
    const ok = await onSaveTemplate(newTemplate);
    showToast(ok ? "Template saved" : "Couldn't save template");
  }

  async function deleteTemplate(id) {
    if (!onDeleteTemplate) return;
    await onDeleteTemplate(id);
  }

  function insertLink() {
    const url = window.prompt("Link URL");
    if (!url) return;
    const safe = String(url).trim();
    if (!safe) return;
    const el = bodyRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      // Wrap existing selection in <a>.
      execFormat("createLink", safe);
    } else {
      // No selection — insert the URL as visible link text.
      execFormat(
        "insertHTML",
        `<a href="${safe.replace(/"/g, "&quot;")}">${safe}</a>`,
      );
    }
  }

  const personalizeOne = useCallback(
    async (person, progress, overrides = {}) => {
      if (!person) return;
      // `overrides` = { job, nameClient } from a menu choice made this instant; `batch` tags a bulk run.
      const pitched =
        overrides.job !== undefined ? overrides.job : pitchJobFor(person);
      const revealClient =
        overrides.nameClient !== undefined ? overrides.nameClient : nameClient;
      // Set up cancel handle + busy state. `progress` carries the
      // {index, total} bulk-mode tracker; `null`/undefined for single mode.
      const controller = new AbortController();
      aiAbortRef.current = controller;
      setAiActive({
        id: person.id,
        name: person.name,
        index: progress?.index,
        total: progress?.total,
      });
      setAiStartedAt(Date.now());
      setAiBusy(true);
      try {
        const draft = await generateEmailDraft(
          {
            to: person.email,
            name: person.name,
            role: person.role,
            company: person.company,
            location: [person.location, person.country]
              .filter(Boolean)
              .join(", "),
            recipientType: person.kind || "candidate",
            candidateContext: buildCandidateContext(person),
            job: pitched ? { title: pitched.title, company: pitched.company } : null,
            nameClient: revealClient,
            // Saved prompt from Settings, {{tokens}} resolved for this recipient.
            customPrompt: selectedPrompt
              ? resolveTokens(selectedPrompt.body, person)
              : "",
            ...(overrides.batch || {}),
          },
          { signal: controller.signal },
        );
        const newBodyRaw = draft.body || drafts[person.id]?.body || "";
        const newBody = joinBodyAndSignature(
          normalizeBodyToHtml(newBodyRaw),
          signatureBlock,
        );
        updateDraft(person.id, {
          subject: draft.subject || drafts[person.id]?.subject || "",
          body: newBody,
          personalized: true,
          isHtml: true,
        });
        if (bodyRef.current && initializedFor.current === person.id) {
          bodyRef.current.innerHTML = sanitizeComposerHtml(newBody);
        }
      } catch (err) {
        // Suppress the toast on a deliberate Cancel — that's not an error
        // the user should see, they pressed the button.
        if (err?.name !== "AbortError") {
          showToast(err.message || "Couldn't generate a draft");
        }
        throw err;
      } finally {
        setAiBusy(false);
        setAiActive(null);
        aiAbortRef.current = null;
      }
    },
    [drafts, showToast, activeId, selectedPrompt, pitchJobFor, nameClient],
  );

  // Cancel button on the AI header strip — abort the in-flight fetch and
  // tear down busy state. The personalizeOne `finally` block handles the
  // reset; calling abort() here is enough.
  const cancelAi = useCallback(() => {
    if (aiAbortRef.current) {
      try {
        aiAbortRef.current.abort();
      } catch {}
    }
  }, []);

  /** Auto-personalize via AI when the composer was opened from a "Draft
   * with AI" click. Runs ONCE per (mount, recipient) pair — otherwise the
   * effect would re-fire on every draft state change and clobber the
   * user's edits. */
  const aiFiredFor = useRef(new Set());
  useEffect(() => {
    if (!initialAiForId) return;
    if (aiFiredFor.current.has(initialAiForId)) return;
    const p = recipients.find((r) => r.id === initialAiForId);
    if (!p) return;
    aiFiredFor.current.add(initialAiForId);
    personalizeOne(p);
    // eslint-disable-next-line
  }, [initialAiForId]);

  async function personalizeAll() {
    setBulkBusy(true);
    try {
      const queue = recipients.filter((r) => !drafts[r.id]?.personalized);
      const total = queue.length;
      // One activity entry for the whole run.
      const batch = { batchId: newBatchId(), batchTotal: total };
      let index = 0;
      let cancelled = false;
      for (const r of queue) {
        index += 1;
        try {
          // eslint-disable-next-line no-await-in-loop
          await personalizeOne(r, { index, total }, { batch });
        } catch (err) {
          // User hit Cancel mid-loop — bail out of the rest of the bulk
          // run rather than continuing with the next recipient.
          if (err?.name === "AbortError") {
            cancelled = true;
            break;
          }
          // Other errors are surfaced by personalizeOne; keep going so a
          // single failure doesn't block the rest of the batch.
        }
      }
      if (!cancelled) showToast("Personalized all drafts");
    } finally {
      setBulkBusy(false);
    }
  }

  function applyToAll() {
    if (!active || !activeDraft) return;
    // Copy the source body verbatim. Recipient-specific bits should be
    // expressed as merge tokens — {{firstName}}, {{company}}, etc. — which
    // resolve per-recipient at send time. The sender's signature line
    // (e.g. "Best regards, Speedy") is the same for everyone and naturally
    // stays unchanged.
    setDrafts((d) => {
      const next = { ...d };
      recipients.forEach((r) => {
        if (r.id === active.id) return;
        next[r.id] = {
          ...next[r.id],
          subject: activeDraft.subject,
          body: activeDraft.body,
          isHtml: !!activeDraft.isHtml,
          personalized: false,
        };
      });
      return next;
    });
    // Attachments live outside `drafts` to avoid persisting base64 blobs
    // to the encrypted outreach-state doc. Copy them to every other
    // recipient too so apply-to-all matches what the user sees.
    const sourceAtts = attachments[active.id] || [];
    if (sourceAtts.length > 0) {
      setAttachments((m) => {
        const next = { ...m };
        recipients.forEach((r) => {
          if (r.id === active.id) return;
          next[r.id] = sourceAtts.map((a) => ({ ...a }));
        });
        return next;
      });
    }
    showToast("Applied to all recipients");
  }

  /** Send just the currently-active recipient's draft. Same encryption /
   * token-resolution pipeline as sendAll, scoped to one. Leaves the
   * composer open so the user can keep working on the other recipients. */
  async function sendActiveOnly() {
    if (!active || !activeDraft) return;
    if (!fromAddress) {
      showToast("Connect a mailbox first");
      return;
    }
    if (!active.email) {
      showToast(`${active.name || "Recipient"} has no email on file`);
      return;
    }
    if (
      !(activeDraft.subject || "").trim() ||
      !(activeDraft.body || "").trim()
    ) {
      showToast("Fill in subject and message first");
      return;
    }
    const placeholders = [
      ...new Set([
        ...placeholdersIn(activeDraft.subject),
        ...placeholdersIn(activeDraft.body),
      ]),
    ];
    if (placeholders.length > 0) {
      showToast(
        `Fill in or delete ${placeholders.join(", ")} before sending.`,
        { duration: 5000 },
      );
      return;
    }
    const missing = [
      ...new Set([
        ...unresolvedTokens(activeDraft.subject || "", active),
        ...unresolvedTokens(activeDraft.body || "", active),
      ]),
    ];
    if (missing.length > 0) {
      showToast(
        `Can't send — missing data for ${missing.join(", ")}. Use Preview to check.`,
        { duration: 5000 },
      );
      return;
    }
    setBulkBusy(true);
    try {
      const atts = (attachments[active.id] || []).map((a) => ({
        name: a.name,
        contentType: a.type,
        contentBase64: a.base64,
      }));
      const resolvedSubject = resolveTokens(activeDraft.subject || "", active);
      const resolvedBody = resolveTokens(activeDraft.body || "", active);
      const ok = await onSendOne(active, {
        ...activeDraft,
        subject: resolvedSubject,
        body: resolvedBody,
        html: !!activeDraft.isHtml,
        attachments: atts,
      });
      if (ok) {
        updateDraft(active.id, { sent: true });
        setAttachments((m) => {
          const next = { ...m };
          delete next[active.id];
          return next;
        });
        showToast(`Sent to ${active.name || active.email}`);
        // The row stays in the recipient list with ✓ Sent. Clicking it
        // shows the centered "Sent" confirmation on the right. The user
        // closes the composer manually when they're done.
      }
    } finally {
      setBulkBusy(false);
    }
  }

  /** Identify why a recipient can't be sent to. Returns "" when they're
   * sendable. Called only at click time, never per render. */
  function skipReasonFor(r) {
    if (!r?.email) return "No email";
    const d = drafts[r.id];
    if (!d?.subject?.trim() || !d?.body?.trim()) return "Empty draft";
    const placeholders = [
      ...new Set([...placeholdersIn(d.subject), ...placeholdersIn(d.body)]),
    ];
    if (placeholders.length > 0) return `Fill in ${placeholders.join(", ")}`;
    const missing = [
      ...new Set([
        ...unresolvedTokens(d.subject || "", r),
        ...unresolvedTokens(d.body || "", r),
      ]),
    ];
    if (missing.length > 0) return `Missing ${missing.join(", ")}`;
    return "";
  }

  async function sendAll() {
    // Pre-clear any prior sent/skipped flags so a re-run of Send all
    // gives a fresh result. Walk every recipient top-to-bottom: mark
    // skipped ones in red right away, send the rest one at a time.
    setDrafts((d) => {
      const next = { ...d };
      for (const r of recipients) {
        if (next[r.id]) {
          next[r.id] = { ...next[r.id], skipped: false, skipReason: "" };
        }
      }
      return next;
    });

    // Plan the work: split recipients into "send" and "skip" upfront so
    // the progress count is accurate and we can show row colors live.
    const plan = recipients.map((r) => ({
      r,
      reason: skipReasonFor(r),
    }));
    const sendable = plan.filter((p) => !p.reason);
    if (sendable.length === 0) {
      showToast(
        "No recipient is ready — fix missing email, content, or {{tokens}}",
      );
      return;
    }

    setBulkBusy(true);
    // One activity entry for the whole run; only sendable recipients count.
    const batch = { batchId: newBatchId(), batchTotal: sendable.length };
    try {
      let sentCount = 0;
      let skippedCount = 0;
      const skippedReasons = {
        noEmail: 0,
        emptyDraft: 0,
        missingTokens: 0,
      };
      for (const { r, reason } of plan) {
        if (reason) {
          // Skipped — mark red, count by reason, move on.
          updateDraft(r.id, { skipped: true, skipReason: reason });
          skippedCount += 1;
          if (reason === "No email") skippedReasons.noEmail += 1;
          else if (reason === "Empty draft") skippedReasons.emptyDraft += 1;
          else skippedReasons.missingTokens += 1;
          continue;
        }
        // Sendable — show the spinner on this row, then send.
        setSendProgress({
          currentId: r.id,
          currentIndex: sentCount + skippedCount + 1,
          total: recipients.length,
        });
        const draft = drafts[r.id];
        const atts = (attachments[r.id] || []).map((a) => ({
          name: a.name,
          contentType: a.type,
          contentBase64: a.base64,
        }));
        const resolvedSubject = resolveTokens(draft.subject || "", r);
        const resolvedBody = resolveTokens(draft.body || "", r);
        // eslint-disable-next-line no-await-in-loop
        const ok = await onSendOne(r, {
          ...draft,
          subject: resolvedSubject,
          body: resolvedBody,
          html: !!draft.isHtml,
          attachments: atts,
          batch,
        });
        if (ok) {
          updateDraft(r.id, { sent: true });
          setAttachments((m) => {
            const next = { ...m };
            delete next[r.id];
            return next;
          });
          sentCount += 1;
        } else {
          updateDraft(r.id, { skipped: true, skipReason: "Send failed" });
          skippedCount += 1;
        }
      }
      // Final toast with breakdown.
      const parts = [];
      if (skippedReasons.noEmail)
        parts.push(`${skippedReasons.noEmail} no email`);
      if (skippedReasons.emptyDraft)
        parts.push(`${skippedReasons.emptyDraft} empty draft`);
      if (skippedReasons.missingTokens)
        parts.push(`${skippedReasons.missingTokens} missing data`);
      if (skippedCount > 0) {
        showToast(
          `Sent ${sentCount} · Skipped ${skippedCount}${
            parts.length ? ` (${parts.join(", ")})` : ""
          }`,
          { duration: 5500 },
        );
      } else {
        showToast(`Sent ${sentCount} email${sentCount === 1 ? "" : "s"}`);
      }
      // Leave every row visible (green ✓ Sent + red ✗ Skipped). Clicking
      // a sent row on the left swaps the right-pane into the centered
      // "Sent to {email}" confirmation; clicking a skipped row shows the
      // editable draft so the user can fix the reason and re-send.
      // The composer stays open until the user closes it manually.
    } finally {
      setBulkBusy(false);
      setSendProgress(null);
    }
  }

  // Bulk personalize ("Personalize all") is the multi-recipient AI flow —
  // signalled by aiActive.total > 1. Single personalize stays inline in the
  // compose card so it sits next to the editor being filled in. Bulk
  // surfaces at the top of the recipients column so the user can watch
  // it walk through the list.
  const isBulkAi =
    aiBusy &&
    aiActive &&
    typeof aiActive.total === "number" &&
    aiActive.total > 1;

  return (
    <ComposerWrap>
      <RecipientsCol>
        {isBulkAi && (
          <div
            role="status"
            aria-live="polite"
            style={{
              margin: "10px 12px 0",
              padding: "10px 12px",
              borderRadius: 8,
              background: "var(--orx-accent-soft)",
              border: "1px solid var(--orx-line)",
              color: "var(--orx-ink-2)",
              fontSize: 12.5,
              flex: "0 0 auto",
              display: "flex",
              flexDirection: "column",
              gap: 6,
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontWeight: 600,
                minWidth: 0,
              }}
            >
              <Spark size={12} style={{ flex: "0 0 auto" }} />
              <span style={{ flex: "0 0 auto" }}>Personalizing</span>
              <span
                style={{
                  marginLeft: "auto",
                  color: "var(--orx-ink-muted)",
                  fontSize: 11.5,
                  fontWeight: 500,
                  flex: "0 0 auto",
                }}
              >
                {aiActive.index} / {aiActive.total}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "var(--orx-ink)",
                }}
                title={aiActive.name || ""}
              >
                {aiActive.name || ""}
              </span>
              <span
                style={{
                  color: "var(--orx-ink-muted)",
                  fontSize: 11.5,
                  flex: "0 0 auto",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatElapsed(aiStartedAt)}
              </span>
              <AiCancelBtn
                onClick={cancelAi}
                style={{ padding: "3px 8px", fontSize: 11.5 }}
              >
                Cancel
              </AiCancelBtn>
            </div>
          </div>
        )}
        <div
          style={{
            padding: "16px 16px 14px",
            borderBottom: `1px solid var(--orx-line-soft)`,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: "var(--orx-ink-muted)",
              textTransform: "uppercase",
              letterSpacing: ".08em",
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            Recipients
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              {recipients.length}{" "}
              {recipients.length === 1 ? "person" : "people"}
            </span>
            {personalizedCount > 0 && (
              <span
                style={{
                  fontSize: 11.5,
                  color: "var(--orx-accent-ink)",
                  background: "var(--orx-accent-soft)",
                  padding: "2px 8px",
                  borderRadius: 99,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  lineHeight: 1.4,
                }}
              >
                <Spark size={12} /> {personalizedCount} personalized
              </span>
            )}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {recipients.map((p) => {
            const d = drafts[p.id] || {};
            const isSending = sendProgress?.currentId === p.id;
            // Token-resolution pills only render while Preview is on —
            // that's the explicit "review my emails" mode. Editing stays
            // free of per-keystroke token work.
            const missingTokens =
              showPreview && !d.sent && !d.skipped && p.email
                ? [
                    ...new Set([
                      ...unresolvedTokens(d.subject || "", p),
                      ...unresolvedTokens(d.body || "", p),
                    ]),
                  ]
                : [];
            return (
              <RecipientItem
                key={p.id}
                $active={p.id === activeId}
                onClick={() => setActiveId(p.id)}
                style={
                  isSending
                    ? {
                        background: "var(--orx-accent-soft)",
                      }
                    : undefined
                }
              >
                <Avatar person={p} size="sm" />
                <div style={{ minWidth: 0 }}>
                  <RName>{p.name}</RName>
                  <RRole>
                    {isSending ? (
                      <span style={{ color: "var(--orx-accent-ink)" }}>
                        <MatchStripSpinner
                          aria-hidden
                          style={{
                            width: 9,
                            height: 9,
                            verticalAlign: "middle",
                            marginRight: 4,
                          }}
                        />
                        Sending…
                      </span>
                    ) : d.sent ? (
                      <span
                        style={{
                          color: "var(--orx-success)",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <FiCheck size={12} /> Sent
                      </span>
                    ) : d.skipped ? (
                      <span
                        style={{
                          color: "var(--orx-danger, var(--danger))",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                        title={d.skipReason || "Skipped"}
                      >
                        <FiX size={12} /> Skipped{d.skipReason ? ` · ${d.skipReason}` : ""}
                      </span>
                    ) : !p.email ? (
                      <span
                        style={{
                          color: "var(--orx-danger, var(--danger))",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                        title="No email on file — this recipient will be skipped"
                      >
                        <FiAlertTriangle size={12} /> No email
                      </span>
                    ) : missingTokens.length > 0 ? (
                      <span
                        style={{ color: "var(--orx-danger, var(--danger))" }}
                        title={`Missing: ${missingTokens.join(", ")}`}
                      >
                        <FiAlertTriangle size={12} /> Missing {missingTokens.length}
                      </span>
                    ) : d.personalized ? (
                      <span
                        style={{
                          color: "var(--orx-accent-ink)",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <Spark size={12} /> Personalized
                      </span>
                    ) : (
                      <span>Draft</span>
                    )}
                    <span style={{ color: "var(--orx-ink-faint)" }}> · </span>
                    {p.company || p.role}
                  </RRole>
                </div>
                <IconBtn
                  $size={22}
                  title="Remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveRecipient(p.id);
                  }}
                >
                  <FiX size={12} />
                </IconBtn>
              </RecipientItem>
            );
          })}
        </div>
        <div
          style={{
            padding: "10px 14px",
            borderTop: `1px solid var(--orx-line-soft)`,
            background: "var(--orx-surface-tint)",
          }}
        >
          <Btn
            $sm
            style={{ width: "100%", justifyContent: "center" }}
            onClick={personalizeAll}
            disabled={bulkBusy || aiBusy}
          >
            <Spark size={12} />{" "}
            {bulkBusy ? "Personalizing…" : "Personalize all with AI"}
          </Btn>
        </div>
      </RecipientsCol>

      <EditCol>
        <div
          style={{
            padding: "16px var(--compose-gx, 24px) 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <Btn $variant="ghost" $sm onClick={() => onClose(drafts, draftIds)}>
            <FiChevronLeft size={14} /> Cancel
          </Btn>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--orx-ink-muted)" }}>
              {readyCount} of {recipients.length} ready
            </span>
            <div
              style={{
                width: 1,
                height: 16,
                background: "var(--orx-line)",
                margin: "0 6px",
              }}
            />
            <Btn $sm onClick={applyToAll} disabled={!active}>
              <FiCopy size={12} /> Apply to all
            </Btn>
            <Btn
              $sm
              $variant={showPreview ? "primary" : undefined}
              onClick={() => setShowPreview((v) => !v)}
              disabled={!active}
              title={
                showPreview
                  ? "Back to editing"
                  : "Preview the current recipient's resolved email (switch recipients in the list to compare)"
              }
            >
              <FiSearch size={12} /> {showPreview ? "Edit" : "Preview"}
            </Btn>
          </div>
        </div>

        {active ? (
          <ComposeCard>
            <div
              style={{
                padding: "10px 16px",
                borderBottom: `1px solid var(--orx-line-soft)`,
                background: "var(--orx-surface-tint)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  minWidth: 0,
                }}
              >
                <Avatar person={active} size="sm" />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                    {active.name}
                  </div>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: "var(--orx-ink-muted)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {[active.role, active.company, active.email]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
              </div>
              {/* Wraps: AI pill + prompt + role pickers run ~500px, wider
                  than the card on a phone. */}
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {activeDraft?.personalized && (
                  <AiPill>
                    <Spark size={12} /> AI personalized
                  </AiPill>
                )}
                {/* Prompt picker. Hidden entirely when the user has no saved
                 * prompts — an empty dropdown next to Personalize is noise. */}
                {aiPrompts.length > 0 && (
                  <>
                    <Btn
                      $sm
                      ref={promptBtnRef}
                      onClick={() => setShowPromptMenu((v) => !v)}
                      disabled={aiBusy || bulkBusy}
                      title={
                        selectedPrompt
                          ? `AI prompt: ${selectedPrompt.name}`
                          : "No AI prompt applied — pick one from Settings → AI Prompts"
                      }
                    >
                      <Spark size={12} />
                      <span
                        style={{
                          maxWidth: 130,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedPrompt ? selectedPrompt.name : "No prompt"}
                      </span>
                      <FiChevronDown size={12} />
                    </Btn>
                    <DropMenu
                      open={showPromptMenu}
                      onClose={() => setShowPromptMenu(false)}
                      anchorRef={promptBtnRef}
                      align="right"
                      minWidth={240}
                      maxHeight={300}
                    >
                      <MenuItem onClick={() => choosePrompt(null)}>
                        <span style={{ flex: 1 }}>No prompt</span>
                        {!selectedPrompt && <FiCheck size={12} />}
                      </MenuItem>
                      <MenuDivider />
                      {aiPrompts.map((p) => (
                        <MenuItem key={p.id} onClick={() => choosePrompt(p.id)}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>{p.name}</span>
                            <span
                              style={{
                                fontSize: 11.5,
                                color: "var(--orx-ink-muted)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {p.body}
                            </span>
                          </div>
                          {selectedPrompt?.id === p.id && <FiCheck size={12} />}
                        </MenuItem>
                      ))}
                    </DropMenu>
                  </>
                )}
                {/* Role to pitch: one job per email, or the general template.
                 * Candidates only; clients get the SLA pitch. */}
                {active?.kind !== "client" && (
                  <>
                    <Btn
                      $sm
                      ref={jobBtnRef}
                      onClick={() => setShowJobMenu((v) => !v)}
                      disabled={aiBusy || bulkBusy}
                      title={
                        pitchJobFor(active)
                          ? `Role to pitch: ${pitchJobFor(active).title}`
                          : "General template — the draft leaves [Job title] and [Industry] for you to fill in"
                      }
                    >
                      <FiBriefcase size={12} />
                      <span
                        style={{
                          maxWidth: 150,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {pitchJobFor(active)?.title || "General Template"}
                      </span>
                      <FiChevronDown size={12} />
                    </Btn>
                    <DropMenu
                      open={showJobMenu}
                      onClose={() => setShowJobMenu(false)}
                      anchorRef={jobBtnRef}
                      align="right"
                      minWidth={260}
                      maxHeight={320}
                    >
                      {jobLinksFor(active).map((l) => (
                        <MenuItem
                          key={l.jobId}
                          onClick={() => {
                            setPitchJobIds((m) => ({ ...m, [active.id]: l.jobId }));
                            setShowJobMenu(false);
                            personalizeOne(active, null, { job: l });
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>{l.title}</span>
                            {l.company && (
                              <span
                                style={{
                                  fontSize: 11.5,
                                  color: "var(--orx-ink-muted)",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {l.company}
                              </span>
                            )}
                          </div>
                          {pitchJobFor(active)?.jobId === l.jobId && (
                            <FiCheck size={12} />
                          )}
                        </MenuItem>
                      ))}
                      <MenuItem
                        onClick={() => {
                          setPitchJobIds((m) => ({ ...m, [active.id]: "" }));
                          setShowJobMenu(false);
                          personalizeOne(active, null, { job: null });
                        }}
                      >
                        <span style={{ flex: 1 }}>General Template</span>
                        {!pitchJobFor(active) && <FiCheck size={12} />}
                      </MenuItem>
                      {/* Anonymity only means something when a job is pitched. */}
                      {pitchJobFor(active) && (
                        <>
                          <MenuDivider />
                          <MenuItem
                            onClick={() => {
                              const next = !nameClient;
                              setNameClient(next);
                              setShowJobMenu(false);
                              personalizeOne(active, null, { nameClient: next });
                            }}
                            title="On: the client is described only by industry. Off: the hiring company is named."
                          >
                            <span style={{ flex: 1 }}>Keep client anonymous</span>
                            {!nameClient && <FiCheck size={12} />}
                          </MenuItem>
                        </>
                      )}
                    </DropMenu>
                  </>
                )}
                <Btn
                  $sm
                  onClick={() => personalizeOne(active)}
                  disabled={aiBusy}
                >
                  <Spark size={12} /> {aiBusy ? "…" : "Personalize"}
                </Btn>
              </div>
            </div>

            {/* AI loading state — header strip + shimmer placeholders for
             * subject/body. Rendered only while AI is generating for the
             * active recipient. */}
            {aiBusy && aiActive && aiActive.id === active.id && !isBulkAi && (
              <AiHeader role="status" aria-live="polite">
                <Spark size={14} />
                <span>
                  Personalizing for{" "}
                  <b style={{ color: "var(--orx-ink)" }}>
                    {aiActive.name || active.name}
                  </b>
                  …
                </span>
                {aiActive.total > 1 && (
                  <AiHeaderMeta>
                    {aiActive.index} of {aiActive.total}
                  </AiHeaderMeta>
                )}
                <AiHeaderMeta>{formatElapsed(aiStartedAt)}</AiHeaderMeta>
                <AiCancelBtn onClick={cancelAi}>Cancel</AiCancelBtn>
              </AiHeader>
            )}

            {/* "Sent" confirmation. Sent rows stay visible in the recipient
             * list with a green ✓ Sent label; clicking one shows this
             * centered confirmation instead of the editable form. */}
            {activeDraft?.sent ? (
              <div
                style={{
                  flex: "1 1 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 12,
                  padding: 40,
                  color: "var(--orx-success)",
                  textAlign: "center",
                }}
              >
                <FiCheck size={36} />
                <div style={{ fontSize: 16, fontWeight: 600 }}>
                  Sent to {active?.email || active?.name || "this recipient"}
                </div>
              </div>
            ) : (
              <>
                <ComposeField>
                  <label>From</label>
                  <div
                    style={{
                      fontSize: 13.5,
                      color: "var(--orx-ink-soft)",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {fromName || "You"}{" "}
                    <span style={{ color: "var(--orx-ink-muted)" }}>
                      &lt;{fromAddress || "no mailbox connected"}&gt;
                    </span>
                  </div>
                </ComposeField>
                <ComposeField>
                  <label>To</label>
                  <div
                    style={{
                      fontSize: 13.5,
                      color: "var(--orx-ink)",
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {active.email ? (
                      active.email
                    ) : canFindEmail(active) && onFindEmail ? (
                      <FindEmailAction
                        pending={!!emailPendingIds?.has(active.id)}
                        onClick={() => onFindEmail(active.id)}
                      />
                    ) : (
                      <span style={{ color: "var(--orx-danger)" }}>
                        {active.emailSearched ? "No email found" : "No email on file"}
                      </span>
                    )}
                    {(active.emails?.length || 0) > 1 && (
                      <>
                        <button
                          ref={toBtnRef}
                          type="button"
                          onClick={() => setShowToMenu((v) => !v)}
                          title="Choose which email to send to"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                            padding: "2px 8px",
                            borderRadius: 99,
                            border: "1px solid var(--orx-line-soft)",
                            background: "var(--orx-bg-2)",
                            color: "var(--orx-ink-soft)",
                            fontSize: 11.5,
                            cursor: "pointer",
                            // Beats the global 44px phone touch floor (App.js).
                            minHeight: 0,
                          }}
                        >
                          {active.emails.length} possible
                          <FiChevronDown size={12} />
                        </button>
                        <DropMenu
                          open={showToMenu}
                          onClose={() => setShowToMenu(false)}
                          anchorRef={toBtnRef}
                        >
                          {active.emails.map((e) => (
                            <MenuItem
                              key={e}
                              onClick={() => {
                                onChangeRecipientEmail?.(active.id, e);
                                setShowToMenu(false);
                              }}
                            >
                              <FiCheck
                                size={12}
                                style={{
                                  visibility:
                                    e === active.email ? "visible" : "hidden",
                                }}
                              />
                              {e}
                            </MenuItem>
                          ))}
                        </DropMenu>
                      </>
                    )}
                  </div>
                  {!active.email && emailNotes?.get(active.id) && (
                    <div
                      style={{
                        fontSize: 12,
                        marginTop: 4,
                        color: emailNotes.get(active.id).isError
                          ? "var(--orx-danger, var(--danger))"
                          : "var(--orx-ink-muted)",
                      }}
                    >
                      {emailNotes.get(active.id).text}
                    </div>
                  )}
                </ComposeField>
                <ComposeField>
                  <label>Subject</label>
                  {aiBusy && aiActive?.id === active.id ? (
                    <ShimmerBar aria-hidden />
                  ) : showPreview ? (
                    <div
                      style={{ fontSize: 14, color: "var(--orx-ink)" }}
                      // Resolved + token-highlighted. Sanitized via the same
                      // helper the editor uses, with a custom highlighter that
                      // wraps any remaining {{token}} in a red <mark>.
                      dangerouslySetInnerHTML={{
                        __html: sanitizeComposerHtml(
                          highlightTokens(
                            resolveTokens(activeDraft?.subject || "", active),
                          ) || "<i>(empty)</i>",
                        ),
                      }}
                    />
                  ) : (
                    <input
                      value={activeDraft?.subject || ""}
                      onChange={(e) =>
                        updateDraft(active.id, { subject: e.target.value })
                      }
                      placeholder="Subject line…"
                    />
                  )}
                </ComposeField>
                <ComposeBody>
                  {(() => {
                    const ph = [
                      ...new Set([
                        ...placeholdersIn(activeDraft?.subject),
                        ...placeholdersIn(activeDraft?.body),
                      ]),
                    ];
                    return ph.length > 0 &&
                      !(aiBusy && aiActive?.id === active.id) ? (
                      <PlaceholderStrip role="status">
                        <FiAlertTriangle size={14} />
                        <span>
                          Fill in or delete{" "}
                          {ph.map((t, i) => (
                            <span key={t}>
                              {i > 0 && ", "}
                              <code>{t}</code>
                            </span>
                          ))}{" "}
                          before sending.
                        </span>
                      </PlaceholderStrip>
                    ) : null;
                  })()}
                  {/* Keep the Editor mounted at all times so bodyRef.current
                   * stays valid for the AI write (personalizeOne pokes the
                   * contentEditable directly). Visually hide it while the
                   * shimmer is shown — the content lands correctly under the
                   * shimmer and is visible the moment the shimmer disappears. */}
                  {aiBusy && aiActive?.id === active.id && (
                    <ShimmerLines aria-hidden>
                      <ShimmerLine $w="92%" />
                      <ShimmerLine $w="78%" />
                      <ShimmerLine $w="86%" />
                      <ShimmerLine $w="64%" />
                      <ShimmerLine $w="88%" />
                      <ShimmerLine $w="55%" />
                    </ShimmerLines>
                  )}
                  {showPreview && !(aiBusy && aiActive?.id === active.id) && (
                    <div
                      style={{
                        minHeight: 280,
                        fontSize: 14.5,
                        lineHeight: 1.6,
                        color: "var(--orx-ink)",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeComposerHtml(
                          highlightTokens(
                            resolveTokens(activeDraft?.body || "", active),
                          ) || "<i>(empty)</i>",
                        ),
                      }}
                    />
                  )}
                  <Editor
                    ref={bodyRef}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder="Write your message…"
                    onInput={captureEditorBody}
                    onBlur={captureEditorBody}
                    style={
                      showPreview || (aiBusy && aiActive?.id === active.id)
                        ? { display: "none" }
                        : undefined
                    }
                  />
                  {activeAttachments.length > 0 && (
                    <AttachmentStrip>
                      {activeAttachments.map((a) => (
                        <AttachmentChip key={a.id}>
                          <FiPaperclip size={12} />
                          <span className="att-name" title={a.name}>
                            {a.name}
                          </span>
                          <span className="att-size">
                            {formatBytes(a.size)}
                          </span>
                          <button
                            type="button"
                            aria-label="Remove attachment"
                            onClick={() => removeAttachment(a.id)}
                          >
                            <FiX size={12} />
                          </button>
                        </AttachmentChip>
                      ))}
                    </AttachmentStrip>
                  )}
                </ComposeBody>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => handleAttachFiles(e.target.files)}
                />
                <AiDimmable
                  $dimmed={aiBusy && aiActive?.id === active.id}
                  aria-busy={
                    aiBusy && aiActive?.id === active.id ? true : undefined
                  }
                >
                  <ComposeFoot>
                    <div
                      style={{ display: "flex", gap: 4, alignItems: "center" }}
                    >
                      <IconBtn
                        title="Bold (Ctrl+B)"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => execFormat("bold")}
                      >
                        <FiBold size={12} />
                      </IconBtn>
                      <IconBtn
                        title="Italic (Ctrl+I)"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => execFormat("italic")}
                      >
                        <FiItalic size={12} />
                      </IconBtn>
                      <IconBtn
                        title="Insert link"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={insertLink}
                      >
                        <FiLink size={12} />
                      </IconBtn>
                      <IconBtn
                        title="Attach files"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <FiPaperclip size={12} />
                      </IconBtn>
                      <div style={{ position: "relative" }}>
                        <IconBtn
                          title="Insert recipient field (resolves at send time)"
                          onClick={() => setShowInsertMenu((v) => !v)}
                        >
                          <FiTag size={12} />
                        </IconBtn>
                        {showInsertMenu && (
                          <Menu
                            style={{
                              position: "absolute",
                              bottom: "calc(100% + 4px)",
                              left: 0,
                              minWidth: 240,
                            }}
                            onClose={() => setShowInsertMenu(false)}
                          >
                            <MenuScroll>
                              <div
                                style={{
                                  padding: "8px 12px 4px",
                                  fontSize: 11,
                                  color: "var(--orx-ink-muted)",
                                  textTransform: "uppercase",
                                  letterSpacing: ".06em",
                                  fontWeight: 600,
                                }}
                              >
                                Insert recipient field
                              </div>
                              {SUPPORTED_TOKENS.map((t) => (
                                <MenuItem
                                  key={t.key}
                                  onClick={() => insertToken(t.key)}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 2,
                                    }}
                                  >
                                    <span style={{ fontWeight: 500 }}>
                                      {t.label}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: "var(--orx-ink-muted)",
                                        fontFamily:
                                          "ui-monospace, Menlo, monospace",
                                      }}
                                    >
                                      {t.example}
                                    </span>
                                  </div>
                                </MenuItem>
                              ))}
                            </MenuScroll>
                          </Menu>
                        )}
                      </div>
                      <IconBtn
                        title="Insert signature"
                        onClick={insertSignature}
                        disabled={!signatureBlock}
                      >
                        <FiEdit3 size={12} />
                      </IconBtn>
                      <div style={{ position: "relative" }}>
                        <IconBtn
                          title="Templates"
                          onClick={() => setShowTemplates((v) => !v)}
                        >
                          <Spark size={12} />
                        </IconBtn>
                        {showTemplates && (
                          <Menu
                            style={{
                              position: "absolute",
                              bottom: "calc(100% + 4px)",
                              left: 0,
                              minWidth: 220,
                              maxHeight: 280,
                            }}
                          >
                            <MenuScroll style={{ maxHeight: 280 }}>
                              {templates.length === 0 ? (
                                <div
                                  style={{
                                    padding: "10px 12px",
                                    fontSize: 12.5,
                                    color: "var(--orx-ink-muted)",
                                  }}
                                >
                                  No saved templates yet
                                </div>
                              ) : (
                                templates.map((t) => (
                                  <MenuItem
                                    key={t.id}
                                    onClick={() => {
                                      applyTemplate(t);
                                      setShowTemplates(false);
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        minWidth: 0,
                                        flex: 1,
                                      }}
                                    >
                                      <span style={{ fontWeight: 600 }}>
                                        {t.name}
                                      </span>
                                      {t.subject && (
                                        <span
                                          style={{
                                            fontSize: 11.5,
                                            color: "var(--orx-ink-muted)",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {t.subject}
                                        </span>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      title="Delete template"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteTemplate(t.id);
                                      }}
                                      style={{
                                        background: "transparent",
                                        border: 0,
                                        cursor: "pointer",
                                        color: "var(--orx-ink-muted)",
                                        padding: 2,
                                        // Beats the global 44px phone touch floor (App.js).
                                        minHeight: 0,
                                      }}
                                    >
                                      <FiTrash2 size={12} />
                                    </button>
                                  </MenuItem>
                                ))
                              )}
                              <MenuDivider />
                              <MenuItem
                                onClick={() => {
                                  setShowTemplates(false);
                                  saveCurrentAsTemplate();
                                }}
                              >
                                + Save current as template
                              </MenuItem>
                            </MenuScroll>
                          </Menu>
                        )}
                      </div>
                    </div>
                    {(() => {
                      // Per-button gates. Each evaluated to (a) flip `disabled`
                      // and (b) populate a tooltip + inline reason so the user
                      // knows WHY a send is blocked, not just that it is.
                      const activeReady =
                        activeDraft &&
                        (activeDraft.subject || "").trim() &&
                        (activeDraft.body || "").trim();
                      // Token resolution is NOT checked here — that runs at the
                      // moment of Send (sendActiveOnly / sendAll) and via the
                      // Preview view. This keeps typing cheap.
                      const activePlaceholders = [
                        ...new Set([
                          ...placeholdersIn(activeDraft?.subject),
                          ...placeholdersIn(activeDraft?.body),
                        ]),
                      ];
                      const sendOneReason = !fromAddress
                        ? "Connect a mailbox first"
                        : !active?.email
                          ? `${active?.name || "Recipient"} has no email on file`
                          : !activeReady
                            ? "Fill in subject and message"
                            : activePlaceholders.length
                              ? `Fill in or delete ${activePlaceholders.join(", ")}`
                              : "";
                      const sendAllReason = !fromAddress
                        ? "Connect a mailbox first"
                        : readyCount === 0
                          ? "No recipient is ready — fix missing email, content, or {{tokens}}"
                          : "";
                      const showBulkButton = recipients.length > 1;
                      // Pick the most informative reason to surface inline.
                      // Prefer the one-recipient reason when the active draft is
                      // the blocker; fall back to the all-recipients reason.
                      const inlineReason = sendOneReason || sendAllReason;

                      return (
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexWrap: "nowrap",
                            minWidth: 0,
                          }}
                        >
                          {inlineReason ? (
                            <span
                              style={{
                                fontSize: 11.5,
                                color: "var(--orx-danger, var(--danger))",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: "1 1 auto",
                              }}
                              title={inlineReason}
                            >
                              <FiAlertCircle
                                size={12}
                                style={{ flex: "0 0 auto" }}
                              />
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {inlineReason}
                              </span>
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: 12,
                                color: "var(--orx-ink-muted)",
                                whiteSpace: "nowrap",
                                flex: "0 0 auto",
                              }}
                            >
                              {(activeDraft?.body || "").length} chars
                            </span>
                          )}
                          <Btn
                            $sm
                            onClick={sendActiveOnly}
                            disabled={bulkBusy || !!sendOneReason}
                            title={
                              sendOneReason ||
                              `Send to ${active?.name || active?.email || "this recipient"}`
                            }
                          >
                            <FiSend size={12} />{" "}
                            {bulkBusy ? "Sending…" : "Send"}
                          </Btn>
                          {showBulkButton && (
                            <Btn
                              $variant="primary"
                              $sm
                              onClick={sendAll}
                              disabled={bulkBusy || !!sendAllReason}
                              title={sendAllReason || "Send to every recipient"}
                            >
                              <FiSend size={12} />{" "}
                              {bulkBusy && sendProgress
                                ? `Sending ${sendProgress.currentIndex} of ${sendProgress.total}…`
                                : bulkBusy
                                  ? "Sending…"
                                  : "Send all"}
                            </Btn>
                          )}
                        </div>
                      );
                    })()}
                  </ComposeFoot>
                </AiDimmable>
              </>
            )}
          </ComposeCard>
        ) : (
          <div
            style={{
              padding: 60,
              textAlign: "center",
              color: "var(--orx-ink-muted)",
            }}
          >
            No recipients selected.
          </div>
        )}
      </EditCol>
    </ComposerWrap>
  );
}

/* ───────────────── Selection toolbar ─────────────────
   Sits under the count/sort row while anything is ticked: the count and its
   actions share one line at the top of the list instead of floating over the
   page and wrapping. */
const SelBar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 42px;
  box-sizing: border-box;
  padding: 6px 8px 6px 16px;
  border-bottom: 1px solid var(--orx-line-soft);
  background: var(--orx-accent-soft);
  color: var(--orx-ink);
  font-size: 13px;
  white-space: nowrap;
  animation: ${fadeIn} 0.15s ease both;
  /* The animation makes the bar a stacking context, so SelTip's z-index only counts inside
     it; lift the whole bar above the list that follows or the labels paint underneath. */
  position: relative;
  z-index: 3;
  /* Visible overflow, or the hover labels under the icon buttons (SelTip) get clipped:
     overflow-x other than visible clips the vertical axis too. Very narrow phones,
     which have no hover, scroll instead of wrapping. */
  overflow: visible;
  @media (max-width: 480px) {
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;
/* Master checkbox, aligned with the row checkboxes below it. Some selected
   shows a dash; all selected shows a tick. Click toggles all / none. */
const SelMaster = styled(Check)`
  cursor: pointer;
  margin-right: 8px;
`;
/* "All" label beside the master checkbox; clicks act like the box. */
const SelWord = styled.span`
  font-weight: 500;
  cursor: pointer;
  user-select: none;
`;
const SelSpacer = styled.div`
  flex: 1;
  min-width: 8px;
`;
const SelBtn = styled.button`
  height: 30px;
  /* Global 44px touch-target floor in App.js would clamp the height. */
  min-height: 0;
  padding: 0 ${(p) => (p.$icon ? "8px" : "9px")};
  background: ${(p) => (p.$primary ? "var(--orx-accent)" : "transparent")};
  border: 0;
  color: ${(p) => (p.$primary ? "var(--on-fill)" : "var(--orx-ink)")};
  font: inherit;
  font-size: 12.5px;
  font-weight: ${(p) => (p.$primary ? 600 : 500)};
  border-radius: 7px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;
  position: relative;
  &:hover {
    background: ${(p) =>
      p.$primary
        ? "var(--orx-accent-2)"
        : "color-mix(in srgb, var(--orx-accent) 14%, transparent)"};
  }
  &[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
/* Hover label for the icon-only actions, shown under the button so it never
   covers the count/sort row above. Hidden while the button's menu is open. */
const SelTip = styled.span`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--orx-elevated-bg);
  color: var(--orx-elevated-fg);
  padding: 5px 9px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  white-space: nowrap;
  box-shadow: var(--shadow-md);
  z-index: 40;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.1s;
  ${SelBtn}:hover &,
  ${SelBtn}:focus-visible & {
    opacity: 1;
  }
`;
/* Small count pill inside an icon button (e.g. how many emails to look up). */
const SelBadge = styled.span`
  font-size: 10.5px;
  font-weight: 600;
  line-height: 16px;
  height: 16px;
  padding: 0 5px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--orx-accent) 18%, transparent);
  color: var(--orx-accent);
  font-variant-numeric: tabular-nums;
`;
/* The bar's action buttons. Phone-only: on desktop the actions live in the
   open profile (one ticked) or the right-column selection pane (two or more),
   so the bar is just the master checkbox there. */
const SelActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  @media (min-width: 901px) {
    display: none;
  }
`;

/* ───────────────── Selection pane (right column, 2+ people ticked) ─────────────────
   The bar's icon buttons are cramped for a multi-person action, so once two or
   more rows are ticked the right column becomes the action surface: who is
   picked plus one roomy card per bulk action. The bar keeps only the master
   checkbox while the pane is showing; on phones (no right column) the bar
   keeps its buttons. */
const SelPaneWrap = styled.div`
  flex: 1;
  overflow-y: auto;
  background: var(--orx-bg);
  min-width: 0;
  animation: ${fadeIn} 0.18s ease both;
`;
const SelPaneInner = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 48px 28px 40px;
`;
const SelPaneHead = styled.div`
  text-align: center;
  margin-bottom: 26px;
  h3 {
    font-family: ${C.fontDisplay};
    font-size: 26px;
    font-weight: 400;
    margin: 14px 0 6px;
    color: var(--orx-ink);
  }
  p {
    margin: 0;
    font-size: 13.5px;
    color: var(--orx-ink-muted);
    line-height: 1.5;
  }
`;
/* Overlapping avatars of the picked people; the last slot is a "+N" count. */
const SelFaces = styled.div`
  display: flex;
  justify-content: center;
  > * {
    margin-left: -8px;
    border: 2px solid var(--orx-bg);
    border-radius: 99px;
    box-sizing: content-box;
  }
  > *:first-child {
    margin-left: 0;
  }
`;
const SelFaceMore = styled.div`
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  background: var(--orx-surface-tint);
  color: var(--orx-ink-soft);
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;
const SelActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const SelAction = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid var(--orx-line);
  border-radius: 12px;
  background: var(--orx-surface);
  @media (max-width: 1100px) {
    flex-wrap: wrap;
  }
`;
const SelActionIcon = styled.div`
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: ${(p) => (p.$muted ? "var(--orx-surface-tint)" : "var(--orx-accent-soft)")};
  color: ${(p) => (p.$muted ? "var(--orx-ink-muted)" : "var(--orx-accent)")};
`;
const SelActionText = styled.div`
  flex: 1;
  min-width: 0;
  .t {
    font-size: 14px;
    font-weight: 600;
    color: var(--orx-ink);
  }
  .d {
    font-size: 12.5px;
    color: var(--orx-ink-muted);
    margin-top: 2px;
    line-height: 1.4;
  }
`;
const SelPaneFoot = styled.div`
  display: flex;
  justify-content: center;
  gap: 18px;
  margin-top: 22px;
  font-size: 12.5px;
  button {
    background: none;
    border: 0;
    padding: 0;
    min-height: 0;
    font: inherit;
    color: var(--orx-accent);
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
`;

function SelectionPane({
  people,
  count,
  tab,
  allCount,
  onSelectAll,
  onClear,
  onEmail,
  canMatch,
  matchRunning,
  onMatch,
  showEmailLookup,
  emailLookupCount,
  emailJobRunning,
  onFindEmails,
  onExportCsv,
  exportBusy,
}) {
  const [menu, setMenu] = useState(false);
  const matchRef = useRef(null);
  const noun = tab === "clients" ? "clients" : "people";
  const faces = people.slice(0, 5);
  const names = people.map((p) => p.name).filter(Boolean);
  const who =
    names.length <= 3
      ? names.join(", ")
      : `${names.slice(0, 2).join(", ")} and ${names.length - 2} others`;
  return (
    <SelPaneWrap>
      <SelPaneInner>
        <SelPaneHead>
          <SelFaces>
            {faces.map((p) => (
              <Avatar key={p.id} person={p} size="sm" />
            ))}
            {count > faces.length && <SelFaceMore>+{count - faces.length}</SelFaceMore>}
          </SelFaces>
          <h3>
            {count} {noun} selected
          </h3>
          <p>{who}</p>
        </SelPaneHead>
        <SelActionList>
          <SelAction>
            <SelActionIcon>
              <FiSend size={17} />
            </SelActionIcon>
            <SelActionText>
              <div className="t">Email</div>
              <div className="d">
                Open the composer with everyone as a recipient. Each person gets
                their own draft.
              </div>
            </SelActionText>
            <Btn $variant="primary" onClick={onEmail}>
              <FiSend size={13} /> Email {count}
            </Btn>
          </SelAction>
          {canMatch && (
            <SelAction>
              <SelActionIcon>
                <Spark size={17} />
              </SelActionIcon>
              <SelActionText>
                <div className="t">Job match</div>
                <div className="d">
                  Rank these candidates against your jobs. Runs in the
                  background and shows up under Activities.
                </div>
              </SelActionText>
              <div style={{ position: "relative" }} ref={matchRef}>
                <Btn onClick={() => setMenu((v) => !v)} disabled={matchRunning}>
                  {matchRunning ? "Matching…" : "Match against"}
                  <FiChevronDown size={12} />
                </Btn>
                <DropMenu
                  open={menu}
                  onClose={() => setMenu(false)}
                  anchorRef={matchRef}
                  align="right"
                  minWidth={240}
                >
                  {MATCH_SCOPES.map((s) => (
                    <MenuItem
                      key={s.id}
                      onClick={() => {
                        setMenu(false);
                        onMatch(s.id);
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 500 }}>{s.label}</div>
                        <div
                          style={{
                            fontSize: 11.5,
                            color: "var(--orx-ink-muted)",
                            marginTop: 1,
                          }}
                        >
                          {s.sub}
                        </div>
                      </div>
                    </MenuItem>
                  ))}
                </DropMenu>
              </div>
            </SelAction>
          )}
          {/* Always listed on candidates so the action is discoverable; with
              nobody left to look up it just says so. */}
          {showEmailLookup && (
            <SelAction>
              <SelActionIcon $muted={emailLookupCount === 0}>
                {emailLookupCount === 0 ? (
                  <FiCheck size={17} />
                ) : (
                  <FiSearch size={17} />
                )}
              </SelActionIcon>
              <SelActionText>
                <div className="t">Find emails</div>
                <div className="d">
                  {emailLookupCount === 0 ? (
                    "Emails for these contacts have already been sourced."
                  ) : (
                    <>
                      {emailLookupCount} sourced{" "}
                      {emailLookupCount === 1 ? "person has" : "people have"} no
                      address yet.
                      {emailJobRunning
                        ? " A lookup is already running; they join it."
                        : ""}
                    </>
                  )}
                </div>
              </SelActionText>
              {emailLookupCount === 0 ? (
                <Btn disabled>Nothing to find</Btn>
              ) : (
                <Btn onClick={onFindEmails}>
                  <FiSearch size={13} /> {emailJobRunning ? "Add" : "Find"}{" "}
                  {emailLookupCount}
                </Btn>
              )}
            </SelAction>
          )}
          <SelAction>
            <SelActionIcon>
              <FiDownload size={17} />
            </SelActionIcon>
            <SelActionText>
              <div className="t">Export to CSV</div>
              <div className="d">
                Download a spreadsheet of the selected {noun}: name, title,
                company, location, email and LinkedIn.
              </div>
            </SelActionText>
            <Btn onClick={onExportCsv} disabled={exportBusy}>
              <FiDownload size={13} /> {exportBusy ? "Preparing…" : "Download CSV"}
            </Btn>
          </SelAction>
        </SelActionList>
        <SelPaneFoot>
          {count < allCount && (
            <button type="button" onClick={onSelectAll}>
              Select all {allCount}
            </button>
          )}
          <button type="button" onClick={onClear}>
            Clear selection
          </button>
        </SelPaneFoot>
      </SelPaneInner>
    </SelPaneWrap>
  );
}

/* ───────────────── Empty state ───────────────── */
const EmptyWrap = styled.div`
  height: 100%;
  display: grid;
  place-items: center;
  padding: 60px 32px;
  text-align: center;
  h3 {
    font-family: ${C.fontDisplay};
    font-size: 26px;
    margin: 0 0 6px;
  }
  p {
    color: var(--orx-ink-muted);
    max-width: 360px;
    margin: 0 auto 18px;
    font-size: 13.5px;
  }
`;
/* Accent-tinted tile at the shared 96/24 page-level size — matches the
   Dashboard empty state (rebuilt on the --orx-* palette). */
const EmptyArt = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 24px;
  background: linear-gradient(
    155deg,
    var(--orx-accent-soft),
    var(--orx-surface-tint)
  );
  border: 1px solid var(--orx-line);
  display: grid;
  place-items: center;
  color: var(--orx-accent);
  margin: 0 auto 18px;
  box-shadow: var(--shadow-md);
  box-shadow: 0 8px 22px -10px
    color-mix(in srgb, var(--orx-accent) 35%, transparent);
`;

/* ───────────────── Drafts list (replaces ListPane on drafts tab) ───────────────── */
const DraftsPaneWrap = styled.div`
  background: var(--orx-surface);
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid var(--orx-line);
  grid-column: 1 / -1;
  @media (min-width: 901px) {
    /* fill the whole body when active */
  }
`;
const DraftRow = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr auto;
  gap: 12px;
  padding: 12px 18px;
  border-bottom: 1px solid var(--orx-line-soft);
  cursor: pointer;
  align-items: flex-start;
  transition: background 0.1s;
  &:hover {
    background: var(--orx-surface-tint);
  }
`;
const DraftSubject = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--orx-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const DraftRecipient = styled.div`
  font-size: 12.5px;
  color: var(--orx-ink-soft);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const DraftPreview = styled.div`
  font-size: 12.5px;
  color: var(--orx-ink-muted);
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  line-height: 1.4;
`;
const DraftTime = styled.div`
  font-size: 11.5px;
  color: var(--orx-ink-muted);
  white-space: nowrap;
  padding-top: 2px;
`;

// Skeleton shaped like <DraftRow> (avatar + subject/recipient/preview lines).
function SkeletonDraftRow() {
  return (
    <DraftRow as="div" style={{ cursor: "default" }}>
      <Skeleton width={36} height={36} circle />
      <div style={{ minWidth: 0 }}>
        <Skeleton width="60%" height={14} mb={6} />
        <Skeleton width="45%" height={12.5} mb={6} />
        <Skeleton width="92%" height={12.5} mb={5} />
        <Skeleton width="70%" height={12.5} />
      </div>
      <Skeleton width={44} height={12} />
    </DraftRow>
  );
}

function DraftsPane({ drafts, onResume, onDelete, loading }) {
  const draftSkelRef = useRef(null);
  const draftSkelCount = useFillCount(draftSkelRef, { min: 5, enabled: !!loading });
  // While the server outreach-state is still hydrating, show skeleton rows so
  // we never flash "No drafts" before the real list arrives.
  if (loading) {
    return (
      <DraftsPaneWrap>
        <ListHead>
          <ListCount>
            <Skeleton width={70} height={13} />
          </ListCount>
        </ListHead>
        <List ref={draftSkelRef}>
          {Array.from({ length: draftSkelCount }).map((_, i) => (
            <SkeletonDraftRow key={i} />
          ))}
        </List>
      </DraftsPaneWrap>
    );
  }
  if (drafts.length === 0) {
    return (
      <DraftsPaneWrap>
        <EmptyWrap>
          <div>
            <EmptyArt>
              <FiMail size={42} />
            </EmptyArt>
            <h3>No drafts</h3>
            <p>
              Start composing an email — when you cancel before sending, the
              draft saves here.
            </p>
          </div>
        </EmptyWrap>
      </DraftsPaneWrap>
    );
  }
  return (
    <DraftsPaneWrap>
      <ListHead>
        <ListCount>
          <b>{drafts.length}</b>
          <span> draft{drafts.length === 1 ? "" : "s"}</span>
        </ListCount>
      </ListHead>
      <List>
        <FadeIn>
          {drafts.map((d) => {
            const p = d.person || {};
            return (
              <DraftRow key={d.id} onClick={() => onResume(d)}>
                <Avatar person={p} />
                <div style={{ minWidth: 0 }}>
                  <DraftSubject>
                    {d.subject || (
                      <span
                        style={{
                          color: "var(--orx-ink-faint)",
                          fontWeight: 500,
                        }}
                      >
                        (no subject)
                      </span>
                    )}
                  </DraftSubject>
                  <DraftRecipient>
                    To {p.name}
                    {p.company && (
                      <>
                        <span style={{ color: "var(--orx-ink-faint)" }}>
                          {" "}
                          ·{" "}
                        </span>
                        {p.company}
                      </>
                    )}
                  </DraftRecipient>
                  {d.body && (
                    <DraftPreview>
                      {d.body
                        .replace(/<br\s*\/?>(?!\n)/gi, " ")
                        .replace(/<\/?[^>]+>/g, "")
                        .slice(0, 200)}
                    </DraftPreview>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 8,
                  }}
                >
                  <DraftTime>{timeAgo(d.updatedAt)}</DraftTime>
                  <IconBtn
                    $size={26}
                    title="Discard draft"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(d.id);
                    }}
                  >
                    <FiTrash2 size={12} />
                  </IconBtn>
                </div>
              </DraftRow>
            );
          })}
        </FadeIn>
      </List>
    </DraftsPaneWrap>
  );
}

/* ───────────────── Unseen-matches storage ─────────────────
 * Tracks candidates whose match completed while the user wasn't looking at
 * them, so a "Re-ranked" pill on the list row and a "missed update" strip
 * on profile open can prompt them to check.
 *
 * Storage: localStorage["outreach:unseenMatches"] → JSON array of
 *   { personId, kind, matchedAt } entries. 7-day expiry on read.
 *
 * Why localStorage: it's device-local UI state, not data. Loss is harmless
 * (the actual match results live in the DB). No PII, no auth — just opaque
 * IDs and timestamps. Same category as "channels with unread messages" in
 * Slack / Discord. */
const UNSEEN_MATCHES_KEY = "outreach:unseenMatches";
const UNSEEN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function readUnseenMatches() {
  try {
    const raw = localStorage.getItem(UNSEEN_MATCHES_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const now = Date.now();
    return arr.filter(
      (e) =>
        e &&
        typeof e.personId === "string" &&
        typeof e.matchedAt === "number" &&
        now - e.matchedAt < UNSEEN_TTL_MS,
    );
  } catch {
    return [];
  }
}

function writeUnseenMatches(arr) {
  try {
    localStorage.setItem(UNSEEN_MATCHES_KEY, JSON.stringify(arr));
  } catch {
    /* quota / private mode — fail silently, UI stays in-memory */
  }
}

/* ───────────────── Main page ───────────────── */
/** Starting filter state for each tab. Tabs hold their own copy. */
const DEFAULT_LIST_FILTERS = {
  query: "",
  statusFilter: "all",
  companyFilter: "all",
  countryFilter: "all",
  seniorityFilter: "all",
  jobFilter: "all",
  sortBy: "name",
};

// userId → { candidates, external }: the Candidates tab's lists, kept for the session.
const candidateListCache = new Map();

export default function Outreach() {
  const navigate = useNavigate();
  const { user, isAuthenticated, features: featureFlags } = useAuth();
  // Adding candidates is parked (FEATURE_ADD_CANDIDATES): the Add button stays hidden while it is off.
  const addCandidatesOn = !!featureFlags?.addCandidates;
  const [searchParams, setSearchParams] = useSearchParams();
  const appTheme = useTheme();
  // Constant now that these are aliases — the values they point at are what
  // change with the theme, so there's nothing per-mode left to recompute.
  const paletteVars = OUTREACH_PALETTE_VARS;

  // Mirror the palette onto :root so portalled children (DropMenu, modal)
  // can resolve the same vars. Removed on unmount / leaving the page.
  useEffect(() => {
    const root = document.documentElement;
    const keys = Object.keys(paletteVars);
    keys.forEach((k) => root.style.setProperty(k, paletteVars[k]));
    return () => keys.forEach((k) => root.style.removeProperty(k));
  }, [paletteVars]);
  const { showToast } = useToast();

  // tabs / view
  const [tab, setTab] = useState("candidates");

  // data
  // Last lists seen this session: a revisit paints them at once and refreshes quietly.
  const cachedLists = candidateListCache.get(user?.sub);
  const [candidates, setCandidates] = useState(() => cachedLists?.candidates || []);
  const [externalCandidates, setExternalCandidates] = useState(() => cachedLists?.external || []);
  const [externalLoading, setExternalLoading] = useState(() => !cachedLists?.external);
  const [clients, setClients] = useState([]);
  const [clientsTotal, setClientsTotal] = useState(null);
  // Constant DB-wide client count for the tab badge. clientsTotal tracks
  // the active company/country filter; this one never does.
  const [clientsGrandTotal, setClientsGrandTotal] = useState(null);
  const [clientsLoadingMore, setClientsLoadingMore] = useState(false);
  /** Full company / location lists for the filter dropdowns, fetched once
   * from /api/people/filter-options. Without these the dropdowns can only
   * show values whose clients have already paged in. */
  const [allCompanies, setAllCompanies] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(() => !cachedLists?.candidates);
  const [candidatesTotal, setCandidatesTotal] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(false);

  // filters / sort — held per tab. Candidates and clients are different
  // populations, so a company or country narrowed on one tab must not follow
  // the user to the other; each tab also keeps its own selection on return.
  // `seniorityFilter` / `jobFilter` only render on candidates, and `jobFilter`
  // is additionally gated on tab === "candidates" where it is applied.
  const [filtersByTab, setFiltersByTab] = useState(() => ({
    candidates: { ...DEFAULT_LIST_FILTERS },
    clients: { ...DEFAULT_LIST_FILTERS },
    saved: { ...DEFAULT_LIST_FILTERS },
  }));

  /** Write one filter on a named tab. Takes the tab explicitly so callers that
   *  switch tabs in the same pass (the ?tab=&company= deep link) can target the
   *  destination rather than the tab being navigated away from. */
  const setFilterFor = useCallback((tabKey, key, value) => {
    setFiltersByTab((prev) => {
      const current = prev[tabKey] || DEFAULT_LIST_FILTERS;
      const next = typeof value === "function" ? value(current[key]) : value;
      if (next === current[key]) return prev;
      return { ...prev, [tabKey]: { ...current, [key]: next } };
    });
  }, []);

  const activeFilters = filtersByTab[tab] || DEFAULT_LIST_FILTERS;
  const {
    query,
    statusFilter,
    companyFilter,
    countryFilter,
    seniorityFilter,
    jobFilter,
    sortBy,
  } = activeFilters;

  const setQuery = useCallback((v) => setFilterFor(tab, "query", v), [tab, setFilterFor]);
  const setStatusFilter = useCallback((v) => setFilterFor(tab, "statusFilter", v), [tab, setFilterFor]);
  const setCompanyFilter = useCallback((v) => setFilterFor(tab, "companyFilter", v), [tab, setFilterFor]);
  const setCountryFilter = useCallback((v) => setFilterFor(tab, "countryFilter", v), [tab, setFilterFor]);
  const setSeniorityFilter = useCallback((v) => setFilterFor(tab, "seniorityFilter", v), [tab, setFilterFor]);
  const setJobFilter = useCallback((v) => setFilterFor(tab, "jobFilter", v), [tab, setFilterFor]);
  const setSortBy = useCallback((v) => setFilterFor(tab, "sortBy", v), [tab, setFilterFor]);

  // [{ jobId, candidateId, title, company, source }] from /api/outreach/candidate-jobs.
  const [candidateJobLinks, setCandidateJobLinks] = useState([]);
  // Saved tab: main filter picking which kind to view. Drives which sub-filters
  // appear (candidate vs client). Only consulted when tab === "saved".
  const [savedKind, setSavedKind] = useState("candidates");
  // Switching kind resets the sub-filters that don't apply to the other kind,
  // so a stale candidate-only filter can't silently hide every client.
  const changeSavedKind = useCallback(
    (k) => {
      setSavedKind(k);
      ["companyFilter", "seniorityFilter", "jobFilter"].forEach(
        (key) => setFilterFor("saved", key, "all"),
      );
    },
    [setFilterFor],
  );

  // selection / open
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [openId, setOpenId] = useState(null);
  // Person fetched via deep-link (?personId=). Survives loader overwrites so
  // the detail pane resolves even when the target isn't on the loaded page
  // or in the active tab's filter.
  const [pinnedPerson, setPinnedPerson] = useState(null);
  // Keep openIdRef in sync so the bulk-match success handler can decide
  // which candidate is *currently being viewed* without re-reading state.
  useEffect(() => {
    openIdRef.current = openId;
  }, [openId]);
  const [addOpen, setAddOpen] = useState(false);
  /* ─── Multi-tab right column ───
   * When composerOpen is true and a profile is also open, the right column
   * renders a tab strip with two tabs: "Compose (N)" and "{personName}".
   * Both panels stay mounted side-by-side (visibility toggled with display:none)
   * so the composer's in-memory state (drafts being typed, attachments,
   * AI shimmer, autosave debounce) survives tab switches.
   *
   * Trigger semantics:
   *   - Click "Send email" / "Draft with AI" on a person → ADD them to the
   *     composer's existing recipients (deduped by id) and switch active
   *     tab to "compose". Opens a fresh composer if it wasn't already.
   *   - Click on a person in the list → updates openId for the profile
   *     tab and switches active tab to "profile" (composer stays alive
   *     behind it).
   *   - Closing the composer (X on the compose tab) → composerOpen=false,
   *     tab strip disappears, right column reverts to single-detail. */
  const [composerOpen, setComposerOpen] = useState(false);
  const [activeRightTab, setActiveRightTab] = useState("profile"); // "profile" | "compose"
  const [composerAiId, setComposerAiId] = useState(null);
  /** When opening composer from a draft snapshot (or another out-of-list source),
   * carry the recipients directly so we don't depend on the current tab list. */
  const [composerOverride, setComposerOverride] = useState(null); // person[] | null
  /** Resume a specific draft by its id when entering the composer. Map of
   * personId → draftId. Empty by default → composer generates fresh ids. */
  const [composerResumeIds, setComposerResumeIds] = useState({}); // { [personId]: draftId }

  // status / saved / drafts / templates — server-backed, per-user, in the
  // tenant client DB. Templates are owned at the page level so they
  // survive opening/closing the composer multiple times within a session and
  // so the initial load can't race with a Save (which used to wipe state).
  const [statusMap, setStatusMap] = useState({});
  const [savedMap, setSavedMap] = useState({});
  const [draftsMap, setDraftsMap] = useState({});
  const [templates, setTemplates] = useState([]);
  const [stateLoaded, setStateLoaded] = useState(false);

  // Deep-link entry points. Other pages (Research, person tiles) navigate here
  // with these params instead of opening their own compose modal:
  //   ?tab=candidates|clients → switches the active tab
  //   ?company=<name>            → pre-applies the Company filter
  //   ?personId=<id>             → opens that person's profile in the detail pane
  //
  // For ?personId we also fetch the person from /api/people/:id and prepend
  // them to the active tab's list — that way the detail pane finds them even
  // when they're outside the loaded page, and Outreach's filters never hide
  // the deep-linked target.
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    const urlCompany = searchParams.get("company");
    const urlPersonId = searchParams.get("personId");
    if (!urlTab && !urlCompany && !urlPersonId) return;

    const targetTab =
      urlTab === "candidates" || urlTab === "clients" ? urlTab : null;
    if (targetTab) setTab(targetTab);
    // Write onto the destination tab explicitly — `tab` still holds the old
    // value in this pass, so the active-tab setter would filter the wrong list.
    if (urlCompany) setFilterFor(targetTab || tab, "companyFilter", urlCompany);

    let cancelled = false;
    if (urlPersonId) {
      setOpenId(urlPersonId);
      // Server-side fetch — store in `pinnedPerson` so the detail pane can
      // resolve immediately, independent of the paginated list loaders.
      // `cancelled` guards against a stale resolve landing on top of a
      // newer deep-link (e.g. Research → Outreach → back → forward).
      (async () => {
        try {
          const res = await fetchWithAuth(
            `/api/people/${encodeURIComponent(urlPersonId)}`,
          );
          if (cancelled || !res.ok) return;
          const raw = await res.json();
          if (cancelled) return;
          const kind = targetTab === "clients" ? "client" : "candidate";
          setPinnedPerson(normalizePerson(raw, kind));
        } catch (err) {
          if (cancelled) return;
          console.warn("Outreach deep-link person fetch failed:", err);
        }
      })();
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        next.delete("company");
        next.delete("personId");
        return next;
      },
      { replace: true },
    );

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [server, states] = await Promise.all([
          getOutreachState(),
          getCandidateState(),
        ]);
        if (cancelled) return;
        // Statuses live in candidate_state — one row per candidate. The
        // encrypted blob still owns saved/drafts/templates/todos.
        setStatusMap(states);
        setSavedMap(server.saved || {});
        setDraftsMap(server.drafts || {});
        setTemplates(Array.isArray(server.templates) ? server.templates : []);
        setStateLoaded(true);
      } catch (err) {
        // Leave stateLoaded=false so pushState() short-circuits and we never
        // overwrite the server blob with an empty baseline derived from a
        // failed load. User must refresh once the underlying issue clears.
        if (cancelled) return;
        console.warn("outreach-state load failed", err);
        showToast(
          "Couldn't load your saved outreach. Refresh to try again — changes won't save until this loads.",
          { duration: 8000 },
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  /** Fire-and-forget background sync. Server replaces whatever sub-map(s)
   * we send; omitted top-level keys are preserved. */
  function pushState(patch) {
    if (!stateLoaded) return; // don't write back during initial hydration
    patchOutreachState(patch).catch((err) => {
      console.warn("outreach-state save failed", err);
    });
  }

  function toggleSaved(person) {
    setSavedMap((m) => {
      const next = { ...m };
      if (next[person.id]) {
        delete next[person.id];
      } else {
        next[person.id] = snapshotPerson(person);
      }
      pushState({ saved: next });
      return next;
    });
  }
  /** Remove a single draft by its unique draftId. */
  function removeDraft(draftId) {
    setDraftsMap((m) => {
      const next = { ...m };
      delete next[draftId];
      pushState({ drafts: next });
      return next;
    });
  }

  /** Save a new template (or update an existing one by id) into the
   * page-level templates list + persist to outreach-state. Returns true on
   * success, false on failure — composer uses this to roll back its UI. */
  async function saveTemplate(template) {
    if (!stateLoaded || !template?.id) return false;
    const next = [...templates.filter((t) => t.id !== template.id), template];
    setTemplates(next);
    try {
      await patchOutreachState({ templates: next });
      return true;
    } catch (err) {
      console.warn("templates save failed", err);
      setTemplates(templates); // roll back
      return false;
    }
  }

  /** Delete a template by id. */
  async function removeTemplate(id) {
    if (!stateLoaded) return false;
    const next = templates.filter((t) => t.id !== id);
    const previous = templates;
    setTemplates(next);
    try {
      await patchOutreachState({ templates: next });
      return true;
    } catch (err) {
      console.warn("templates delete failed", err);
      setTemplates(previous);
      return false;
    }
  }
  /** Persist or update draft snapshots from composer. Each recipient has a
   * stable draftId (so the same person can have multiple drafts). Empties
   * and just-sent drafts are dropped. */
  function persistDrafts(
    personSnapshots,
    draftIdsByPersonId,
    draftsByPersonId,
  ) {
    setDraftsMap((m) => {
      const next = { ...m };
      personSnapshots.forEach((p) => {
        const draftId = draftIdsByPersonId[p.id];
        if (!draftId) return;
        const d = draftsByPersonId[p.id] || {};
        const hasContent = (d.subject || "").trim() || (d.body || "").trim();
        if (d.sent || !hasContent) {
          delete next[draftId];
        } else {
          next[draftId] = {
            id: draftId,
            person: snapshotPerson(p),
            subject: d.subject || "",
            body: d.body || "",
            isHtml: !!d.isHtml,
            personalized: !!d.personalized,
            updatedAt: Date.now(),
          };
        }
      });
      pushState({ drafts: next });
      return next;
    });
  }

  // mail status / history
  const [mailStatus, setMailStatus] = useState(null);
  const [history, setHistory] = useState([]);
  // Tracks the initial outreach-history fetch so the detail-pane timeline can
  // show skeletons instead of an empty "No outreach yet" flash before load.
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Resolved display name for the signed-in user, used by the AI
  // personalize endpoint as the email signature. Sourced from the
  // encrypted user_settings (set via Settings → Your name or the
  // onboarding wizard). If empty, the server-side AI endpoint derives a
  // fallback from the email local part — no extra Auth0 round-trip
  // needed on every page load.
  const [displayName, setDisplayName] = useState("");
  // Outreach signatures live in encrypted user_settings. If the user has
  // saved a signature we use its HTML; otherwise we fall back to a simple
  // "Best regards,\n<Full name>" footer. Either way, this string is what
  // the composer appends to every outgoing email body — the AI prompt is
  // told NOT to generate its own closing.
  const [defaultSignatureHtml, setDefaultSignatureHtml] = useState("");
  // Reusable AI prompts from Settings → AI Prompts. The composer offers them
  // in a picker and sends the selected one to the AI as the email brief.
  const [aiPrompts, setAiPrompts] = useState([]);
  useEffect(() => {
    if (!isAuthenticated || !user?.sub) return;
    let cancelled = false;
    (async () => {
      try {
        const settings = await getUserSettings(user.sub);
        if (cancelled) return;
        const first = String(settings?.firstName || "").trim();
        const last = String(settings?.lastName || "").trim();
        const fullName = [first, last].filter(Boolean).join(" ");
        setDisplayName(fullName);
        setAiPrompts(
          Array.isArray(settings?.aiPrompts) ? settings.aiPrompts : [],
        );
        const sigs = Array.isArray(settings?.signatures)
          ? settings.signatures
          : [];
        const def = sigs.find((s) => s.isDefault) || sigs[0] || null;
        if (def?.body) {
          setDefaultSignatureHtml(normalizeSignatureHtml(def.body));
        } else {
          // Fallback footer when no signature is saved. Uses the user's
          // saved name; if blank, falls back to Auth0's name claim, then
          // to the email local-part. Keep this in sync with the server
          // fallback in /api/ai/email-template.
          const fallbackName =
            fullName ||
            String(user?.name || "").trim() ||
            (String(user?.email || "").split("@")[0] || "")
              .split(/[._-]+/)
              .filter(Boolean)
              .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
              .join(" ");
          setDefaultSignatureHtml(
            `Best regards,<br>${escapeHtml(fallbackName || "")}`,
          );
        }
      } catch (err) {
        if (cancelled) return;
        console.warn("Failed to load user_settings for display name", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.sub]);

  const loadMail = useCallback(async () => {
    try {
      setMailStatus(await getMailStatus());
    } catch {
      setMailStatus({ connected: false });
    }
  }, []);
  const loadHistory = useCallback(async () => {
    try {
      const data = await getOutreachHistory({ page: 1, pageSize: 100 });
      setHistory(data.items || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoaded(true);
    }
  }, []);

  // load candidates — auto-fetches all pages (100/page) and streams each page
  // into state so the first results appear immediately while the rest load.
  // Own candidates, all pages (100 each) in one go, streamed into state as they land.
  // Rows share one data key since 2026-09-08, so a full load is one KMS call — cheap
  // enough that search and filters can simply see everyone.
  const loadCandidates = useCallback(async () => {
    if (!isAuthenticated || !user?.sub) return;
    // With a cached list on screen the refresh is silent.
    if (!candidateListCache.get(user.sub)?.candidates) setCandidatesLoading(true);
    const PAGE_SIZE = 100;
    const all = [];
    try {
      let skip = 0;
      let hasMore = true;
      while (hasMore) {
        const res = await fetchWithAuth(
          `/api/user-candidates/${encodeURIComponent(user.sub)}?limit=${PAGE_SIZE}&skip=${skip}`,
        );
        if (!res.ok) throw new Error("Failed to load");
        const data = await res.json();
        const page = Array.isArray(data) ? data : data.candidates || [];
        all.push(
          ...page.map((p) => normalizePerson(p, "candidate", "internal")),
        );
        setCandidates([...all]);
        if (typeof data.total === "number") setCandidatesTotal(data.total);
        // The first page decides the screen (list or empty state); later pages append.
        setCandidatesLoading(false);
        hasMore = data.hasMore === true;
        skip += PAGE_SIZE;
      }
    } catch (err) {
      console.warn("candidates load failed", err);
      if (all.length > 0) setCandidates([...all]);
    } finally {
      setCandidatesLoading(false);
    }
  }, [isAuthenticated, user?.sub]);

  // Sourced ("external") candidates from Find Candidates, flattened across all
  // of this user's searches. Loaded once and merged into the Candidates tab.
  // `silent` refreshes the rows in place without the loading skeleton.
  const loadExternalCandidates = useCallback(async ({ silent = false } = {}) => {
    if (!isAuthenticated || !user?.sub) return;
    if (!silent && !candidateListCache.get(user.sub)?.external) setExternalLoading(true);
    try {
      const res = await fetchWithAuth("/api/outreach/external-candidates");
      if (!res.ok) throw new Error("Failed to load");
      const data = await res.json();
      const arr = Array.isArray(data) ? data : data.candidates || [];
      setExternalCandidates(
        arr.map((p) => normalizePerson(p, "candidate", "external")),
      );
    } catch (err) {
      console.warn("external candidates load failed", err);
    } finally {
      if (!silent) setExternalLoading(false);
    }
  }, [isAuthenticated, user?.sub]);

  // After an email lookup settles, square the sourced list with the server without a
  // visible reload: in place while the candidates tab is open, else deferred to the
  // next time that tab is opened.
  const tabRef = useRef(tab);
  useEffect(() => { tabRef.current = tab; }, [tab]);
  const externalStaleRef = useRef(false);
  const settleExternalList = useCallback(() => {
    if (tabRef.current === "candidates") loadExternalCandidates({ silent: true });
    else externalStaleRef.current = true;
  }, [loadExternalCandidates]);
  useEffect(() => {
    if (tab !== "candidates" || !externalStaleRef.current) return;
    externalStaleRef.current = false;
    loadExternalCandidates({ silent: true });
  }, [tab, loadExternalCandidates]);

  // Patch a freshly-found address into the sourced list. Updating state rather
  // than refetching keeps the open profile from flickering, and the send flow
  // reads `email` off this same list.
  const handleEmailResolved = useCallback((personId, { email, found }) => {
    const apply = (p) =>
      p.id === personId
        ? found
          ? {
              ...p,
              email,
              emails: [...new Set([email, ...(p.emails || [])])],
            }
          // A settled miss, so the action retires immediately rather than
          // waiting for the next load of the sourced list.
          : { ...p, emailSearched: true }
        : p;
    setExternalCandidates((prev) => prev.map(apply));
    // The composer holds its own copy of each recipient; fill the To line there too.
    setComposerOverride((prev) => (prev ? prev.map(apply) : prev));
  }, []);

  // Job↔candidate links, for the candidates-tab "Job" filter. Best-effort: on
  // failure the filter simply doesn't appear rather than blocking the list.
  const loadCandidateJobLinks = useCallback(async () => {
    if (!isAuthenticated || !user?.sub) return;
    try {
      const res = await fetchWithAuth("/api/outreach/candidate-jobs");
      if (!res.ok) throw new Error("Failed to load");
      const data = await res.json();
      setCandidateJobLinks(Array.isArray(data?.links) ? data.links : []);
    } catch (err) {
      console.warn("candidate job links load failed", err);
      setCandidateJobLinks([]);
    }
  }, [isAuthenticated, user?.sub]);

  // load clients
const CLIENTS_PAGE_SIZE = 20;
  /** Server-side sort key — /api/people only supports name / company.
   * Clients have no match score, so anything else falls back to name. */
  // Read the clients tab's own filters: this loader runs independently of
  // which tab is displayed, so keying off the active tab would refetch
  // clients whenever the candidates filters changed.
  const clientFilters = filtersByTab.clients || DEFAULT_LIST_FILTERS;
  const clientsSortKey =
    clientFilters.sortBy === "company" ? "company" : "name";
  /** Server-side company / country filters for clients (selected from
   * the full options list, not the loaded subset). */
  const clientsCompanyParam =
    clientFilters.companyFilter && clientFilters.companyFilter !== "all"
      ? `&company=${encodeURIComponent(clientFilters.companyFilter)}`
      : "";
  const clientsCountryParam =
    clientFilters.countryFilter && clientFilters.countryFilter !== "all"
      ? `&country=${encodeURIComponent(clientFilters.countryFilter)}`
      : "";

  const loadClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const res = await fetchWithAuth(
        `/api/people?limit=${CLIENTS_PAGE_SIZE}&offset=0&clientsOnly=1&sort=${clientsSortKey}${clientsCompanyParam}${clientsCountryParam}`,
      );
      if (!res.ok) throw new Error("Failed to load");
      const data = await res.json();
      const arr = data.people || [];
      setClients(arr.map((p) => normalizePerson(p, "client")));
      setClientsTotal(typeof data.total === "number" ? data.total : null);
    } catch (err) {
      console.warn("clients load failed", err);
    } finally {
      setClientsLoading(false);
    }
  }, [clientsSortKey, clientsCompanyParam, clientsCountryParam]);

  /** Append the next page of clients. Guarded against concurrent calls
   * and against running past the known total. */
  const loadMoreClients = useCallback(async () => {
    if (clientsLoading || clientsLoadingMore) return;
    if (clientsTotal != null && clients.length >= clientsTotal) return;
    setClientsLoadingMore(true);
    try {
      const res = await fetchWithAuth(
        `/api/people?limit=${CLIENTS_PAGE_SIZE}&offset=${clients.length}&clientsOnly=1&sort=${clientsSortKey}${clientsCompanyParam}${clientsCountryParam}`,
      );
      if (!res.ok) throw new Error("Failed to load more");
      const data = await res.json();
      const arr = data.people || [];
      setClients((prev) => [
        ...prev,
        ...arr.map((p) => normalizePerson(p, "client")),
      ]);
      if (typeof data.total === "number") setClientsTotal(data.total);
    } catch (err) {
      console.warn("clients loadMore failed", err);
    } finally {
      setClientsLoadingMore(false);
    }
  }, [
    clients.length,
    clientsTotal,
    clientsLoading,
    clientsLoadingMore,
    clientsSortKey,
    clientsCompanyParam,
    clientsCountryParam,
  ]);

  useEffect(() => {
    loadMail();
    loadHistory();
  }, [loadMail, loadHistory]);
  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);
  useEffect(() => {
    if (!user?.sub || candidatesLoading || externalLoading) return;
    candidateListCache.set(user.sub, { candidates, external: externalCandidates });
  }, [user?.sub, candidates, externalCandidates, candidatesLoading, externalLoading]);
  useEffect(() => {
    loadExternalCandidates();
  }, [loadExternalCandidates]);
  useEffect(() => {
    loadCandidateJobLinks();
  }, [loadCandidateJobLinks]);
  useEffect(() => {
    loadClients();
  }, [loadClients]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth("/api/people/filter-options");
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setAllCompanies(
          Array.isArray(data.companies) ? data.companies.filter(Boolean) : [],
        );
        setAllCountries(
          Array.isArray(data.countries)
            ? data.countries.filter(Boolean).sort((a, b) => a.localeCompare(b))
            : [],
        );
      } catch (err) {
        console.warn("filter-options load failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Filterless client total for the tab badge — deliberately omits the
  // company/country params so the badge shows the constant DB count.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth(
          "/api/people?limit=1&offset=0&clientsOnly=1",
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && typeof data.total === "number")
          setClientsGrandTotal(data.total);
      } catch (err) {
        console.warn("clients total load failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // saved + drafts lists derived from local storage maps
  const savedList = useMemo(() => {
    const byId = new Map();
    [...candidates, ...clients].forEach((p) => byId.set(p.id, p));
    return Object.entries(savedMap)
      .map(([id, snap]) => byId.get(id) || (snap && { ...snap }))
      .filter(Boolean);
  }, [candidates, clients, savedMap]);
  const draftsList = useMemo(() => {
    // Fall back to the map key as `id` so list rendering / open / delete
    // always have a stable handle.
    return Object.entries(draftsMap)
      .map(([id, d]) => ({ ...d, id: d?.id || id }))
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  }, [draftsMap]);

  // Distinct jobs that have at least one candidate attached, for the "Job"
  // filter dropdown; and candidateId -> [jobId] for applying it.
  const jobOptions = useMemo(() => {
    const map = new Map(); // jobId -> { jobId, title, company }
    for (const l of candidateJobLinks) {
      if (!l?.jobId || map.has(l.jobId)) continue;
      map.set(l.jobId, {
        jobId: l.jobId,
        title: l.title || "Untitled role",
        company: l.company || "",
      });
    }
    return [...map.values()].sort((a, b) => a.title.localeCompare(b.title));
  }, [candidateJobLinks]);

  // Full links per candidate. Beyond the Job filter's needs, this is what lets
  // the detail pane draw the Assigned jobs rows the instant a person is opened
  // — titles included — instead of waiting on a per-candidate request.
  const jobLinksByCandidate = useMemo(() => {
    const out = {};
    for (const l of candidateJobLinks) {
      if (!l?.candidateId || !l?.jobId) continue;
      (out[l.candidateId] = out[l.candidateId] || []).push(l);
    }
    return out;
  }, [candidateJobLinks]);

  const jobIdsByCandidate = useMemo(() => {
    const out = {};
    for (const [cid, links] of Object.entries(jobLinksByCandidate)) {
      out[cid] = links.map((l) => l.jobId);
    }
    return out;
  }, [jobLinksByCandidate]);

  // active list (people-shaped) — drafts tab uses its own renderer below
  // Candidates tab = internal (Add flow) + external (sourced) merged. Dedup by
  // LinkedIn URL, keeping the internal row when a person exists in both (the
  // user explicitly added that one).
  const mergedCandidates = useMemo(() => {
    const seen = new Set(
      candidates
        .map((p) => (p.linkedin || "").trim().toLowerCase())
        .filter(Boolean),
    );
    const externalDeduped = externalCandidates.filter((p) => {
      const key = (p.linkedin || "").trim().toLowerCase();
      return !key || !seen.has(key);
    });
    return [...candidates, ...externalDeduped];
  }, [candidates, externalCandidates]);

  const baseList =
    tab === "candidates"
      ? mergedCandidates
      : tab === "clients"
        ? clients
        : tab === "saved"
          ? savedList
          : [];
  // Prepend a deep-linked person (from ?personId=) if they belong on this tab
  // and aren't already in the loaded page. Survives loader overwrites.
  const list = useMemo(() => {
    if (!pinnedPerson) return baseList;
    if (tab !== pinnedPerson.kind + "s") return baseList; // wrong tab → ignore
    if (baseList.some((p) => p.id === pinnedPerson.id)) return baseList;
    return [pinnedPerson, ...baseList];
  }, [baseList, pinnedPerson, tab]);
  const loading =
    tab === "candidates"
      ? candidatesLoading || externalLoading
      : tab === "clients"
        ? clientsLoading
        : false;
  // Header/tab counts hold a skeleton until their source fully settles.
  const candidatesCountLoading = candidatesLoading || externalLoading;
  const clientsCountLoading = clientsLoading && clientsTotal == null;

  const openPerson =
    list.find((p) => p.id === openId) ||
    (pinnedPerson?.id === openId ? pinnedPerson : null);

  /* The candidates list carries slim rows (no resume body or sections). Opening one of
     your own candidates fetches the full record once and swaps it into the list. */
  const detailFetchedRef = useRef(new Set());
  useEffect(() => {
    const p = openPerson;
    if (!p || p.origin !== "internal" || !p.raw?.summaryOnly) return;
    if (!user?.sub || detailFetchedRef.current.has(p.id)) return;
    detailFetchedRef.current.add(p.id);
    (async () => {
      try {
        const res = await fetchWithAuth(
          `/api/user-candidates/${encodeURIComponent(user.sub)}/${encodeURIComponent(p.id)}`,
        );
        if (!res.ok) throw new Error("Failed to load candidate");
        const data = await res.json();
        const full = normalizePerson(data.candidate || {}, "candidate", "internal");
        setCandidates((prev) => prev.map((c) => (c.id === p.id ? { ...c, ...full } : c)));
      } catch (err) {
        detailFetchedRef.current.delete(p.id);
        console.warn("candidate detail load failed", err);
      }
    })();
  }, [openPerson, user?.sub]);

  // Composer recipients always come from composerOverride now — every entry
  // path (startCompose, openDraft) sets it before flipping composerOpen,
  // and closeComposer clears it. Empty array when composer isn't open.
  const recipients = composerOverride || [];

  // visible-after-filters ids (for select-all)
  const visibleIds = useMemo(() => {
    let arr = list;
    if (query) {
      const q = query.toLowerCase();
      arr = arr.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.role.toLowerCase().includes(q) ||
          p.company.toLowerCase().includes(q) ||
          p.location.toLowerCase().includes(q) ||
          (p.country || "").toLowerCase().includes(q),
      );
    }
    if (statusFilter !== "all")
      arr = arr.filter(
        (p) =>
          rowStatusOf(statusMap, jobFilter, p).status ===
          statusFilter,
      );
    if (companyFilter !== "all")
      arr = arr.filter((p) => p.company === companyFilter);
    if (countryFilter !== "all")
      arr = arr.filter((p) => p.country === countryFilter);
    if (tab === "candidates" && jobFilter !== "all")
      arr = arr.filter((p) => (jobIdsByCandidate[p.id] || []).includes(jobFilter));
    return arr.map((p) => p.id);
  }, [
    list,
    tab,
    query,
    statusFilter,
    companyFilter,
    countryFilter,
    jobFilter,
    jobIdsByCandidate,
    statusMap,
  ]);

  // Ticking a row hands the right column to the selection pane; clicking a
  // row name hands it back to that person's profile until the next tick.
  const [selectionFocus, setSelectionFocus] = useState(true);
  function toggleId(id) {
    setSelectionFocus(true);
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function clearSelection() {
    setSelectedIds(new Set());
  }
  function selectAllVisible() {
    setSelectionFocus(true);
    setSelectedIds(new Set(visibleIds));
  }

  /* ─── Bulk match: a queued job; progress shows in the activity tray ───
   * The running record (jobId + startedAt + matchedIds) is kept in localStorage so a
   * reload reattaches and can still badge the matched candidates as unseen. */
  const [bulkMatchRunning, setBulkMatchRunning] = useState(false);
  // Bulk "Job match" pool picker (all/yours/scoutm8).
  const [showBulkMatchMenu, setShowBulkMatchMenu] = useState(false);
  const bulkMatchBtnRef = useRef(null);
  const BULK_MATCH_LS_KEY = "outreach:bulkMatchActive";

  /* ─── Unseen-match markers (badges on rows + "missed update" strip) ─── */
  const [unseenMatches, setUnseenMatches] = useState(() => {
    // Hydrate from localStorage with TTL housekeeping. Write the filtered
    // list back so stale entries don't accumulate across reloads.
    const arr = readUnseenMatches();
    writeUnseenMatches(arr);
    return new Set(arr.map((e) => e.personId));
  });

  const addUnseenMatch = useCallback((personId, kind = "candidate") => {
    if (!personId) return;
    const arr = readUnseenMatches().filter((e) => e.personId !== personId);
    arr.push({ personId, kind, matchedAt: Date.now() });
    writeUnseenMatches(arr);
    setUnseenMatches((prev) => {
      if (prev.has(personId)) return prev;
      const next = new Set(prev);
      next.add(personId);
      return next;
    });
  }, []);

  const addUnseenMatchesMany = useCallback((personIds, kind = "candidate") => {
    const ids = (personIds || []).filter(Boolean);
    if (ids.length === 0) return;
    const now = Date.now();
    const existing = readUnseenMatches();
    const keep = existing.filter((e) => !ids.includes(e.personId));
    const merged = keep.concat(
      ids.map((personId) => ({ personId, kind, matchedAt: now })),
    );
    writeUnseenMatches(merged);
    setUnseenMatches((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const clearUnseenMatch = useCallback((personId) => {
    if (!personId) return;
    const arr = readUnseenMatches().filter((e) => e.personId !== personId);
    writeUnseenMatches(arr);
    setUnseenMatches((prev) => {
      if (!prev.has(personId)) return prev;
      const next = new Set(prev);
      next.delete(personId);
      return next;
    });
  }, []);

  // Live ref of the candidate id the user is currently viewing in the
  // detail pane. We don't want to slap a "Re-ranked" badge on the profile
  // they're staring at — they'll see the change in real time. Updated by
  // the openId effect below.
  const openIdRef = useRef(null);

  // Watch a queued bulk match to its end, then badge the re-ranked candidates.
  const watchBulkMatch = useCallback(
    async (jobId, startedAt, matchedIds) => {
      setBulkMatchRunning(true);
      try {
        const job = await waitForMatchJob(jobId, startedAt);
        if (job.status === "failed") {
          showToast(job.error || "Job matching failed.");
          return;
        }
        // Badge every matched candidate as unseen, EXCEPT the one the user is
        // looking at — they see the re-ranking happen live on the detail pane.
        const openId = openIdRef.current;
        addUnseenMatchesMany(
          (matchedIds || []).filter((id) => id !== openId),
          "candidate",
        );
        loadCandidates();
      } catch (err) {
        showToast(err?.message || "Job matching failed.");
      } finally {
        try {
          localStorage.removeItem(BULK_MATCH_LS_KEY);
        } catch {}
        setBulkMatchRunning(false);
      }
    },
    [loadCandidates, addUnseenMatchesMany, showToast],
  );

  const runBulkMatch = useCallback(
    async (scope = "all") => {
      if (bulkMatchRunning) return;
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      // Tag each selected id with its origin so the matcher reads the right store
      // (internal → user_candidates, external → outreach_external_shortlist).
      const originById = new Map(mergedCandidates.map((p) => [p.id, p.origin]));
      const tagged = ids.map((id) => ({
        id,
        origin: originById.get(id) === "external" ? "external" : "internal",
      }));
      const startedAt = Date.now();
      setBulkMatchRunning(true);
      // The selection bar's job is done once the action fires.
      setSelectedIds(new Set());
      try {
        const jobId = await startMatchJob(tagged, scope);
        try {
          localStorage.setItem(
            BULK_MATCH_LS_KEY,
            JSON.stringify({ jobId, startedAt, matchedIds: ids }),
          );
        } catch {}
        watchBulkMatch(jobId, startedAt, ids);
      } catch (err) {
        setBulkMatchRunning(false);
        showToast(err?.message || "Couldn't start the job matching.");
      }
    },
    [bulkMatchRunning, selectedIds, mergedCandidates, showToast, watchBulkMatch],
  );


  // On mount, check localStorage for a still-running job and resume polling.
  // Lets a tab reload (intentional or accidental) re-attach to a server job
  // that's still in flight.
  useEffect(() => {
    let saved = null;
    try {
      const raw = localStorage.getItem(BULK_MATCH_LS_KEY);
      saved = raw ? JSON.parse(raw) : null;
    } catch {}
    if (!saved?.jobId) return;
    // Older than the 5-min poll ceiling → server has either finished or
    // given up; just refresh the list and clear the record.
    if (Date.now() - Number(saved.startedAt) > MATCH_MAX_MS) {
      try {
        localStorage.removeItem(BULK_MATCH_LS_KEY);
      } catch {}
      loadCandidates();
      return;
    }
    watchBulkMatch(
      saved.jobId,
      Number(saved.startedAt),
      Array.isArray(saved.matchedIds) ? saved.matchedIds : [],
    );
    // Intentional one-shot on mount.
    // eslint-disable-next-line
  }, []);


  // beforeunload guard: while a bulk match is being watched, prompt before
  // tab close. The server job continues either way; this is about not
  // dropping the in-progress UI without an explicit confirmation.
  useEffect(() => {
    if (!bulkMatchRunning) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [bulkMatchRunning]);

  /* ─── Bulk email lookup: a queued job the client can leave and come back to ───
   * Shape of emailActivity:
   *   { phase: "running", jobId, total, processed, found, startedAt }
   *   { phase: "success", total, processed, found, missed, failed, stopReason, startedAt, finishedAt }
   *   { phase: "error",   total, error }
   * The server is the record: on mount the page asks for the latest job and
   * re-attaches if it is still running, so no localStorage is involved. */
  const [emailActivity, setEmailActivity] = useState(null);
  // Which job the poll loop is watching; bumping the token ends the current loop.
  const emailPollRef = useRef({ jobId: null, token: 0 });
  // Ids already patched into the list, so a poll never re-applies a result.
  const emailAppliedRef = useRef(new Set());
  // Ids still waiting on the job, so the profile pane can say "Searching…" for them.
  const [emailPendingIds, setEmailPendingIds] = useState(() => new Set());
  // Per-person caption after a lookup settled without a plain address: an error,
  // a skipped id, or a catch-all domain. Cleared when the person is looked up again.
  const [emailNotes, setEmailNotes] = useState(() => new Map());
  const emailJobRunning = emailActivity?.phase === "running";

  // Selected sourced people who still need an address and aren't already queued.
  const emailLookupIds = useMemo(() => {
    if (selectedIds.size === 0) return [];
    const byId = new Map(mergedCandidates.map((p) => [p.id, p]));
    return Array.from(selectedIds).filter((id) => {
      const p = byId.get(id);
      return (
        !!p && p.origin === "external" && !p.email && !p.emailSearched && !!p.linkedin &&
        !emailPendingIds.has(id)
      );
    });
  }, [selectedIds, mergedCandidates, emailPendingIds]);

  const applyEmailResults = useCallback(
    (results) => {
      const notes = [];
      for (const row of Array.isArray(results) ? results : []) {
        if (!row?.objectId || emailAppliedRef.current.has(row.objectId)) continue;
        emailAppliedRef.current.add(row.objectId);
        if (row.outcome === "found" || row.outcome === "missed") {
          handleEmailResolved(row.objectId, {
            email: row.outcome === "found" ? row.email || "" : "",
            found: row.outcome === "found",
          });
        } else if (row.outcome === "skipped" && row.error === "insufficient_credits") {
          notes.push([row.objectId, { text: "You're out of credits. Top up to find this email.", isError: true }]);
        } else {
          // Never show the raw server reason; the button stays so they can try again.
          notes.push([row.objectId, { text: "We couldn't check for an email just now. Please try again in a moment.", isError: false }]);
        }
      }
      if (notes.length)
        setEmailNotes((m) => {
          const next = new Map(m);
          for (const [id, note] of notes) next.set(id, note);
          return next;
        });
    },
    [handleEmailResolved],
  );

  const pollEmailJob = useCallback(
    async (jobId, startedAt) => {
      const token = ++emailPollRef.current.token;
      emailPollRef.current.jobId = jobId;
      const live = () => emailPollRef.current.token === token;
      const maxMs = 90 * 60 * 1000;
      while (Date.now() - startedAt < maxMs) {
        if (!live()) return;
        await new Promise((r) => setTimeout(r, 2500));
        if (!live()) return;
        let sb = {};
        try {
          const sr = await fetchWithAuth(
            `/api/outreach/external-candidates/find-emails/status?jobId=${encodeURIComponent(jobId)}`,
          );
          sb = await sr.json().catch(() => ({}));
        } catch (netErr) {
          console.warn("email lookup poll failed", netErr);
          continue;
        }
        if (!live()) return;
        applyEmailResults(sb.results);
        setEmailPendingIds(new Set(Array.isArray(sb.pendingIds) ? sb.pendingIds : []));
        if (sb.status === "succeeded") {
          emailPollRef.current.jobId = null;
          setEmailActivity({
            phase: "success",
            total: sb.total || 0,
            processed: sb.processed || 0,
            found: sb.found || 0,
            missed: sb.missed || 0,
            failed: sb.failed || 0,
            stopReason: sb.stopReason || "",
            startedAt,
            finishedAt: Date.now(),
          });
          creditsChanged();
          // The list is patched as results land; this squares it with the server quietly.
          settleExternalList();
          return;
        }
        if (sb.status === "failed") {
          emailPollRef.current.jobId = null;
          setEmailPendingIds(new Set());
          setEmailActivity({
            phase: "error",
            total: sb.total || 0,
            error: sb.error || "The email lookup stopped unexpectedly. Try again.",
          });
          creditsChanged();
          settleExternalList();
          return;
        }
        setEmailActivity((a) =>
          a && a.phase === "running"
            ? {
                ...a,
                total: sb.total || a.total,
                processed: sb.processed || 0,
                found: sb.found || 0,
              }
            : a,
        );
      }
      emailPollRef.current.jobId = null;
      setEmailActivity({
        phase: "error",
        total: 0,
        error: "The email lookup is taking longer than expected. Check back later.",
      });
    },
    [applyEmailResults, settleExternalList],
  );

  // Show the banner for a job and poll it, unless that same job is already being watched.
  const watchEmailJob = useCallback(
    (jobId, total, startedAt) => {
      if (emailPollRef.current.jobId === jobId) {
        setEmailActivity((a) => (a && a.phase === "running" ? { ...a, total: total || a.total } : a));
        return;
      }
      setEmailActivity({ phase: "running", jobId, total: total || 0, processed: 0, found: 0, startedAt });
      pollEmailJob(jobId, startedAt);
    },
    [pollEmailJob],
  );

  // One person's lookup from the profile pane: joins the live job at the front of the
  // queue (or starts one), so it shows in the banner and the Activities list.
  const requestEmailLookup = useCallback(
    async (personId) => {
      const startedAt = Date.now();
      setEmailPendingIds((ids) => new Set([...ids, personId]));
      setEmailNotes((m) => {
        if (!m.has(personId)) return m;
        const next = new Map(m);
        next.delete(personId);
        return next;
      });
      // Banner shows at once; watchEmailJob swaps in the real job id when the server answers.
      if (!emailJobRunning)
        setEmailActivity({ phase: "running", jobId: null, total: 1, processed: 0, found: 0, startedAt });
      try {
        const res = await fetchWithAuth(
          `/api/outreach/external-candidates/${encodeURIComponent(personId)}/find-email`,
          { method: "POST" },
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.jobId)
          throw new Error(data?.error || "Couldn't start the email lookup.");
        watchEmailJob(data.jobId, data.total, startedAt);
      } catch (err) {
        setEmailPendingIds((ids) => {
          const next = new Set(ids);
          next.delete(personId);
          return next;
        });
        // Drop the provisional banner; one attached to a real job stays.
        setEmailActivity((a) => (a && a.phase === "running" && !a.jobId ? null : a));
        setEmailNotes((m) => new Map(m).set(personId, { text: err?.message || "Couldn't start the email lookup.", isError: true }));
      }
    },
    [emailJobRunning, watchEmailJob],
  );

  // Selected people join the live job if one is running, otherwise start one.
  const runBulkEmailLookup = useCallback(async () => {
    const ids = emailLookupIds;
    if (ids.length === 0) return;
    const startedAt = Date.now();
    setEmailPendingIds((prev) => new Set([...prev, ...ids]));
    if (!emailJobRunning)
      setEmailActivity({ phase: "running", jobId: null, total: ids.length, processed: 0, found: 0, startedAt });
    setSelectedIds(new Set());
    try {
      const res = await fetchWithAuth("/api/outreach/external-candidates/find-emails", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ objectIds: ids }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.jobId) {
        throw new Error(data?.error || "Couldn't start the email lookup.");
      }
      watchEmailJob(data.jobId, data.total || ids.length, startedAt);
    } catch (err) {
      setEmailPendingIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
      showToast(err?.message || "Couldn't start the email lookup.");
      setEmailActivity({
        phase: "error",
        total: ids.length,
        error: err?.message || "Couldn't start the email lookup.",
      });
    }
  }, [emailJobRunning, emailLookupIds, watchEmailJob, showToast]);


  // On load, re-attach to a lookup that is still running: this is what makes
  // "start it, leave, come back" work across reloads, tabs and devices.
  useEffect(() => {
    if (!isAuthenticated || !user?.sub) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth("/api/outreach/external-candidates/find-emails/status");
        const sb = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || !sb?.exists) return;
        if (sb.status !== "queued" && sb.status !== "running") return;
        const startedAt = Date.parse(sb.startedAt || sb.createdAt || "") || Date.now();
        setEmailPendingIds(new Set(Array.isArray(sb.pendingIds) ? sb.pendingIds : []));
        setEmailActivity({
          phase: "running",
          jobId: sb.jobId,
          total: sb.total || 0,
          processed: sb.processed || 0,
          found: sb.found || 0,
          startedAt,
        });
        pollEmailJob(sb.jobId, startedAt);
      } catch (err) {
        console.warn("email lookup resume failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentional one-shot per signed-in user.
    // eslint-disable-next-line
  }, [isAuthenticated, user?.sub]);

  useEffect(() => () => {
    emailPollRef.current.token++;
  }, []);


  function openDraft(draft) {
    if (!draft || !draft.person) return;
    // Snapshots saved before email normalization may still hold an array.
    const emails = Array.isArray(draft.person.emails) && draft.person.emails.length
      ? draft.person.emails
      : emailList(draft.person.email);
    setComposerOverride([
      { ...draft.person, email: emailList(draft.person.email)[0] || emails[0] || "", emails },
    ]);
    // Carry this draft's id so the composer resumes it (rather than spawning
    // a new sibling draft alongside it on close).
    setComposerResumeIds({ [draft.person.id]: draft.id });
    setComposerAiId(null);
    setComposerOpen(true);
    setActiveRightTab("compose");
  }
  function startCompose(ids, opts = {}) {
    // Resolve the request into person objects from either the explicit id
    // list (detail-pane Send Email path) or the current bulk-selection
    // (selection toolbar Email path).
    let people = [];
    if (ids && ids.length > 0) {
      // Search both the active list AND pinnedPerson — when called from a
      // detail pane that's pinned via deep-link, the person may not be in
      // the currently-loaded list page.
      const pool = pinnedPerson ? [...list, pinnedPerson] : list;
      people = ids.map((id) => pool.find((p) => p.id === id)).filter(Boolean);
    } else if (selectedIds.size > 0) {
      people = list.filter((p) => selectedIds.has(p.id));
    }
    if (people.length === 0) return;

    // Clear the bulk selection the moment the action is taken — the
    // composer now owns these recipients, and the SelBar shouldn't linger.
    if (selectedIds.size > 0) clearSelection();

    if (composerOpen && Array.isArray(composerOverride)) {
      // Composer already open — ADD the new recipients to the existing
      // list, deduped by id. Switch the right-column tab to compose so
      // the user sees them. This is the multi-tab "add to draft" path.
      const existingIds = new Set(composerOverride.map((p) => p.id));
      const additions = people.filter((p) => !existingIds.has(p.id));
      if (additions.length > 0) {
        setComposerOverride([...composerOverride, ...additions]);
      }
      if (opts.ai && additions.length > 0) {
        // Trigger AI personalize for the newly-added recipient (first one
        // if multiple). If the person was already in the composer, also
        // trigger for them — they explicitly clicked Draft with AI.
        setComposerAiId(additions[0]?.id || people[0].id);
      }
      setActiveRightTab("compose");
      return;
    }

    // Composer not yet open — open a fresh session with these recipients.
    setComposerOverride(people);
    setComposerResumeIds({});
    setComposerAiId(opts.ai && people.length === 1 ? people[0].id : null);
    setComposerOpen(true);
    setActiveRightTab("compose");
  }

  function closeComposer() {
    setComposerOpen(false);
    setComposerOverride(null);
    setComposerResumeIds({});
    setComposerAiId(null);
    setActiveRightTab("profile");
  }

  /** Remove a single recipient from the composer. Used by both Composer
   * mount points (drafts-tab and right-column) so their behaviour can't
   * drift. Prunes a resumed draft if there was one, removes from the
   * recipients list, and closes the composer if the list goes empty. */
  function handleRemoveRecipient(id) {
    const resumeId = composerResumeIds[id];
    if (resumeId) {
      removeDraft(resumeId);
      setComposerResumeIds((m) => {
        const next = { ...m };
        delete next[id];
        return next;
      });
    }
    const current = composerOverride || [];
    const next = current.filter((p) => p.id !== id);
    if (next.length === 0) {
      closeComposer();
    } else {
      setComposerOverride(next);
    }
  }

  // Composer "To" picker — swap which of the recipient's possible addresses
  // the send/draft paths use. Autosave persists the choice with the draft.
  function handleChangeRecipientEmail(id, email) {
    setComposerOverride((current) =>
      (current || []).map((p) => (p.id === id ? { ...p, email } : p)),
    );
  }

  // Candidate queued for deletion by the detail pane's menu; the confirm
  // dialog reads from it. Null when nothing is pending.
  const [pendingDelete, setPendingDelete] = useState(null);

  /**
   * Delete a candidate and everything of ours that points at them. The server
   * removes their status and job links either way, and the record itself only
   * for internal adds — sourced people live in a corpus other tenants read.
   *
   * Local state is pruned rather than refetched: the lists are paginated and a
   * reload would jump the user back to page one of a list they were reading.
   */
  const deleteCandidate = useCallback(
    async (person) => {
      if (!person?.id) return;
      try {
        await deleteOutreachCandidate(person.id, person.origin);
      } catch (err) {
        showToast("Couldn't delete that candidate. Please try again.", {
          duration: 5000,
        });
        return;
      }
      const gone = (list) => (list || []).filter((p) => p.id !== person.id);
      setCandidates(gone);
      setExternalCandidates(gone);
      setPinnedPerson((prev) => (prev?.id === person.id ? null : prev));
      setStatusMap((m) => {
        const next = { ...m };
        delete next[person.id];
        return next;
      });
      setSavedMap((m) => {
        if (!m[person.id]) return m;
        const next = { ...m };
        delete next[person.id];
        pushState({ saved: next });
        return next;
      });
      setSelectedIds((prev) => {
        if (!prev.has(person.id)) return prev;
        const next = new Set(prev);
        next.delete(person.id);
        return next;
      });
      // Their job links are gone server-side, so the Job filter's options have
      // to be rebuilt or it keeps offering roles they were the only match for.
      loadCandidateJobLinks();
      setOpenId((cur) => (cur === person.id ? null : cur));
      setPendingDelete(null);
      showToast(`${person.name || "Candidate"} deleted`);
    },
    [showToast, loadCandidateJobLinks],
  );

  /**
   * Set a candidate's status on one job. `jobId` is required — every stage
   * belongs to an assignment. Callers that only know the person fall back to
   * the active job filter; with no filter and no jobId there is nothing to
   * write, so the call is a no-op.
   *
   * Optimistic: the row updates immediately and reverts if the write fails.
   */
  async function changeStatus(id, status, jobId = undefined) {
    const scope =
      jobId !== undefined && jobId !== null
        ? jobId
        : jobFilter !== "all"
          ? jobFilter
          : null;
    if (!scope) return;
    const before = statusMap[id];
    setStatusMap((m) => ({
      ...m,
      [id]: applyScopedStatus(m[id], scope, status),
    }));
    try {
      const { state } = await patchCandidateStatus(id, {
        jobId: scope,
        status,
      });
      // Take the server's row back: it reflects any edit made elsewhere, which
      // the optimistic patch above would otherwise have painted over.
      setStatusMap((m) => ({ ...m, [id]: state }));
      showToast("Status updated for this job");
    } catch (err) {
      setStatusMap((m) => ({ ...m, [id]: before }));
      showToast("Couldn't save the status change. Please try again.", {
        duration: 5000,
      });
    }
  }

  async function sendOne(person, draft) {
    try {
      // Convert inline data: images (signature images, pasted screenshots)
      // into RFC2392 CID-referenced attachments so they render properly in
      // recipient mail clients instead of being stripped or downranked.
      const { body: bodyForSend, inlineAttachments } =
        extractInlineImagesForSend(draft.body || "");
      const userAttachments = Array.isArray(draft.attachments)
        ? draft.attachments
        : [];
      await sendOutreachEmail({
        to: person.email,
        subject: draft.subject.trim(),
        // If we extracted any inline images, body has to be HTML — even if
        // the original draft was flagged plain. (data: images in a "plain"
        // body shouldn't happen, but if they do, sending as HTML preserves
        // the image rather than dropping it.)
        body: bodyForSend,
        html: !!draft.html || inlineAttachments.length > 0,
        attachments: [...userAttachments, ...inlineAttachments],
        ...(draft.batch || {}),
      });
      // Sending an email advances a fresh target to "outreach"; never downgrade
      // someone already further along the pipeline. Clients have no pipeline
      // status, so emailing one must not create a status entry for them.
      if (person.kind !== "client") {
        // An email isn't sent "for" one job, so this advances every assignment
        // the person has. Only ones still sitting at the default move — nobody
        // already further along is dragged back to Outreach. A candidate on no
        // job has nowhere to record it: the email still shows in their
        // timeline, and assigning them later starts that role at Target.
        const st = statusMap[person.id];
        const scopes = Object.keys(st?.jobs || {});
        for (const scope of scopes) {
          if (statusFor(statusMap, scope, person.id) === DEFAULT_STATUS) {
            // Sequential: each write returns the whole row and changeStatus
            // adopts it, so overlapping them could hand back a snapshot taken
            // before a sibling write landed.
            await changeStatus(person.id, "outreach", scope);
          }
        }
      }
      // refresh history in background
      loadHistory();
      return true;
    } catch (err) {
      showToast(err.message || `Failed to send to ${person.name}`, {
        duration: 4500,
      });
      return false;
    }
  }

  // per-person outreach history (filter by email)
  const personHistory = useMemo(() => {
    if (!openPerson?.email) return [];
    const e = openPerson.email.toLowerCase();
    return history.filter(
      (h) =>
        String(h.to || "")
          .toLowerCase()
          .trim() === e,
    );
  }, [openPerson, history]);

  // Counted straight off the outreach state, which covers every contact the
  // user has touched. Intersecting it with the loaded rows made the figures
  // track pagination and the active filter — clients past the first page,
  // or excluded by a company filter, silently dropped out of the totals.
  const stats = useMemo(() => {
    const meetingStages = new Set(MEETING_STATUSES);
    const out = { awaiting: 0, meetings: 0 };
    Object.values(statusMap).forEach((s) => {
      if (s === AWAITING_STATUS) out.awaiting += 1;
      else if (meetingStages.has(s)) out.meetings += 1;
    });
    return out;
  }, [statusMap]);

  /** DB-wide contact total for the header — candidates plus every client,
   *  never narrowed by the active tab, its filters, or how far the list has
   *  paged. Mirrors the Clients tab badge. */
  const totalContacts =
    mergedCandidates.length +
    (clientsGrandTotal != null
      ? clientsGrandTotal
      : clientsTotal != null
        ? clientsTotal
        : clients.length);

  const hasActiveListFilters =
    query.trim().length > 0 ||
    statusFilter !== "all" ||
    companyFilter !== "all" ||
    countryFilter !== "all" ||
    (tab === "candidates" && jobFilter !== "all");
  // Show the big full-page empty state only when the tab is truly empty with
  // no active filters/search. If filters are applied and no rows match (e.g.
  // Research deep-link sets company=...), keep the normal list/detail layout.
  const isEmpty = list.length === 0 && !loading && !hasActiveListFilters;
  const showFilters = list.length > 0 || hasActiveListFilters;
  // Only a load slower than 200 ms shows the row skeleton; a faster one goes straight to the content.
  const showSkeleton = useDelayedFlag(loading, 200);
  const connected = !!mailStatus?.connected;
  // Two or more ticked (and the composer not owning the right column) turns
  // the right column into the bulk-action pane.
  const selectionPaneOn =
    selectedIds.size > 1 && selectionFocus && !composerOpen;
  const selectedPeople = useMemo(
    () => list.filter((p) => selectedIds.has(p.id)),
    [list, selectedIds],
  );
  // Same columns as the People page export, plus Email and pipeline Status, which only
  // Outreach has. The list rows are lean, so each person's full profile is fetched first:
  // sourced people from the shared candidate store, added ones from the user's own.
  const [exportBusy, setExportBusy] = useState(false);
  async function exportSelectedCsv() {
    if (selectedPeople.length === 0 || exportBusy) return;
    setExportBusy(true);
    try {
      const cells = {};
      const queue = [...selectedPeople];
      await Promise.all(
        Array.from({ length: Math.min(5, queue.length) }, async () => {
          while (queue.length) {
            const p = queue.shift();
            try {
              if (p.origin === "external") {
                const res = await fetchWithAuth(`/api/linkedin-candidates/${encodeURIComponent(p.id)}`);
                const data = res.ok ? await res.json() : null;
                cells[p.id] = profileCells(data?.linkdapi_profile, formatLanguageProficiency);
              } else if (p.kind === "client") {
                cells[p.id] = internalProfileCells(p.raw || {});
              } else {
                let c = p.raw || {};
                if (c.summaryOnly && user?.sub) {
                  const res = await fetchWithAuth(
                    `/api/user-candidates/${encodeURIComponent(user.sub)}/${encodeURIComponent(p.id)}`,
                  );
                  if (res.ok) c = (await res.json()).candidate || c;
                }
                cells[p.id] = internalProfileCells(c);
              }
            } catch {
              // The lean row's fields still make the line; the sections stay blank.
            }
          }
        }),
      );
      const withStatus = tab !== "clients";
      const headers = [
        "#", "Name", "Title", "Company", "Location", "Country", "LinkedIn", "Match", "Description",
        "Experience", "Education", "Skills", "Languages", "Certifications", "Summary", "Email",
      ];
      if (withStatus) headers.push("Status");
      const rows = selectedPeople.map((p, i) => {
        const pc = cells[p.id] || internalProfileCells(p);
        const row = [
          i + 1, p.name, p.role, p.company, p.location, p.country, p.linkedin,
          matchLevelFromScore(p.raw?.percentage_match)?.label || "",
          "",
          pc.experience, pc.education, pc.skills, pc.languages, pc.certifications, pc.summary,
          p.email,
        ];
        if (withStatus) {
          const { status } = rowStatusOf(statusMap, jobFilter, p);
          row.push(STATUSES.find((x) => x.id === status)?.label || "");
        }
        return row;
      });
      downloadCsv(csvFileName(`outreach-${tab}`), toCsv(headers, rows));
      showToast(`Exported ${rows.length} ${tab === "clients" ? "clients" : "people"} to CSV`);
    } finally {
      setExportBusy(false);
    }
  }

  return (
    <Shell style={paletteVars} data-theme-mode={appTheme?.mode}>
      <Top>
        <TopRow>
          <TopLead>
            <TopTitle>
              Outreach
              <TopMeta>
                {candidatesCountLoading || clientsCountLoading ? (
                  <Skeleton
                    width={26}
                    height={10}
                    radius="4px"
                    inline
                    style={{ verticalAlign: "middle" }}
                  />
                ) : (
                  totalContacts.toLocaleString()
                )}{" "}
                contacts · {stats.awaiting} awaiting reply · {stats.meetings}{" "}
                meetings
              </TopMeta>
            </TopTitle>
            <Tabs data-tour="outreach-tabs">
              {/* Counts render as a shimmer until their source has fully
               * loaded — a partial count (3 → 19) reads as data popping in. */}
              {[
                [
                  "candidates",
                  "Candidates",
                  candidatesCountLoading ? null : mergedCandidates.length,
                ],
                [
                  "clients",
                  "Clients",
                  // Constant DB count — never tracks the company/country filter.
                  clientsGrandTotal != null
                    ? clientsGrandTotal
                    : clientsCountLoading
                      ? null
                      : clientsTotal != null
                        ? clientsTotal
                        : clients.length,
                ],
                ["saved", "Saved", stateLoaded ? savedList.length : null],
                ["drafts", "Drafts", stateLoaded ? draftsList.length : null],
              ].map(([id, label, count]) => (
                <Tab
                  key={id}
                  $active={tab === id}
                  onClick={() => {
                    setTab(id);
                    clearSelection();
                    setOpenId(null);
                    // Switching left-column tabs closes the composer to
                    // keep the new tab's view clean. Drafts in flight are
                    // already autosaved server-side every 2s.
                    closeComposer();
                  }}
                >
                  {label}{" "}
                  <TabCount>
                    {count == null ? (
                      <Skeleton
                        width={14}
                        height={9}
                        radius="4px"
                        inline
                        style={{ verticalAlign: "middle" }}
                      />
                    ) : (
                      count
                    )}
                  </TabCount>
                </Tab>
              ))}
            </Tabs>
          </TopLead>
          <HeaderActions>
            {!connected && mailStatus !== null && (
              <span
                style={{
                  fontSize: 12,
                  color: "var(--orx-warn)",
                  background: "var(--orx-warn-soft)",
                  padding: "4px 10px",
                  borderRadius: 99,
                  fontWeight: 500,
                }}
              >
                No mailbox connected
              </span>
            )}
            {addCandidatesOn && (
              <Btn
                $variant="primary"
                onClick={() => {
                  setTab("candidates");
                  setAddOpen(true);
                }}
              >
                <FiPlus size={14} /> Add
              </Btn>
            )}
          </HeaderActions>
        </TopRow>
      </Top>

      {tab === "drafts" ? (
        <Body $solo style={{ position: "relative" }}>
          {composerOpen ? (
            <Composer
              recipients={recipients}
              initialDrafts={recipients.reduce((acc, p) => {
                // Resume a specific draft if one was selected from the
                // drafts tab; otherwise this is a fresh session (no seed).
                const resumeId = composerResumeIds[p.id];
                const d = resumeId ? draftsMap[resumeId] : null;
                if (d) acc[p.id] = d;
                return acc;
              }, {})}
              initialDraftIds={composerResumeIds}
              onAutosave={persistDrafts}
              templates={templates}
              onSaveTemplate={saveTemplate}
              onDeleteTemplate={removeTemplate}
              onClose={(draftsState, draftIdMap) => {
                if (draftsState && draftIdMap)
                  persistDrafts(recipients, draftIdMap, draftsState);
                closeComposer();
              }}
              onSendOne={sendOne}
              onRemoveRecipient={handleRemoveRecipient}
              onChangeRecipientEmail={handleChangeRecipientEmail}
              onFindEmail={requestEmailLookup}
              emailPendingIds={emailPendingIds}
              emailNotes={emailNotes}
              fromAddress={mailStatus?.emailAddress}
              fromName={
                mailStatus?.displayName || displayName || user?.name || ""
              }
              initialAiForId={composerAiId}
              signatureHtml={defaultSignatureHtml}
              aiPrompts={aiPrompts}
              jobLinksByCandidate={jobLinksByCandidate}
            />
          ) : (
            <DraftsPane
              drafts={draftsList}
              onResume={openDraft}
              onDelete={removeDraft}
              loading={!stateLoaded}
            />
          )}
        </Body>
      ) : (
        <Body data-tour="outreach-main">
          <ListPane
            tab={tab}
            people={list}
            query={query}
            onQuery={setQuery}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            savedKind={savedKind}
            setSavedKind={changeSavedKind}
            companyFilter={companyFilter}
            setCompanyFilter={setCompanyFilter}
            countryFilter={countryFilter}
            setCountryFilter={setCountryFilter}
            seniorityFilter={seniorityFilter}
            setSeniorityFilter={setSeniorityFilter}
            jobFilter={jobFilter}
            setJobFilter={setJobFilter}
            jobOptions={jobOptions}
            jobIdsByCandidate={jobIdsByCandidate}
            sortBy={sortBy}
            setSortBy={setSortBy}
            selectionBar={
              selectedIds.size > 0 ? (
                <SelBar>
                  <SelMaster
                    role="checkbox"
                    aria-checked={
                      selectedIds.size >= visibleIds.length ? "true" : "mixed"
                    }
                    aria-label={
                      selectedIds.size >= visibleIds.length
                        ? "Clear selection"
                        : `Select all ${visibleIds.length}`
                    }
                    title={
                      selectedIds.size >= visibleIds.length
                        ? "Clear selection"
                        : `Select all ${visibleIds.length}`
                    }
                    $on
                    onClick={() =>
                      selectedIds.size >= visibleIds.length
                        ? clearSelection()
                        : selectAllVisible()
                    }
                  >
                    {selectedIds.size >= visibleIds.length ? (
                      <FiCheck size={12} strokeWidth={3} />
                    ) : (
                      <FiMinus size={12} strokeWidth={3} />
                    )}
                  </SelMaster>
                  <SelWord
                    onClick={() =>
                      selectedIds.size >= visibleIds.length
                        ? clearSelection()
                        : selectAllVisible()
                    }
                  >
                    All
                  </SelWord>
                  <SelSpacer />
                  <SelActions>
                  {tab === "candidates" && !!featureFlags?.jobMatch && (
                    <div style={{ position: "relative" }} ref={bulkMatchBtnRef}>
                      <SelBtn
                        $icon
                        onClick={() => setShowBulkMatchMenu((v) => !v)}
                        disabled={bulkMatchRunning}
                        aria-label="Job match"
                      >
                        <Spark size={15} />
                        <FiChevronDown size={12} />
                        {!showBulkMatchMenu && (
                          <SelTip>
                            {bulkMatchRunning ? "Matching…" : "Job match"}
                          </SelTip>
                        )}
                      </SelBtn>
                      <DropMenu
                        open={showBulkMatchMenu}
                        onClose={() => setShowBulkMatchMenu(false)}
                        // Portalled: SelBar scrolls sideways on phones, which
                        // would clip an in-bar menu to the bar's height.
                        anchorRef={bulkMatchBtnRef}
                        align="right"
                        minWidth={240}
                      >
                        {MATCH_SCOPES.map((s) => (
                          <MenuItem
                            key={s.id}
                            onClick={() => {
                              setShowBulkMatchMenu(false);
                              runBulkMatch(s.id);
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 500 }}>{s.label}</div>
                              <div
                                style={{
                                  fontSize: 11.5,
                                  color: "var(--orx-ink-muted)",
                                  marginTop: 1,
                                }}
                              >
                                {s.sub}
                              </div>
                            </div>
                          </MenuItem>
                        ))}
                      </DropMenu>
                    </div>
                  )}
                  {tab === "candidates" && emailLookupIds.length > 0 && (
                    <SelBtn
                      $icon
                      onClick={runBulkEmailLookup}
                      aria-label="Find emails"
                    >
                      <FiSearch size={15} />
                      <SelBadge>{emailLookupIds.length}</SelBadge>
                      <SelTip>
                        {`${emailJobRunning ? "Add" : "Find emails for"} ${emailLookupIds.length} sourced ${
                          emailLookupIds.length === 1 ? "person" : "people"
                        }${emailJobRunning ? " to the running lookup" : ""}`}
                      </SelTip>
                    </SelBtn>
                  )}
                  <SelBtn $primary onClick={() => startCompose()}>
                    <FiSend size={12} /> Email
                  </SelBtn>
                  </SelActions>
                </SelBar>
              ) : null
            }
            selectedIds={selectedIds}
            onToggleId={toggleId}
            onOpenPerson={(p) => {
              setOpenId(p.id);
              setSelectionFocus(false);
              // If composer is open, keep it open in its tab — just flip
              // the right column to the profile view so the user can see
              // who they clicked. Composer stays mounted (display:none).
              if (composerOpen) {
                setActiveRightTab("profile");
              }
            }}
            activeId={openId}
            statusMap={statusMap}
            loading={showSkeleton}
            pending={loading && !showSkeleton && list.length === 0}
            tabEmpty={isEmpty}
            showFilters={showFilters}
            onScrollBottom={
              tab === "clients" ? loadMoreClients : undefined
            }
            loadingMore={tab === "clients" ? clientsLoadingMore : false}
            totalCount={
              tab === "clients"
                ? clientsTotal
                : tab === "candidates" && candidatesTotal != null
                  ? candidatesTotal + externalCandidates.length
                  : null
            }
            disableClientSort={tab === "clients"}
            companyOptionsOverride={
              tab === "clients" ? allCompanies : undefined
            }
            countryOptionsOverride={
              tab === "clients" ? allCountries : undefined
            }
            hideCompanyFilter={
              tab === "candidates" ||
              (tab === "saved" && savedKind === "candidates")
            }
            unseenMatches={unseenMatches}
          />

          {/* Right column. When composerOpen AND a profile is also open,
           * we show a tab strip and keep both panels mounted (toggled with
           * display:none) so composer state survives tab switches. */}
          <RightColumn
            $mobileActive={(!!openPerson && !selectionPaneOn) || composerOpen}
          >
            {selectionPaneOn ? (
              <SelectionPane
                people={selectedPeople}
                count={selectedIds.size}
                tab={tab}
                allCount={visibleIds.length}
                onSelectAll={selectAllVisible}
                onClear={clearSelection}
                onEmail={() => startCompose()}
                canMatch={tab === "candidates" && !!featureFlags?.jobMatch}
                matchRunning={bulkMatchRunning}
                onMatch={runBulkMatch}
                showEmailLookup={tab === "candidates"}
                emailLookupCount={emailLookupIds.length}
                emailJobRunning={emailJobRunning}
                onFindEmails={runBulkEmailLookup}
                onExportCsv={exportSelectedCsv}
                exportBusy={exportBusy}
              />
            ) : (
            <>
            {composerOpen && openPerson && (
              <RightTabStrip>
                <RightTab
                  $active={activeRightTab === "profile"}
                  onClick={() => setActiveRightTab("profile")}
                  type="button"
                >
                  <span className="name">{openPerson.name || "Profile"}</span>
                </RightTab>
                <RightTab
                  $active={activeRightTab === "compose"}
                  onClick={() => setActiveRightTab("compose")}
                  type="button"
                >
                  <FiSend size={12} />
                  <span className="name">Compose</span>
                  <span className="count">
                    ({(composerOverride && composerOverride.length) || 0})
                  </span>
                  <RightTabClose
                    role="button"
                    aria-label="Close compose"
                    onClick={(e) => {
                      e.stopPropagation();
                      closeComposer();
                    }}
                  >
                    <FiX size={12} />
                  </RightTabClose>
                </RightTab>
              </RightTabStrip>
            )}

            {/* Composer panel — mounted whenever composerOpen, visible only
             * when the compose tab is active OR when no profile is open
             * (in which case it takes the whole right column). */}
            {composerOpen && (
              <RightPanel $show={!openPerson || activeRightTab === "compose"}>
                <Composer
                  recipients={recipients}
                  initialDrafts={recipients.reduce((acc, p) => {
                    const resumeId = composerResumeIds[p.id];
                    const d = resumeId ? draftsMap[resumeId] : null;
                    if (d) acc[p.id] = d;
                    return acc;
                  }, {})}
                  initialDraftIds={composerResumeIds}
                  onAutosave={persistDrafts}
                  templates={templates}
                  onSaveTemplate={saveTemplate}
                  onDeleteTemplate={removeTemplate}
                  signatureHtml={defaultSignatureHtml}
                  onClose={(draftsState, draftIdMap) => {
                    if (draftsState && draftIdMap)
                      persistDrafts(recipients, draftIdMap, draftsState);
                    closeComposer();
                  }}
                  onSendOne={sendOne}
                  onRemoveRecipient={handleRemoveRecipient}
                  onChangeRecipientEmail={handleChangeRecipientEmail}
                  onFindEmail={requestEmailLookup}
                  emailPendingIds={emailPendingIds}
                  emailNotes={emailNotes}
                  fromAddress={mailStatus?.emailAddress}
                  fromName={
                    mailStatus?.displayName || displayName || user?.name || ""
                  }
                  initialAiForId={composerAiId}
                  aiPrompts={aiPrompts}
                  jobLinksByCandidate={jobLinksByCandidate}
                />
              </RightPanel>
            )}

            {/* Profile panel — visible when (a) composer is closed and a
             * person is open, OR (b) composer is open AND the profile tab
             * is active. */}
            <RightPanel
              $show={
                !!openPerson && (!composerOpen || activeRightTab === "profile")
              }
            >
              {openPerson && (
                <DetailPane
                  person={openPerson}
                  statusMap={statusMap}
                  jobFilter={jobFilter}
                  jobIdsByCandidate={jobIdsByCandidate}
                  jobLinksByCandidate={jobLinksByCandidate}
                  onChangeStatus={changeStatus}
                  onSendEmail={(ids, opts) => startCompose(ids, opts)}
                  onBack={() => setOpenId(null)}
                  history={personHistory}
                  historyLoading={!historyLoaded}
                  userId={user?.sub}
                  isSaved={!!savedMap[openPerson.id]}
                  onToggleSaved={toggleSaved}
                  isUnseenMatch={unseenMatches.has(openPerson.id)}
                  onMarkMatchUnseen={addUnseenMatch}
                  onClearMatchUnseen={clearUnseenMatch}
                  onJobLinksChanged={loadCandidateJobLinks}
                  onRequestDelete={setPendingDelete}
                  onFindEmail={requestEmailLookup}
                  emailPending={emailPendingIds.has(openPerson.id)}
                  emailNote={emailNotes.get(openPerson.id) || null}
                />
              )}
            </RightPanel>

            {/* Empty state — only when nothing else is showing in the
             * right column. */}
            {!composerOpen && !openPerson && (
              <Detail>
                <div
                  style={{
                    height: "100%",
                    display: "grid",
                    placeItems: "center",
                    color: "var(--orx-ink-muted)",
                    fontSize: 13.5,
                  }}
                >
                  {loading && list.length === 0 ? null : isEmpty ? (
                    <FadeIn style={{ textAlign: "center", maxWidth: 340 }}>
                      <EmptyArt>
                        <FiUsers size={42} />
                      </EmptyArt>
                      <h3 style={{ fontFamily: C.fontDisplay, fontSize: 24, fontWeight: 400, margin: "0 0 6px", color: "var(--orx-ink)" }}>
                        {tab === "candidates" ? "Start with a search" : tab === "saved" ? "Nothing saved yet" : "No clients yet"}
                      </h3>
                      <p style={{ margin: "0 0 18px", lineHeight: 1.5 }}>
                        {tab === "candidates"
                          ? "Find candidates on the People page and they'll land here, ready to contact."
                          : tab === "saved"
                            ? "Open a profile and click Save to keep someone here."
                            : "Clients appear here once they're imported."}
                      </p>
                      {tab === "candidates" && (
                        <Btn $variant="primary" onClick={() => navigate("/find-candidates")}>
                          Find candidates
                        </Btn>
                      )}
                    </FadeIn>
                  ) : (
                    <div style={{ textAlign: "center" }}>
                      <FiUsers size={32} style={{ color: "var(--orx-ink-faint)" }} />
                      <div style={{ marginTop: 10 }}>Select someone from the list</div>
                    </div>
                  )}
                </div>
              </Detail>
            )}
            </>
            )}
          </RightColumn>

        </Body>
      )}

      <AddCandidatesModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={() => {
          // Refresh the candidates list so newly imported rows appear.
          loadCandidates();
        }}
      />

      {/* Bulk match and email lookups show in the shared Activities tray (ActivityTray). */}

      {pendingDelete && (
        <DeleteCandidateModal
          person={pendingDelete}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => deleteCandidate(pendingDelete)}
        />
      )}
    </Shell>
  );
}
